import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { HiOutlineGlobeAlt } from 'react-icons/hi2';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { useUser } from './UserContext';
import { useTranslation } from 'react-i18next';
import { changeLanguage } from '../i18n';
import CountrySelectModal from './CountrySelectModal';
import CitySelectModal from './CitySelectModal';
import FirmaSecimModal from './FirmaSecimModal';
import { useActivityTracker } from './useActivityTracker';
// useSiteTour artık FloatingHelpButton içinde global olarak kullanılıyor
import { COUNTRIES } from '../data/countries.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ROBOTO_REGULAR, ROBOTO_BOLD, INVAMED_LOGO_PNG } from '../fonts/roboto-base64';
// import { products } from '../data/products';

// ── Tutar formatı yardımcıları (Türkçe: 1.234,56) ──
// Kullanıcının girdiği değeri Türkçe formata çevirir
function formatTutar(value) {
  if (!value && value !== 0) return '';
  // Sadece rakam, virgül ve nokta bırak
  let raw = String(value).replace(/[^\d.,]/g, '');
  // Virgülü ondalık ayracı olarak kabul et, noktaları kaldır
  raw = raw.replace(/\./g, '');
  // Birden fazla virgül varsa sadece ilkini tut
  const parts = raw.split(',');
  let intPart = parts[0] || '';
  let decPart = parts.length > 1 ? parts.slice(1).join('') : null;
  // Binlik ayracı ekle (nokta)
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  if (decPart !== null) {
    return intPart + ',' + decPart.slice(0, 2);
  }
  return intPart;
}

// Türkçe formattan sayıya çevirir (1.234,56 → 1234.56)
function parseTutar(formatted) {
  if (!formatted) return 0;
  const raw = String(formatted).replace(/\./g, '').replace(',', '.');
  const num = parseFloat(raw);
  return isNaN(num) ? 0 : num;
}

// Input onChange handler: otomatik formatlama
function handleTutarInput(e) {
  const el = e.target;
  const cursor = el.selectionStart;
  const oldLen = el.value.length;
  el.value = formatTutar(el.value);
  const newLen = el.value.length;
  // Cursor pozisyonunu koru
  const diff = newLen - oldLen;
  el.setSelectionRange(cursor + diff, cursor + diff);
}

// ── Proforma PDF Oluşturma (Antetli Kağıt + Türkçe Font) ──
function generateProformaPDF(order, items, cariInfo) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;

  // --- Roboto fontlarını yükle (Türkçe karakter desteği) ---
  doc.addFileToVFS('Roboto-Regular.ttf', ROBOTO_REGULAR);
  doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
  doc.addFileToVFS('Roboto-Bold.ttf', ROBOTO_BOLD);
  doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
  doc.setFont('Roboto', 'normal');

  // === ANTETLI KAĞIT ÜST BÖLÜM ===
  // Üst koyu şerit
  doc.setFillColor(36, 30, 92); // INVAMED koyu mor (#241e5c)
  doc.rect(0, 0, pageWidth, 2, 'F');

  // Logo (sol üst)
  try { doc.addImage(INVAMED_LOGO_PNG, 'PNG', margin, 8, 38, 16); } catch(e) {}

  // Sağ üst: PROFORMA FATURA başlığı
  doc.setFont('Roboto', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(36, 30, 92);
  doc.text('PROFORMA FATURA', pageWidth - margin, 16, { align: 'right' });
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.setFont('Roboto', 'normal');
  doc.text('Belge No: PF-' + String(order.id).padStart(5, '0'), pageWidth - margin, 22, { align: 'right' });

  // İnce ayırıcı çizgi
  let y = 30;
  doc.setDrawColor(36, 30, 92);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  // === BİLGİ KUTULARI ===
  // Sol kutu: Müşteri bilgileri
  const firmaName = order.firma || cariInfo?.firma || order.email || '-';
  doc.setFillColor(248, 249, 252);
  doc.roundedRect(margin, y, (pageWidth - 2 * margin) / 2 - 4, 32, 2, 2, 'F');

  doc.setFont('Roboto', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(36, 30, 92);
  doc.text('MÜŞTERİ BİLGİLERİ', margin + 5, y + 5);
  doc.setFont('Roboto', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);
  doc.text(firmaName, margin + 5, y + 11);
  doc.setFontSize(7.5);
  doc.setTextColor(90, 90, 90);
  let cy = y + 16;
  if (order.email) { doc.text('E-posta: ' + order.email, margin + 5, cy); cy += 4; }
  if (order.telefon || cariInfo?.telefon) { doc.text('Telefon: ' + (order.telefon || cariInfo?.telefon || '-'), margin + 5, cy); cy += 4; }
  if (order.vergiNo || cariInfo?.vergiNo) { doc.text('Vergi No: ' + (order.vergiNo || cariInfo?.vergiNo || '-'), margin + 5, cy); }

  // Sağ kutu: Sipariş bilgileri
  const rightBoxX = margin + (pageWidth - 2 * margin) / 2 + 4;
  const rightBoxW = (pageWidth - 2 * margin) / 2 - 4;
  doc.setFillColor(248, 249, 252);
  doc.roundedRect(rightBoxX, y, rightBoxW, 32, 2, 2, 'F');

  doc.setFont('Roboto', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(36, 30, 92);
  doc.text('SİPARİŞ BİLGİLERİ', rightBoxX + 5, y + 5);

  doc.setFont('Roboto', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(40, 40, 40);
  const statusLabels = { pending: 'Onay Bekleniyor', approved: 'Onaylandı', shipping: 'Teslimat Sürecinde', delivered: 'Teslim Edildi', cancelled: 'İptal Edildi' };

  const infoLines = [
    ['Sipariş No', '#' + order.id],
    ['Tarih', new Date(order.created_at).toLocaleDateString('tr-TR')],
    ['Durum', statusLabels[order.status] || order.status || '-'],
  ];
  let iy = y + 11;
  infoLines.forEach(([label, value]) => {
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(90, 90, 90);
    doc.text(label + ':', rightBoxX + 5, iy);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text(value, rightBoxX + 30, iy);
    iy += 5;
  });

  y += 38;

  // === ÜRÜN TABLOSU ===
  const tableBody = items.map((item, idx) => {
    const qty = parseInt(item.qty) || 1;
    const price = parseFloat(item.price) || 0;
    const lineTotal = price * qty;
    let discountText = '-';
    let discountAmt = 0;
    if (item.discount_type === 'percent' && parseFloat(item.discount_value) > 0) {
      discountAmt = lineTotal * parseFloat(item.discount_value) / 100;
      discountText = '%' + item.discount_value;
    } else if (item.discount_type === 'amount' && parseFloat(item.discount_value) > 0) {
      discountAmt = parseFloat(item.discount_value);
      discountText = discountAmt.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL';
    }
    const finalTotal = lineTotal - discountAmt;
    return [
      String(idx + 1),
      (item.name || 'Ürün') + (item.barcode ? '\n' + item.barcode : '') + (item.sutCode ? ' | ' + item.sutCode : ''),
      item.marka || '-',
      String(qty),
      price > 0 ? price.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL' : '-',
      discountText,
      finalTotal > 0 ? finalTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL' : '-'
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [['#', 'Ürün', 'Marka', 'Adet', 'Birim Fiyat', 'İndirim', 'Toplam']],
    body: tableBody,
    theme: 'grid',
    styles: { font: 'Roboto', fontSize: 8, cellPadding: 2.5, overflow: 'linebreak', lineColor: [220, 220, 220], lineWidth: 0.3, textColor: [40, 40, 40] },
    headStyles: { fillColor: [36, 30, 92], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8, font: 'Roboto' },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 55 },
      2: { cellWidth: 25 },
      3: { cellWidth: 15, halign: 'center' },
      4: { cellWidth: 25, halign: 'right' },
      5: { cellWidth: 20, halign: 'center' },
      6: { cellWidth: 28, halign: 'right' }
    },
    alternateRowStyles: { fillColor: [248, 249, 252] },
    margin: { left: margin, right: margin }
  });

  y = doc.lastAutoTable.finalY + 8;

  // === TOPLAM BİLGİLERİ ===
  const subtotal = items.reduce((s, i) => s + (parseFloat(i.price) || 0) * (parseInt(i.qty) || 1), 0);
  const itemDiscountsTotal = items.reduce((s, i) => {
    const lt = (parseFloat(i.price) || 0) * (parseInt(i.qty) || 1);
    if (i.discount_type === 'percent') return s + lt * (parseFloat(i.discount_value) || 0) / 100;
    if (i.discount_type === 'amount') return s + (parseFloat(i.discount_value) || 0);
    return s;
  }, 0);
  let orderDiscount = 0;
  if (order.discount_type === 'percent') orderDiscount = (subtotal - itemDiscountsTotal) * (parseFloat(order.discount_value) || 0) / 100;
  else if (order.discount_type === 'amount') orderDiscount = parseFloat(order.discount_value) || 0;
  const totalDiscount = itemDiscountsTotal + orderDiscount;
  const grandTotal = Math.max(0, subtotal - totalDiscount);
  const kdvRate = 10;
  const kdvAmount = grandTotal * kdvRate / 100;
  const grandTotalWithKDV = grandTotal + kdvAmount;

  // Toplam kutusu (sağ alt)
  const boxW = 82;
  const boxX = pageWidth - margin - boxW;
  const rowH = 6;
  let rows = 3; // Toplam + KDV + KDV Dahil Toplam
  if (totalDiscount > 0) rows++; // Ara Toplam
  if (itemDiscountsTotal > 0) rows++;
  if (orderDiscount > 0) rows++;
  const boxH = rows * rowH + 12;

  doc.setFillColor(248, 249, 252);
  doc.roundedRect(boxX, y, boxW, boxH, 2, 2, 'F');
  doc.setDrawColor(36, 30, 92);
  doc.setLineWidth(0.3);
  doc.roundedRect(boxX, y, boxW, boxH, 2, 2, 'S');

  let ty = y + 6;

  if (totalDiscount > 0) {
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text('Ara Toplam:', boxX + 4, ty);
    doc.text(subtotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL', boxX + boxW - 4, ty, { align: 'right' });
    ty += rowH;

    if (itemDiscountsTotal > 0) {
      doc.setTextColor(200, 100, 0);
      doc.text('Ürün İndirimleri:', boxX + 4, ty);
      doc.text('-' + itemDiscountsTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL', boxX + boxW - 4, ty, { align: 'right' });
      ty += rowH;
    }
    if (orderDiscount > 0) {
      doc.setTextColor(200, 100, 0);
      const discLabel = order.discount_type === 'percent' ? 'Sipariş İndirimi (%' + order.discount_value + '):' : 'Sipariş İndirimi:';
      doc.text(discLabel, boxX + 4, ty);
      doc.text('-' + orderDiscount.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL', boxX + boxW - 4, ty, { align: 'right' });
      ty += rowH;
    }

    // Ayırıcı çizgi
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    doc.line(boxX + 4, ty - 3, boxX + boxW - 4, ty - 3);
  }

  // Toplam (KDV hariç)
  doc.setFont('Roboto', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 60);
  doc.text('Toplam:', boxX + 4, ty);
  doc.text(grandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL', boxX + boxW - 4, ty, { align: 'right' });
  ty += rowH;

  // KDV
  doc.setTextColor(90, 90, 90);
  doc.text('KDV (%' + kdvRate + '):', boxX + 4, ty);
  doc.text(kdvAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL', boxX + boxW - 4, ty, { align: 'right' });
  ty += rowH;

  // Ayırıcı kalın çizgi
  doc.setDrawColor(36, 30, 92);
  doc.setLineWidth(0.4);
  doc.line(boxX + 4, ty - 3, boxX + boxW - 4, ty - 3);

  // KDV Dahil Genel Toplam
  doc.setFont('Roboto', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(36, 30, 92);
  doc.text('Genel Toplam:', boxX + 4, ty);
  doc.text(grandTotalWithKDV.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL', boxX + boxW - 4, ty, { align: 'right' });

  // === NOT ===
  if (order.note) {
    const noteY = y + boxH + 8;
    doc.setFont('Roboto', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(36, 30, 92);
    doc.text('NOT:', margin, noteY);
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(90, 90, 90);
    doc.text(order.note, margin + 10, noteY, { maxWidth: pageWidth - 2 * margin - 10 });
  }

  // === ALT BİLGİ (FOOTER) ===
  // Alt koyu şerit
  doc.setFillColor(36, 30, 92);
  doc.rect(0, pageHeight - 14, pageWidth, 14, 'F');
  doc.setFont('Roboto', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(200, 200, 220);
  doc.text('INVAMED Medikal | www.invamed.com', margin, pageHeight - 7);
  doc.text('Bu belge proforma fatura niteliğindedir.', pageWidth / 2, pageHeight - 7, { align: 'center' });
  doc.text(new Date().toLocaleDateString('tr-TR') + ' ' + new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }), pageWidth - margin, pageHeight - 7, { align: 'right' });

  // Cross-browser PDF indirme
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const fileName = 'Proforma_' + order.id + '.pdf';

  // iOS Safari ve bazı mobil tarayıcılarda window.open daha güvenilir
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  if (isIOS || isSafari) {
    // Safari/iOS: Blob URL'yi yeni sekmede aç
    const newWindow = window.open(pdfUrl, '_blank');
    if (!newWindow) {
      // Popup engellenirse link ile indir
      const a = document.createElement('a');
      a.href = pdfUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  } else {
    // Chrome, Firefox, Edge: Invisible anchor ile indir
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  // Belleği temizle
  setTimeout(() => URL.revokeObjectURL(pdfUrl), 5000);
}

const BAYI_FIELDS = [
  { name: 'firma', label: 'Firma / Sağlık Profesyoneli', type: 'text', required: true },
  { name: 'telefon', label: 'Telefon', type: 'tel', required: true },
  { name: 'email', label: 'E-posta', type: 'email', required: true },
  { name: 'sehir', label: 'Şehir', type: 'text', required: true },
  { name: 'sektor', label: 'Faaliyet Alanı / Sektör', type: 'text', required: true },
  { name: 'sicil', label: 'Ticaret Sicil No', type: 'text', required: false },
  { name: 'web', label: 'Web Sitesi', type: 'text', required: false },
];

const BAYI_TABS = [
  {
    name: 'Temel Bilgiler',
    fields: ['firma', 'telefon', 'email', 'sehir'],
  },
  {
    name: 'Ek Bilgiler',
    fields: ['sektor', 'sicil', 'web'],
  },
];

function getAuthHeaders() {
  try {
    const t = localStorage.getItem('authToken') || JSON.parse(localStorage.getItem('user') || '{}').token;
    return t ? { 'Authorization': `Bearer ${t}` } : {};
  } catch { return {}; }
}

function authFetch(url, options = {}) {
  const headers = { ...getAuthHeaders(), ...(options.headers || {}) };
  return fetch(url, { ...options, headers });
}

export default function TeklifOlusturPage() {
  const { t, i18n } = useTranslation();
  const { trackPageView, trackProductDetailOpen, trackProductDetailClose, trackCartAdd, trackCartRemove, trackOrderCreate, trackSearch, trackFilter, trackLogin, trackLogout } = useActivityTracker();
  // Tur artık FloatingHelpButton tarafından yönetiliyor
  
  // Zorunlu bilgiler
  const initialFirmaInfo = (() => {
    try {
      return JSON.parse(localStorage.getItem('teklifFirmaInfo')) || {};
    } catch { return {}; }
  })();

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Tüm Kategoriler'); // Varsayılan olarak Tüm Kategoriler seçili
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('teklifCart')) || [];
    } catch {
      return [];
    }
  });
  const [modalProduct, setModalProduct] = useState(null);
  const [modalQty, setModalQty] = useState(1);
  const [modalDesc, setModalDesc] = useState('');
  const [modalEditIdx, setModalEditIdx] = useState(null);
  // Varyant bazlı modal state'leri
  const [modalVariants, setModalVariants] = useState([]);
  const [modalSutCodes, setModalSutCodes] = useState([]);
  const [modalParentGroups, setModalParentGroups] = useState([]);
  const [modalSutPrices, setModalSutPrices] = useState({});
  const [modalSelectedSut, setModalSelectedSut] = useState('');
  const [modalSelectedParentGroup, setModalSelectedParentGroup] = useState('');
  const [modalSelectedVariant, setModalSelectedVariant] = useState(null);
  const [modalVariantSearch, setModalVariantSearch] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [cartPaymentMethod, setCartPaymentMethod] = useState('');
  const [cartPaymentNotes, setCartPaymentNotes] = useState('');
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  const [langDropdown, setLangDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' veya 'list'
  const navigate = useNavigate();
  const location = useLocation();
  const [firmaId, setFirmaId] = useState(initialFirmaInfo.firmaId || null);
  const [firma, setFirma] = useState(initialFirmaInfo.firma || '');
  const [telefon, setTelefon] = useState(initialFirmaInfo.telefon || '');
  const [email, setEmail] = useState(initialFirmaInfo.email || '');
  const [sehir, setSehir] = useState(initialFirmaInfo.sehir || '');
  const [yetkili, setYetkili] = useState(initialFirmaInfo.yetkili || '');
  const [bilgilerIletildi, setBilgilerIletildi] = useState(!!initialFirmaInfo.bilgilerIletildi);
  const [ulke, setUlke] = useState('TR');
  const bilgilerEksik = !firma.trim() || !sehir.trim() || !telefon.trim() || !email.trim() || !yetkili.trim();
  const [showIletildiUyarisi, setShowIletildiUyarisi] = useState(false);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [showBayiModal, setShowBayiModal] = useState(false);
  const [bayiStep, setBayiStep] = useState(1);
  const [bayiForm, setBayiForm] = useState({});
  const [bayiSozlesmeOnay, setBayiSozlesmeOnay] = useState(false);
  const [bayiFiles, setBayiFiles] = useState([]);
  const [bayiTab, setBayiTab] = useState(0);
  const [showSozlesmeModal, setShowSozlesmeModal] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showCityModal, setShowCityModal] = useState(false);

  // Login/Register panel state
  const [loginTab, setLoginTab] = useState('login'); // 'login' | 'register'
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  // Geçici şifre değiştirme
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pwChangeEmail, setPwChangeEmail] = useState('');
  const [pwChangeCurrent, setPwChangeCurrent] = useState('');
  const [pwChangeNew, setPwChangeNew] = useState('');
  const [pwChangeConfirm, setPwChangeConfirm] = useState('');
  const [pwChangeError, setPwChangeError] = useState('');
  const [pwChangeLoading, setPwChangeLoading] = useState(false);
  const [pwChangePendingUser, setPwChangePendingUser] = useState(null);
  // Register fields
  const [regFirma, setRegFirma] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regTelefon, setRegTelefon] = useState('');
  const [regYetkili, setRegYetkili] = useState('');
  const [regUlke, setRegUlke] = useState('TR');
  const [regSehir, setRegSehir] = useState('');
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('');
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regSuccess, setRegSuccess] = useState(false); // Kayıt başarılı durumu
  // Right panel - sadece sepet
  const [rightPanelView, setRightPanelView] = useState('cart'); // sadece 'cart'
  // Sol ana alan görünüm kontrolü
  const { view: routeView } = useParams();
  const viewMap = { hesabim: 'hesabim', sepetim: 'products', siparislerim: 'siparislerim', odeme: 'odeme', 'siparis-yonetimi': 'adminOrders', cariler: 'cariler', 'fiyat-ayarlari': 'zamayar', 'stok-yonetimi': 'stokYonetimi', 'dagitim-agi': 'dagitimAgi', 'uts-yonetimi': 'utsYonetimi', 'urun-gruplari': 'urunGruplari', 'aktivite-loglar': 'activityLogs', 'tur-yonetimi': 'tourManagement' };
  const reverseViewMap = { hesabim: 'hesabim', products: 'sepetim', siparislerim: 'siparislerim', odeme: 'odeme', adminOrders: 'siparis-yonetimi', cariler: 'cariler', zamayar: 'fiyat-ayarlari', stokYonetimi: 'stok-yonetimi', dagitimAgi: 'dagitim-agi', utsYonetimi: 'uts-yonetimi', activityLogs: 'aktivite-loglar', tourManagement: 'tur-yonetimi' };
  const [mainView, setMainView] = useState(routeView ? (viewMap[routeView] || 'products') : 'products'); // 'products' | 'hesabim' | 'siparislerim' | 'odeme' | 'zamayar' | 'cariler'

  // URL değiştiğinde mainView'ı güncelle
  useEffect(() => {
    if (routeView) {
      const mapped = viewMap[routeView] || 'products';
      setMainView(mapped);
      // URL'den status parametresini oku ve sipariş yönetimi filtresini ayarla
      if (mapped === 'adminOrders') {
        const params = new URLSearchParams(location.search);
        const statusParam = params.get('status');
        if (statusParam) {
          setAdminOrdersStatus(statusParam);
          setAdminOrdersPage(1);
        }
      }
    }
  }, [routeView, location.search]);

  // URL'den status parametresi ile sipariş filtreleme
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status');
    if (statusParam && routeView === 'siparis-yonetimi') {
      setAdminOrdersStatus(statusParam);
      setAdminOrdersPage(1);
    }
  }, [location.search, routeView]);

  // Zam oranı
  const [zamOrani, setZamOrani] = useState(0);
  const [zamInput, setZamInput] = useState('');
  const [zamSaving, setZamSaving] = useState(false);
  // Cariler
  const [cariler, setCariler] = useState([]);
  const [carilerLoading, setCarilerLoading] = useState(false);
  const [carilerStats, setCarilerStats] = useState(null);
  const [carilerSearch, setCarilerSearch] = useState('');
  const [carilerSehir, setCarilerSehir] = useState('');
  const [carilerPage, setCarilerPage] = useState(1);
  const [carilerTotal, setCarilerTotal] = useState(0);
  // Tur Yönetimi state'leri
  const [tourSteps, setTourSteps] = useState([]);
  const [tourStepsLoading, setTourStepsLoading] = useState(false);
  const [tourPages, setTourPages] = useState([]);
  const [tourSelectedPage, setTourSelectedPage] = useState('dashboard');
  const [tourEditingStep, setTourEditingStep] = useState(null);
  const [tourNewStep, setTourNewStep] = useState(false);
  const [tourForm, setTourForm] = useState({ page: 'dashboard', step_order: 1, target_selector: '', title: '', description: '', position: 'bottom', is_active: 1, show_for_role: '' });
  const [tourSaving, setTourSaving] = useState(false);
  // Aktivite Log state'leri
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsStats, setActivityLogsStats] = useState(null);
  const [activityLogsPage, setActivityLogsPage] = useState(1);
  const [activityLogsTotal, setActivityLogsTotal] = useState(0);
  const [activityLogsTotalPages, setActivityLogsTotalPages] = useState(0);
  const [activityLogsSearch, setActivityLogsSearch] = useState('');
  const [activityLogsActionType, setActivityLogsActionType] = useState('');
  const [activityLogsDateFrom, setActivityLogsDateFrom] = useState('');
  const [activityLogsDateTo, setActivityLogsDateTo] = useState('');
  const [activityLogsSessionDetail, setActivityLogsSessionDetail] = useState(null);
  const [activityLogsSessionLoading, setActivityLogsSessionLoading] = useState(false);
  // Profil düzenleme state'leri
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  // Firma talep state'leri
  const [firmaArama, setFirmaArama] = useState('');
  const [aramaResults, setAramaResults] = useState([]);
  const [aramaLoading, setAramaLoading] = useState(false);
  const [talepMesaj, setTalepMesaj] = useState('');
  const [talepGonderiliyor, setTalepGonderiliyor] = useState(false);
  const [talepler, setTalepler] = useState([]);
  const [showTalepForm, setShowTalepForm] = useState(false);
  const [talepSuccess, setTalepSuccess] = useState('');
  const [talepError, setTalepError] = useState('');
  const firmaSearchTimeout = useRef(null);
  // Sipariş & ödeme verileri
  const [userOrders, setUserOrders] = useState([]);
  const [userOrdersLoading, setUserOrdersLoading] = useState(false);
  const [userSummary, setUserSummary] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  // Cari düzenleme
  const [editingCari, setEditingCari] = useState(null);
  const [cariForm, setCariForm] = useState({});
  const [cariSaving, setCariSaving] = useState(false);
  const [cariDetail, setCariDetail] = useState(null);
  const [cariDetailOrders, setCariDetailOrders] = useState([]);
  const [cariDetailLoading, setCariDetailLoading] = useState(false);
  // Cari yetkili firmalar yönetimi
  const [cariDetailFirmalar, setCariDetailFirmalar] = useState([]);
  const [cariDetailTab, setCariDetailTab] = useState('siparisler'); // 'siparisler' | 'firmalar'
  const [cariOrderExpanded, setCariOrderExpanded] = useState(null); // expanded order id
  const [firmaSearchQuery, setFirmaSearchQuery] = useState('');
  const [firmaSearchResults, setFirmaSearchResults] = useState([]);
  const [firmaSearchLoading, setFirmaSearchLoading] = useState(false);
  const [firmaAtamaLoading, setFirmaAtamaLoading] = useState(false);
  // Admin siparişler
  const [adminOrders, setAdminOrders] = useState([]);
  const [adminOrdersLoading, setAdminOrdersLoading] = useState(false);
  const [adminOrdersStats, setAdminOrdersStats] = useState(null);
  const [adminOrdersSearch, setAdminOrdersSearch] = useState('');
  const [adminOrdersStatus, setAdminOrdersStatus] = useState('');
  const [adminOrdersPaymentStatus, setAdminOrdersPaymentStatus] = useState('');
  const [adminOrdersEmail, setAdminOrdersEmail] = useState('');
  const [adminOrdersPage, setAdminOrdersPage] = useState(1);
  const [adminOrdersTotal, setAdminOrdersTotal] = useState(0);
  const [adminExpandedOrder, setAdminExpandedOrder] = useState(null);
  // Sevkiyat & İade state'leri
  const [adminOrderTab, setAdminOrderTab] = useState('kalemler'); // 'kalemler' | 'sevkiyat' | 'iade'
  const [editingItemModal, setEditingItemModal] = useState(null); // { orderId, idx, item }
  const [sevkiyatlar, setSevkiyatlar] = useState({}); // { orderId: [...] }
  const [iadeler, setIadeler] = useState({}); // { orderId: [...] }
  const [sevkForm, setSevkForm] = useState(null); // { orderId, items: [{item_index, qty, max, lot_no, uretim_tarihi, skt, seri_nolar}] }
  const [sevkNotes, setSevkNotes] = useState('');
  // İlave ürün ekleme modal state'leri
  const [addItemModal, setAddItemModal] = useState(false);
  const [addItemOrderId, setAddItemOrderId] = useState(null);
  const [addItemSearch, setAddItemSearch] = useState('');
  const [addItemSelected, setAddItemSelected] = useState(null);
  const [addItemQty, setAddItemQty] = useState(1);
  const [addItemSut, setAddItemSut] = useState('');
  const [addItemMeasure, setAddItemMeasure] = useState('');
  const [addItemSaving, setAddItemSaving] = useState(false);
  const [sevkTrackingNo, setSevkTrackingNo] = useState('');
  const [sevkKargoFirmasi, setSevkKargoFirmasi] = useState('');
  const [sevkLoading, setSevkLoading] = useState(false);
  const [seriNoModal, setSeriNoModal] = useState(null); // { sIdx, name, qty }
  const [seriNoInput, setSeriNoInput] = useState('');
  const [seriNoAutoStart, setSeriNoAutoStart] = useState('');
  const [seriNoAutoCount, setSeriNoAutoCount] = useState('');
  const [seriNoDuplicates, setSeriNoDuplicates] = useState([]); // tekrar eden seri nolar
  const [seriNoChecking, setSeriNoChecking] = useState(false);

  // Bayi Stok Yönetimi state'leri
  const [bayiTeslimBekleyen, setBayiTeslimBekleyen] = useState([]);
  const [bayiStok, setBayiStok] = useState([]);
  const [bayiDagitimlar, setBayiDagitimlar] = useState([]);
  const [bayiStokLoading, setBayiStokLoading] = useState(false);
  const [dagitimModal, setDagitimModal] = useState(null); // { product_name, barcode, measure, lot_no, kalan, seri_nolar, dagitilan_seri_nolar }
  const [dagitimForm, setDagitimForm] = useState({ il: '', hastane_tipi: '', hastane_kurum: '', operasyon_tarihi: '', seri_nolar: [], operasyon_notu: '' });
  const [dagitimLoading, setDagitimLoading] = useState(false);
  const [hastaneIller, setHastaneIller] = useState([]);
  const [hastaneListesi, setHastaneListesi] = useState([]);

  // UTS State
  const [utsDurum, setUtsDurum] = useState(null);
  const [utsForm, setUtsForm] = useState({ uts_token: '', uts_kurum_no: '', is_test: true });
  const [utsLoading, setUtsLoading] = useState(false);
  const [utsBildirimGecmisi, setUtsBildirimGecmisi] = useState([]);
  const [utsBekleyenAlmalar, setUtsBekleyenAlmalar] = useState(null);
  const [utsSorguSonuc, setUtsSorguSonuc] = useState(null);
  const [utsSorguForm, setUtsSorguForm] = useState({ uno: '', lno: '', sno: '' });
  const [utsTab, setUtsTab] = useState('ayarlar');
  const [utsBildirimForm, setUtsBildirimForm] = useState({});
  const [utsBildirimTipi, setUtsBildirimTipi] = useState('');
  // Admin UTS State
  const [utsAdminBayiler, setUtsAdminBayiler] = useState([]);
  const [utsAdminLoglar, setUtsAdminLoglar] = useState([]);
  const [utsAdminSelectedBayi, setUtsAdminSelectedBayi] = useState(null);
  const [utsAdminBayiIzinler, setUtsAdminBayiIzinler] = useState([]);
  const [utsBildirimTipleri, setUtsBildirimTipleri] = useState([]);

  // Admin Dağıtım Ağı state'leri
  const [dagitimAgiBayiler, setDagitimAgiBayiler] = useState([]);
  const [dagitimAgiStats, setDagitimAgiStats] = useState({});
  const [dagitimAgiLoading, setDagitimAgiLoading] = useState(false);
  const [dagitimAgiExpandedFirma, setDagitimAgiExpandedFirma] = useState(null);
  const [dagitimAgiDetay, setDagitimAgiDetay] = useState(null); // { firma, stok, dagitimlar }
  const [dagitimAgiSearch, setDagitimAgiSearch] = useState('');

  // Admin Ürün Grupları & Resimler state'leri
  const [urunGruplari, setUrunGruplari] = useState([]);
  const [urunGruplariLoading, setUrunGruplariLoading] = useState(false);
  const [urunGruplariSearch, setUrunGruplariSearch] = useState('');
  const [urunGruplariKategori, setUrunGruplariKategori] = useState('');
  const [urunGruplariUploading, setUrunGruplariUploading] = useState(null); // uploading product id
  const [variantPriceModal, setVariantPriceModal] = useState(null); // { productId, parent, variants, sutPrices, editedPrices, search, loading, saving }
  const [variantPriceSearch, setVariantPriceSearch] = useState('');
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(null); // product id for media library picker
  const [mediaLibraryItems, setMediaLibraryItems] = useState([]);
  const [mediaLibraryLoading, setMediaLibraryLoading] = useState(false);
  const [mediaLibrarySearch, setMediaLibrarySearch] = useState('');
  const [variantActiveModal, setVariantActiveModal] = useState(null); // { productId, parent, variants, loading }

  // Seri no tekrar kontrolü (barkod bazlı, backend'e sorarak)
  const checkSeriNoDuplicates = async (barcode, lotNo, serials) => {
    if (!serials || serials.length === 0) return [];
    try {
      const resp = await authFetch('/api/sevkiyat/check-serials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ barcode, lot_no: lotNo, seri_nolar: serials })
      });
      if (resp.ok) {
        const data = await resp.json();
        return data.duplicates || [];
      }
    } catch (e) { console.error('Seri no kontrol hatası:', e); }
    return [];
  };

  // Tek seri no ekle (tekrar kontrolü ile)
  const addSingleSeriNo = async (sIdx, barcode, lotNo, serialNo) => {
    if (!serialNo.trim()) return;
    const sn = serialNo.trim();
    // Mevcut listede zaten var mı?
    const currentList = sevkForm?.items?.[sIdx]?.seri_nolar || [];
    if (currentList.includes(sn)) {
      alert(`"${sn}" seri numarası zaten bu listede mevcut!`);
      return;
    }
    // Backend'de daha önce kullanılmış mı?
    setSeriNoChecking(true);
    const dups = await checkSeriNoDuplicates(barcode, lotNo, [sn]);
    setSeriNoChecking(false);
    if (dups.length > 0) {
      alert(`"${sn}" seri numarası bu barkod (${barcode}) için daha önce kullanılmış!`);
      return;
    }
    setSevkForm(prev => ({ ...prev, items: prev.items.map((x, xi) => xi === sIdx ? { ...x, seri_nolar: [...(x.seri_nolar || []), sn] } : x) }));
    setSeriNoInput('');
  };

  // Toplu seri no ekle (tekrar kontrolü ile)
  const addBulkSeriNo = async (sIdx, barcode, lotNo, serials) => {
    if (!serials || serials.length === 0) return;
    // Mevcut listede zaten var olanları filtrele
    const currentList = sevkForm?.items?.[sIdx]?.seri_nolar || [];
    const localDups = serials.filter(sn => currentList.includes(sn));
    const newSerials = serials.filter(sn => !currentList.includes(sn));
    if (newSerials.length === 0) {
      alert('Oluşturulan seri numaralarının tümü zaten listede mevcut!');
      return;
    }
    // Backend'de daha önce kullanılmış mı?
    setSeriNoChecking(true);
    const dups = await checkSeriNoDuplicates(barcode, lotNo, newSerials);
    setSeriNoChecking(false);
    if (dups.length > 0) {
      const safeSerials = newSerials.filter(sn => !dups.includes(sn));
      const msg = `Aşağıdaki seri numaraları bu barkod (${barcode}) için daha önce kullanılmış:\n\n${dups.join(', ')}\n\n${safeSerials.length > 0 ? `Kalan ${safeSerials.length} adet seri no eklensin mi?` : 'Eklenecek yeni seri no kalmadı.'}`;
      if (safeSerials.length > 0 && window.confirm(msg)) {
        setSevkForm(prev => ({ ...prev, items: prev.items.map((x, xi) => xi === sIdx ? { ...x, seri_nolar: [...(x.seri_nolar || []), ...safeSerials] } : x) }));
      } else if (safeSerials.length === 0) {
        alert(msg);
      }
      setSeriNoDuplicates(dups);
    } else {
      if (localDups.length > 0) {
        alert(`${localDups.length} adet seri no zaten listede mevcuttu, geri kalanlar eklendi.`);
      }
      setSevkForm(prev => ({ ...prev, items: prev.items.map((x, xi) => xi === sIdx ? { ...x, seri_nolar: [...(x.seri_nolar || []), ...newSerials] } : x) }));
    }
    setSeriNoAutoStart(''); setSeriNoAutoCount('');
  };
  const [iadeForm, setIadeForm] = useState(null); // { orderId, item_index, qty, reason }
  const [iadeLoading, setIadeLoading] = useState(false);

  const LANGS = [
    { code: 'en', label: 'English', short: 'EN' },
    { code: 'tr', label: 'Türkçe', short: 'TR' },
    { code: 'es', label: 'Español', short: 'ES' },
    { code: 'fr', label: 'Français', short: 'FR' },
    { code: 'de', label: 'Deutsch', short: 'DE' },
    { code: 'it', label: 'Italiano', short: 'IT' },
    { code: 'pt', label: 'Português', short: 'PT' },
    { code: 'ru', label: 'Русский', short: 'RU' },
    { code: 'zh', label: '中文', short: 'ZH' },
    { code: 'ja', label: '日本語', short: 'JA' },
    { code: 'ar', label: 'العربية', short: 'AR' },
    { code: 'hi', label: 'हिन्दी' },
    { code: 'ko', label: '한국어' },
    { code: 'nl', label: 'Nederlands' },
    { code: 'sv', label: 'Svenska' },
    { code: 'pl', label: 'Polski' },
    { code: 'uk', label: 'Українська' },
    { code: 'fa', label: 'فارسی' },
    { code: 'el', label: 'Ελληνικά' },
    { code: 'he', label: 'עברית' },
    { code: 'id', label: 'Bahasa Indonesia' },
  ];

  const PAGE_SIZE = 200;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [allCategories, setAllCategories] = useState([]); // Tüm kategoriler ve ürün sayıları

  // Modal açıkken arka plan scroll'unu engelle
  useEffect(() => {
    const anyModalOpen = !!modalProduct || !!editingItemModal || !!addItemModal || !!showBayiModal || !!mobileMenuOpen || !!seriNoModal || !!sevkForm || !!iadeForm;
    if (anyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [modalProduct, editingItemModal, addItemModal, showBayiModal, mobileMenuOpen, seriNoModal, sevkForm, iadeForm]);

  // Kategorileri ve toplam ürün sayılarını arka planda çek
  React.useEffect(() => {
    console.log('Kategoriler yükleniyor...');
    fetch('/api/categories')
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(data => {
        setAllCategories(data.filter(c => c.name));
        console.log('Kategoriler yüklendi:', data);
      })
      .catch(err => {
        console.error('Kategoriler yüklenirken hata:', err);
      });
  }, []);

  // Ürünleri API'den çek (kategori seçilmeden çekme)
  const fetchProducts = React.useCallback(async (reset = false, kategori = category, startOffset = 0, searchTerm = '') => {
    if (!kategori && !searchTerm) return;
    setLoading(true);
    try {
      let params = new URLSearchParams();
      params.set('offset', startOffset);
      params.set('limit', 10000);
      params.set('lang', i18n.language);
      
      if (kategori && kategori !== 'Tüm Kategoriler') {
        params.set('kategori', kategori);
      }
      
      if (searchTerm && searchTerm.trim()) {
        params.set('search', searchTerm.trim());
      }
      
      const url = `/api/products?${params.toString()}`;
      console.log('API çağrısı yapılıyor:', url);
      
      const res = await fetch(url);
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const rawData = await res.json();
      console.log('API yanıtı alındı, ürün sayısı:', rawData.length);
      
      // measures alanını JSON parse et (SUT bazlı obje: { sutCode: [variants] })
      const data = rawData.map(p => ({
        ...p,
        measures: (() => {
          if (!p.measures) return {};
          if (typeof p.measures === 'object' && !Array.isArray(p.measures)) return p.measures;
          try {
            const parsed = JSON.parse(p.measures);
            // Eski format (array) uyumluluğu
            if (Array.isArray(parsed)) return { 'Genel': parsed };
            return parsed;
          } catch { return {}; }
        })()
      }));
      
      if (reset) {
        setProducts(data);
      } else {
        setProducts(prev => [...prev, ...data]);
      }
      
      setHasMore(false);
      setOffset(startOffset + data.length);
    } catch (error) {
      console.error('API çağrısında hata:', error);
      if (reset) setProducts([]);
      setHasMore(false);
    }
    setLoading(false);
  }, [category, i18n.language]);

  // Zam oranını API'den çek
  React.useEffect(() => {
    fetch('/api/settings/zam-orani')
      .then(r => r.json())
      .then(data => { setZamOrani(data.zam_orani || 0); setZamInput(String(data.zam_orani || 0)); })
      .catch(() => {});
  }, []);

  // Kategori değişince ürünleri sıfırla ve yeni kategoriye göre yükle
  React.useEffect(() => {
    setOffset(0);
    setProducts([]);
    setVisibleCount(PAGE_SIZE);
    if (category) fetchProducts(true, category, 0, '');
  }, [category, fetchProducts]);

  // Arama değişince debounce ile backend'e istek gönder
  React.useEffect(() => {
    if (!search || search.trim().length < 2) {
      // Arama temizlendiğinde kategoriye göre yeniden yükle
      if (search === '' && category) {
        setProducts([]);
        setVisibleCount(PAGE_SIZE);
        fetchProducts(true, category, 0, '');
      }
      return;
    }
    const timer = setTimeout(() => {
      setProducts([]);
      setVisibleCount(PAGE_SIZE);
      fetchProducts(true, category || 'Tüm Kategoriler', 0, search.trim());
    }, 400); // 400ms debounce
    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Daha Fazla Yükle butonu
  const handleLoadMore = () => {
    setVisibleCount(prev => prev + PAGE_SIZE);
  };
  
  // Cart state'ini localStorage'a kaydet
  React.useEffect(() => {
    localStorage.setItem('teklifCart', JSON.stringify(cart));
  }, [cart]);
  
  // Firma bilgilerini localStorage'a kaydet
  React.useEffect(() => {
    const firmaInfo = {
      firmaId,
      firma,
      telefon,
      email,
      sehir,
      ulke,
      bilgilerIletildi
    };
    localStorage.setItem('teklifFirmaInfo', JSON.stringify(firmaInfo));
  }, [firmaId, firma, telefon, email, sehir, bilgilerIletildi]);
  
  // Kategorileri otomatik çıkar (virgülle ayrılmış çoklu kategorileri de böl)
  // İlk harfi büyük yaparak tutarlı gruplama sağla (venous -> Venous)
  const categories = useMemo(() => {
    const allCats = [];
    const seen = new Set();
    products.forEach(p => {
      if (p.kategori) {
        p.kategori.split(',').forEach(c => {
          const trimmed = c.trim();
          if (trimmed) {
            // İlk harfi büyük yap
            const normalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
            if (!seen.has(normalized)) {
              seen.add(normalized);
              allCats.push(normalized);
            }
          }
        });
      }
    });
    return allCats;
  }, [products]);

  // Filtreli ürünler
  const filtered = useMemo(() => {
    let filteredProducts = products.filter(p => {
      // Kategori filtresi
      let categoryMatch = true;
      if (category && category !== 'Tüm Kategoriler') {
        // Belirli bir kategori seçildiğinde, ürünün kategorileri içinde arama yap
        // Çoklu kategorili ürünler virgülle ayrılmış olabilir (ör: "Arterial,Cardiac")
        categoryMatch = p.kategori && p.kategori.split(',').map(c => {
          const t = c.trim();
          return t.charAt(0).toUpperCase() + t.slice(1);
        }).includes(category);
      }
      // Tüm Kategoriler seçildiğinde filtreleme yapma, tüm ürünleri göster
      
      // Arama backend'de yapılıyor, client-side ek filtreleme gerekmiyor
      return categoryMatch;
    });

    // Tüm Kategoriler seçildiğinde özel sıralama yap
    if (category === 'Tüm Kategoriler') {
      const categoryOrder = [
        'Cardiac',
        'Arterial',
        'Neurovascular',
        'Venous',
        'Embolization',
        'Urology',
        'Orthopedic',
        'General Surgery',
        'Ophthalmolohy',
        'Oncology',
      ];
      
      // Çoklu kategorili ürünler için en yüksek öncelikli kategoriyi bul
      const getCategoryIndex = (kategori) => {
        if (!kategori) return 999;
        const cats = kategori.split(',').map(c => c.trim());
        let bestIdx = 999;
        for (const cat of cats) {
          const idx = categoryOrder.indexOf(cat);
          if (idx !== -1 && idx < bestIdx) bestIdx = idx;
        }
        return bestIdx;
      };
      
      filteredProducts.sort((a, b) => {
        const aIndex = getCategoryIndex(a.kategori);
        const bIndex = getCategoryIndex(b.kategori);
        
        // Eğer her iki kategori de sıralama listesinde varsa
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        
        // Eğer sadece a sıralama listesinde varsa
        if (aIndex !== -1) {
          return -1;
        }
        
        // Eğer sadece b sıralama listesinde varsa
        if (bIndex !== -1) {
          return 1;
        }
        
        // Her ikisi de sıralama listesinde yoksa alfabetik sırala
        return (a.kategori || '').localeCompare(b.kategori || '');
      });
    }

    return filteredProducts;
  }, [category, products]);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const paginated = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  
  // Tüm Kategoriler ve Orthopedics için hasMore durumu
  const hasMoreForAllCategories = useMemo(() => {
    if (category === 'Tüm Kategoriler' || category === 'Orthopedics') {
      return visibleCount < filtered.length;
    }
    return hasMore;
  }, [category, visibleCount, filtered.length, hasMore]);

  // Bayi başvuru formu gönderimi
  const handleBayiFormSubmit = async (e) => {
    e.preventDefault();
    if (bayiTab === 0) {
      setBayiTab(1);
      return;
    }
    if (!bayiSozlesmeOnay) return;
    setBayiStep(2);
    try {
      const res = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...bayiForm,
          password: bayiForm.sifre || '123456', // Şifre alanı eklenmemişse default ver
        })
      });
      if (res.ok) {
        setTimeout(() => {
          setBayiStep(3);
        }, 2000);
      } else {
        const data = await res.json();
        alert(data.error || 'Başvuru gönderilemedi');
        setBayiStep(1);
      }
    } catch (err) {
      alert('Başvuru gönderilemedi: ' + err.message);
      setBayiStep(1);
    }
  };

  const handleBayiFileChange = (e) => {
    setBayiFiles(Array.from(e.target.files));
  };

  const handleBayiComplete = () => {
    setShowBayiModal(false);
    setBayiStep(1);
    setBayiForm({});
    setBayiSozlesmeOnay(false);
    setBayiFiles([]);
    setBayiTab(0);
  };

  // Ölçü bilgisini formatlı göster (JSON ise parse et)
  function formatMeasureDisplay(measure) {
    if (!measure || measure === '-' || measure === '' || measure === 'Standart') return '';
    // JSON obje ise formatla
    if (typeof measure === 'string' && measure.trim().startsWith('{')) {
      try {
        const obj = JSON.parse(measure);
        const allVals = new Set();
        Object.values(obj).forEach(arr => {
          if (Array.isArray(arr)) arr.forEach(v => allVals.add(v));
        });
        return [...allVals].join(', ');
      } catch { return measure; }
    }
    if (typeof measure === 'object' && !Array.isArray(measure)) {
      const allVals = new Set();
      Object.values(measure).forEach(arr => {
        if (Array.isArray(arr)) arr.forEach(v => allVals.add(v));
      });
      return [...allVals].join(', ');
    }
    return String(measure);
  }

  // Toplam benzersiz ölçü sayısını hesapla
  function getTotalMeasureCount(measures) {
    if (!measures || typeof measures !== 'object' || Array.isArray(measures)) return 0;
    const all = new Set();
    Object.values(measures).forEach(arr => {
      if (Array.isArray(arr)) arr.forEach(m => all.add(m));
    });
    return all.size;
  }
  
  // Seçili SUT'un ölçülerini getir
  function getMeasuresForSut(measures, sutCode) {
    if (!measures || typeof measures !== 'object') return [];
    return measures[sutCode] || [];
  }

  // Ürünün SUT anahtarlarını al (measures veya sut_codes'dan)
  function getSutKeys(product) {
    // Önce measures'dan SUT anahtarlarını al
    if (product.measures && typeof product.measures === 'object' && !Array.isArray(product.measures)) {
      const keys = Object.keys(product.measures);
      if (keys.length > 0) return keys;
    }
    // Fallback: sut_codes'dan türet (birden fazla SUT kodu varsa veya sut_prices_map varsa)
    if (product.sut_codes) {
      const codes = product.sut_codes.split(',').map(s => s.trim()).filter(Boolean);
      // Tek SUT kodu varsa ve fiyat haritası yoksa atlayabiliriz, ama birden fazla varsa göstermemiz lazım
      if (codes.length > 1 || (product.sut_prices_map && Object.keys(product.sut_prices_map).length > 1)) {
        return codes;
      }
      // Tek kod ama sut_prices_map'te farklı fiyat bilgisi varsa göster
      if (codes.length === 1 && product.sut_prices_map && product.sut_prices_map[codes[0]]) {
        return codes;
      }
    }
    return [];
  }

  // SUT fiyat aralığını hesapla (min-max)
  function getSutPriceRange(product) {
    const pricesMap = product.sut_prices_map;
    if (!pricesMap || typeof pricesMap !== 'object') return null;
    const prices = Object.values(pricesMap).map(p => p.price).filter(p => p > 0);
    if (prices.length === 0) return null;
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return { min, max, count: prices.length };
  }

  // Fiyat formatla
  function formatPrice(price) {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(price);
  }

  // Belirli SUT kodunun fiyatını getir
  function getSutPrice(product, sutCode) {
    const pricesMap = product.sut_prices_map;
    if (!pricesMap || !pricesMap[sutCode]) return null;
    return pricesMap[sutCode].price;
  }


  // Modal aç - varyantları API'den çek
  function openModal(product, editIdx = null) {
    trackProductDetailOpen(product);
    setModalProduct(product);
    setModalVariants([]);
    setModalSutCodes([]);
    setModalParentGroups([]);
    setModalSutPrices({});
    setModalSelectedSut('');
    setModalSelectedParentGroup('');
    setModalSelectedVariant(null);
    setModalVariantSearch('');
    setModalLoading(true);
    setModalQty(editIdx !== null ? cart[editIdx].qty : 1);
    setModalDesc(editIdx !== null ? (cart[editIdx].desc || '') : '');
    setModalEditIdx(editIdx);

    fetch(`/api/products/${product.id}/variants`)
      .then(r => r.json())
      .then(data => {
        setModalVariants(data.variants || []);
        setModalSutCodes(data.sut_codes || []);
        setModalParentGroups(data.parent_groups || []);
        setModalSutPrices(data.sut_prices || {});
        if (data.sut_codes && data.sut_codes.length > 0) {
          setModalSelectedSut(data.sut_codes[0]);
        }
        if (editIdx !== null && cart[editIdx]) {
          const item = cart[editIdx];
          const found = (data.variants || []).find(v => v.barcode === item.barcode);
          if (found) {
            setModalSelectedVariant(found);
            if (found.sut_codes) {
              const firstSut = found.sut_codes.split(',')[0].trim();
              setModalSelectedSut(firstSut);
            }
            if (found.parent_group) setModalSelectedParentGroup(found.parent_group);
          }
        }
        setModalLoading(false);
      })
      .catch(() => setModalLoading(false));
  }

  // Sepete ekle
  function handleAddToCart(keepOpen = false) {
    if (!modalProduct || !modalSelectedVariant) return;
    if (!user) {
      setShowIletildiUyarisi(true);
      const loginForm = document.getElementById('login-panel');
      if (loginForm) loginForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const v = modalSelectedVariant;
    const sutPrice = modalSelectedSut && v.sut_prices_map?.[modalSelectedSut]
      ? v.sut_prices_map[modalSelectedSut].price : (v.price_min || 0);
    const selectedSutPrice = v.custom_price != null ? Number(v.custom_price) : sutPrice;
    const cartItem = {
      id: v.id, variantId: v.id, name: modalProduct.name, variantName: v.variant_name,
      sutCode: modalSelectedSut, measure: v.size_label || '', parentGroup: v.parent_group || '',
      qty: modalQty, desc: modalDesc, price: selectedSutPrice,
      marka: modalProduct.marka || '', kategori: modalProduct.kategori || '',
      barcode: v.barcode, refCode: v.ref_code || '', image: '/urunler/300x300.png'
    };
    if (modalEditIdx !== null) {
      setCart(prev => prev.map((item, i) => i === modalEditIdx ? cartItem : item));
    } else {
      setCart(prev => {
        const existingIdx = prev.findIndex(item => item.barcode === v.barcode && item.sutCode === modalSelectedSut);
        if (existingIdx !== -1) {
          return prev.map((item, i) => {
            if (i !== existingIdx) return item;
            return { ...item, qty: item.qty + modalQty,
              desc: modalDesc ? (item.desc ? item.desc + ' | ' + modalDesc : modalDesc) : item.desc,
              price: selectedSutPrice };
          });
        }
        return [...prev, cartItem];
      });
    }
    // Sepete ekleme logu
    trackCartAdd({ id: modalProduct.id, name: modalProduct.name, code: v.barcode, variant: v.variant_name, selectedVariant: modalSelectedSut }, modalQty);
    if (keepOpen) {
      setModalEditIdx(null); setModalQty(1); setModalDesc(''); setModalSelectedVariant(null);
    } else {
      trackProductDetailClose(modalProduct);
      setModalProduct(null); setModalEditIdx(null);
    }
  }

  // Sepetten çıkar
  function handleRemoveFromCart(idx) {
    const removedItem = cart[idx];
    if (removedItem) trackCartRemove({ id: removedItem.variantId, name: removedItem.name, code: removedItem.barcode, variant: removedItem.variantName });
    setCart(prev => prev.filter((_, i) => i !== idx));
  }

  // Arama takibi (debounced)
  useEffect(() => {
    if (!search || search.length < 2) return;
    const timer = setTimeout(() => {
      trackSearch(search, filtered?.length || 0);
    }, 1500);
    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Kategori filtre takibi
  useEffect(() => {
    if (category && category !== 'Tüm Kategoriler') {
      trackFilter('category', category);
    }
  }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sayfa görüntüleme takibi
  useEffect(() => {
    trackPageView('teklif_olustur');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showIletildiUyarisi) {
      const timer = setTimeout(() => setShowIletildiUyarisi(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showIletildiUyarisi]);

  const { user, login, logout, selectedFirma, selectFirma } = useUser();
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = React.useRef(null);

  // Firma taleplerini getir
  useEffect(() => {
    if (user?.id) {
      fetch(`/api/firma-talep/${user.id}`).then(r => r.ok ? r.json() : []).then(setTalepler).catch(() => {});
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handleClickOutside(event) {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    }
    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userDropdownOpen]);

  useEffect(() => {
    if (!user) {
      setFirma('');
      setTelefon('');
      setEmail('');
      setSehir('');
      setBilgilerIletildi(false);
      setRightPanelView('cart');
    }
  }, [user]);

  // Kullanıcı siparişlerini ve özetini çek (selectedFirma varsa firma_id ile filtrele)
  const fetchUserOrders = useCallback(async () => {
    if (!user?.email) return;
    setUserOrdersLoading(true);
    try {
      const token = user?.token || JSON.parse(localStorage.getItem('user') || '{}').token;
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};
      const firmaParam = selectedFirma?.id ? `?firma_id=${selectedFirma.id}` : '';
      const [ordersRes, summaryRes] = await Promise.all([
        fetch(`/api/user/orders/${encodeURIComponent(user.email)}${firmaParam}`, { headers: authHeaders }),
        fetch(`/api/user/summary/${encodeURIComponent(user.email)}${firmaParam}`, { headers: authHeaders })
      ]);
      if (ordersRes.ok) setUserOrders(await ordersRes.json());
      if (summaryRes.ok) setUserSummary(await summaryRes.json());
    } catch (err) {
      console.error('Siparişler yüklenirken hata:', err);
    } finally {
      setUserOrdersLoading(false);
    }
  }, [user?.email, selectedFirma?.id]);

  useEffect(() => {
    if (user && (mainView === 'siparislerim' || mainView === 'odeme')) {
      fetchUserOrders();
    }
  }, [user, mainView, fetchUserOrders, selectedFirma?.id]);

  // Carileri çek
  const fetchCariler = useCallback(async (searchVal, sehirVal, pageVal) => {
    setCarilerLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchVal) params.set('search', searchVal);
      if (sehirVal) params.set('sehir', sehirVal);
      params.set('page', pageVal || 1);
      params.set('limit', 30);
      const resp = await authFetch('/api/cariler?' + params.toString());
      if (resp.ok) {
        const data = await resp.json();
        setCariler(data.cariler || []);
        setCarilerTotal(data.total || 0);
      }
    } catch (e) { console.error('Cari fetch hatası:', e); }
    setCarilerLoading(false);
  }, []);

  const fetchCarilerStats = useCallback(async () => {
    try {
      const resp = await authFetch('/api/cariler/stats');
      if (resp.ok) setCarilerStats(await resp.json());
    } catch (e) {}
  }, []);

  // Tur adımlarını çek
  const fetchTourSteps = useCallback(async (pageFilter) => {
    setTourStepsLoading(true);
    try {
      const token = user?.token || JSON.parse(localStorage.getItem('user') || '{}').token;
      const params = new URLSearchParams();
      if (pageFilter) params.set('page', pageFilter);
      const resp = await fetch('/api/admin/tour-steps?' + params.toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setTourSteps(data.steps || []);
        setTourPages(data.pages || []);
      }
    } catch (e) { console.error('Tour steps fetch hatası:', e); }
    setTourStepsLoading(false);
  }, [user]);

  const saveTourStep = useCallback(async (stepData, isNew = false) => {
    setTourSaving(true);
    try {
      const token = user?.token || JSON.parse(localStorage.getItem('user') || '{}').token;
      const url = isNew ? '/api/admin/tour-steps' : `/api/admin/tour-steps/${stepData.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const resp = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(stepData)
      });
      if (resp.ok) {
        fetchTourSteps(tourSelectedPage);
        setTourEditingStep(null);
        setTourNewStep(false);
        return true;
      }
    } catch (e) {}
    setTourSaving(false);
    return false;
  }, [user, fetchTourSteps, tourSelectedPage]);

  const deleteTourStep = useCallback(async (id) => {
    if (!window.confirm('Bu tur adımını silmek istediğinize emin misiniz?')) return;
    try {
      const token = user?.token || JSON.parse(localStorage.getItem('user') || '{}').token;
      await fetch(`/api/admin/tour-steps/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTourSteps(tourSelectedPage);
    } catch (e) {}
  }, [user, fetchTourSteps, tourSelectedPage]);

  const moveTourStep = useCallback(async (idx, direction) => {
    const filtered = tourSteps.filter(s => s.page === tourSelectedPage);
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === filtered.length - 1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const items = [
      { id: filtered[idx].id, step_order: filtered[swapIdx].step_order },
      { id: filtered[swapIdx].id, step_order: filtered[idx].step_order }
    ];
    try {
      const token = user?.token || JSON.parse(localStorage.getItem('user') || '{}').token;
      await fetch('/api/admin/tour-steps-reorder', {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ items })
      });
      fetchTourSteps(tourSelectedPage);
    } catch (e) {}
  }, [user, tourSteps, tourSelectedPage, fetchTourSteps]);

  // Tur yönetimi yükleme
  useEffect(() => {
    if (user?.role === 'admin' && mainView === 'tourManagement') {
      fetchTourSteps(tourSelectedPage);
    }
  }, [user, mainView, tourSelectedPage, fetchTourSteps]);

  // Aktivite logları çek
  const fetchActivityLogs = useCallback(async () => {
    setActivityLogsLoading(true);
    try {
      const token = user?.token || JSON.parse(localStorage.getItem('user') || '{}').token;
      const params = new URLSearchParams();
      params.set('page', activityLogsPage);
      params.set('limit', 50);
      if (activityLogsSearch) params.set('search', activityLogsSearch);
      if (activityLogsActionType) params.set('action_type', activityLogsActionType);
      if (activityLogsDateFrom) params.set('date_from', activityLogsDateFrom);
      if (activityLogsDateTo) params.set('date_to', activityLogsDateTo);
      const resp = await fetch('/api/admin/activity-logs?' + params.toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setActivityLogs(data.logs || []);
        setActivityLogsTotal(data.total || 0);
        setActivityLogsTotalPages(data.totalPages || 0);
      }
    } catch (e) { console.error('Activity logs fetch hatası:', e); }
    setActivityLogsLoading(false);
  }, [user, activityLogsPage, activityLogsSearch, activityLogsActionType, activityLogsDateFrom, activityLogsDateTo]);

  const fetchActivityLogsStats = useCallback(async () => {
    try {
      const token = user?.token || JSON.parse(localStorage.getItem('user') || '{}').token;
      const params = new URLSearchParams();
      if (activityLogsDateFrom) params.set('date_from', activityLogsDateFrom);
      if (activityLogsDateTo) params.set('date_to', activityLogsDateTo);
      const resp = await fetch('/api/admin/activity-logs/stats?' + params.toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) setActivityLogsStats(await resp.json());
    } catch (e) {}
  }, [user, activityLogsDateFrom, activityLogsDateTo]);

  const fetchSessionDetail = useCallback(async (sessionId) => {
    setActivityLogsSessionLoading(true);
    try {
      const token = user?.token || JSON.parse(localStorage.getItem('user') || '{}').token;
      const resp = await fetch(`/api/admin/activity-logs/session/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setActivityLogsSessionDetail(data.logs || []);
      }
    } catch (e) {}
    setActivityLogsSessionLoading(false);
  }, [user]);

  // Admin siparişleri çek
  const fetchAdminOrders = useCallback(async (searchVal, statusVal, emailVal, pageVal, paymentStatusVal) => {
    setAdminOrdersLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchVal) params.set('search', searchVal);
      if (statusVal) params.set('status', statusVal);
      if (emailVal) params.set('email', emailVal);
      if (paymentStatusVal) params.set('payment_status', paymentStatusVal);
      params.set('page', pageVal || 1);
      params.set('limit', 30);
      const resp = await authFetch('/api/admin/orders?' + params.toString());
      if (resp.ok) {
        const data = await resp.json();
        setAdminOrders(data.orders || []);
        setAdminOrdersTotal(data.total || 0);
      }
    } catch (e) { console.error('Admin orders fetch error:', e); }
    setAdminOrdersLoading(false);
  }, []);

  const fetchAdminOrdersStats = useCallback(async () => {
    try {
      const resp = await authFetch('/api/admin/orders/stats');
      if (resp.ok) setAdminOrdersStats(await resp.json());
    } catch (e) {}
  }, []);

  // Cari detay çek
  const fetchCariDetail = useCallback(async (cariId) => {
    setCariDetailLoading(true);
    setCariDetailTab('siparisler');
    setCariOrderExpanded(null);
    try {
      const resp = await authFetch('/api/cariler/' + cariId);
      if (resp.ok) {
        const data = await resp.json();
        setCariDetail(data.cari);
        setCariDetailOrders(data.orders || []);
        setCariDetailFirmalar(data.firmalar || []);
      }
    } catch (e) { console.error('Cari detail fetch error:', e); }
    setCariDetailLoading(false);
  }, []);

  // Cari firma arama
  const searchFirmaForCari = useCallback(async (query) => {
    if (!query || query.length < 2) { setFirmaSearchResults([]); return; }
    setFirmaSearchLoading(true);
    try {
      const resp = await fetch('/api/guest-firmalar');
      if (resp.ok) {
        const all = await resp.json();
        const q = query.toLowerCase();
        const filtered = all.filter(f => 
          (f.firma && f.firma.toLowerCase().includes(q)) ||
          (f.email && f.email.toLowerCase().includes(q)) ||
          (f.vergiNo && f.vergiNo.includes(q))
        );
        setFirmaSearchResults(filtered.slice(0, 10));
      }
    } catch (e) { console.error('Firma search error:', e); }
    setFirmaSearchLoading(false);
  }, []);

  // Cariye firma ata
  const handleAtaFirma = useCallback(async (userId, firmaId, isDefault = false) => {
    setFirmaAtamaLoading(true);
    try {
      const resp = await fetch(`/api/users/${userId}/firmalar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firma_id: firmaId, is_default: isDefault })
      });
      if (resp.ok) {
        const data = await resp.json();
        setCariDetailFirmalar(data.firmalar || []);
        setFirmaSearchQuery('');
        setFirmaSearchResults([]);
      }
    } catch (e) { console.error('Firma atama error:', e); }
    setFirmaAtamaLoading(false);
  }, []);

  // Cariden firma yetkisini kaldır
  const handleRemoveFirma = useCallback(async (userId, firmaId) => {
    if (!window.confirm('Bu firma yetkisini kaldırmak istediğinize emin misiniz?')) return;
    try {
      const resp = await fetch(`/api/users/${userId}/firmalar/${firmaId}`, { method: 'DELETE' });
      if (resp.ok) {
        const data = await resp.json();
        setCariDetailFirmalar(data.firmalar || []);
      }
    } catch (e) { console.error('Firma remove error:', e); }
  }, []);

  // Varsayılan firma değiştir
  const handleSetDefaultFirma = useCallback(async (userId, firmaId) => {
    try {
      const resp = await fetch(`/api/users/${userId}/firmalar/${firmaId}/default`, { method: 'PUT' });
      if (resp.ok) {
        const data = await resp.json();
        setCariDetailFirmalar(data.firmalar || []);
      }
    } catch (e) { console.error('Default firma error:', e); }
  }, []);

  // Cari güncelle
  const handleSaveCari = async () => {
    if (!editingCari) return;
    setCariSaving(true);
    try {
      const resp = await authFetch('/api/cariler/' + editingCari.id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cariForm)
      });
      if (resp.ok) {
        setEditingCari(null);
        fetchCariler(carilerSearch, carilerSehir, carilerPage);
      }
    } catch (e) { console.error('Cari save error:', e); }
    setCariSaving(false);
  };

  // Cari aktif/pasif toggle
  const handleToggleCariStatus = async (cariId) => {
    try {
      const resp = await authFetch('/api/cariler/' + cariId + '/toggle-status', { method: 'PUT' });
      if (resp.ok) {
        fetchCariler(carilerSearch, carilerSehir, carilerPage);
        if (cariDetail?.id === cariId) fetchCariDetail(cariId);
      }
    } catch (e) { console.error('Toggle status error:', e); }
  };

  // Sipariş state'ini yerinde güncelle (sayfa yenilemeden)
  const updateAdminOrderLocal = (orderId, updates) => {
    setAdminOrders(prev => prev.map(o => o.id === orderId ? { ...o, ...updates } : o));
  };

  // Sipariş ödeme kaydı ekle (çoklu ödeme)
  const handleAddPayment = async (orderId, paymentData) => {
    try {
      const resp = await authFetch('/api/guest-offers/' + orderId + '/payments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paymentData)
      });
      if (resp.ok) {
        const data = await resp.json();
        updateAdminOrderLocal(orderId, { payment_status: data.payment_status, payment_amount: data.totalPaid, _payments: data.payments, _totalPaid: data.totalPaid, _orderTotal: data.orderTotal });
        return data;
      }
    } catch (e) { console.error('Payment add error:', e); }
    return null;
  };

  // Ödeme kaydı sil
  const handleDeletePayment = async (paymentId, orderId) => {
    try {
      const resp = await authFetch('/api/payments/' + paymentId, { method: 'DELETE' });
      if (resp.ok) {
        const data = await resp.json();
        updateAdminOrderLocal(orderId, { payment_status: data.payment_status, payment_amount: data.totalPaid, _payments: data.payments, _totalPaid: data.totalPaid });
        return data;
      }
    } catch (e) { console.error('Payment delete error:', e); }
    return null;
  };

  // Sipariş ödemelerini yükle (ilk açılışta)
  const loadOrderPayments = async (orderId) => {
    try {
      const resp = await authFetch('/api/guest-offers/' + orderId + '/payments');
      if (resp.ok) {
        const data = await resp.json();
        updateAdminOrderLocal(orderId, { _payments: data.payments, _totalPaid: data.totalPaid });
        return data;
      }
    } catch (e) { console.error('Payment load error:', e); }
    return null;
  };

  // ── Sevkiyat & İade yükleme fonksiyonları ──
  const fetchSevkiyatlar = async (orderId) => {
    try {
      const resp = await authFetch('/api/sevkiyat/order/' + orderId);
      if (resp.ok) {
        const data = await resp.json();
        setSevkiyatlar(prev => ({ ...prev, [orderId]: data }));
      }
    } catch (e) { console.error('Sevkiyat load error:', e); }
  };

  const fetchIadeler = async (orderId) => {
    try {
      const resp = await authFetch('/api/iade/order/' + orderId);
      if (resp.ok) {
        const data = await resp.json();
        setIadeler(prev => ({ ...prev, [orderId]: data }));
      }
    } catch (e) { console.error('İade load error:', e); }
  };

  const handleCreateSevkiyat = async (orderId) => {
    if (!sevkForm || sevkLoading) return;
    const activeItems = sevkForm.items.filter(i => i.qty > 0);
    if (activeItems.length === 0) return alert('En az bir kalem seçmelisiniz');
    // Zorunlu alan kontrolü
    const missingFields = [];
    for (const item of activeItems) {
      if (!item.lot_no || !item.lot_no.trim()) missingFields.push(`"${item.name}" için Lot No`);
      if (!item.uretim_tarihi) missingFields.push(`"${item.name}" için Üretim Tarihi`);
      if (!item.skt) missingFields.push(`"${item.name}" için SKT`);
    }
    if (missingFields.length > 0) {
      alert('Lütfen zorunlu alanları doldurun:\n\n' + missingFields.join('\n'));
      return;
    }
    setSevkLoading(true);
    try {
      const resp = await authFetch('/api/sevkiyat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          items: activeItems.map(i => ({ item_index: i.item_index, qty: i.qty, lot_no: i.lot_no || '', uretim_tarihi: i.uretim_tarihi || '', skt: i.skt || '', seri_nolar: i.seri_nolar || [] })),
          notes: sevkNotes || undefined,
          tracking_no: sevkTrackingNo || undefined,
          kargo_firmasi: sevkKargoFirmasi || undefined,
          created_by: user?.id
        })
      });
      const contentType = resp.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Sunucu yanıtı geçersiz. Lütfen backend servisinin çalıştığından emin olun.');
      }
      const data = await resp.json();
      if (resp.ok) {
        setSevkForm(null); setSevkNotes(''); setSevkTrackingNo(''); setSevkKargoFirmasi('');
        fetchSevkiyatlar(orderId);
        fetchAdminOrders(adminOrdersSearch, adminOrdersStatus, adminOrdersEmail, adminOrdersPage, adminOrdersPaymentStatus);
      } else { alert(data.error || 'Sevkiyat oluşturulamadı'); }
    } catch (e) { alert('Hata: ' + e.message); }
    setSevkLoading(false);
  };

  const handleSevkiyatStatusChange = async (sevkId, orderId, newStatus) => {
    if (newStatus === 'iptal' && !window.confirm('Sevkiyatı iptal etmek istediğinize emin misiniz?')) return;
    try {
      const resp = await authFetch('/api/sevkiyat/' + sevkId + '/status', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (resp.ok) {
        fetchSevkiyatlar(orderId);
        fetchAdminOrders(adminOrdersSearch, adminOrdersStatus, adminOrdersEmail, adminOrdersPage, adminOrdersPaymentStatus);
      } else { const d = await resp.json().catch(() => ({})); alert(d.error || 'Güncelleme başarısız'); }
    } catch (e) { alert('Hata: ' + e.message); }
  };

  const handleCreateIade = async (orderId) => {
    if (!iadeForm || iadeLoading) return;
    if (!iadeForm.qty || iadeForm.qty <= 0) return alert('Miktar belirtmelisiniz');
    setIadeLoading(true);
    try {
      const resp = await authFetch('/api/iade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          item_index: iadeForm.item_index,
          qty: iadeForm.qty,
          reason: iadeForm.reason || '',
          requested_by: user?.role === 'admin' ? 'admin' : 'firma'
        })
      });
      const data = await resp.json();
      if (resp.ok) {
        setIadeForm(null);
        fetchIadeler(orderId);
        fetchAdminOrders(adminOrdersSearch, adminOrdersStatus, adminOrdersEmail, adminOrdersPage, adminOrdersPaymentStatus);
      } else { alert(data.error || 'İade talebi oluşturulamadı'); }
    } catch (e) { alert('Hata: ' + e.message); }
    setIadeLoading(false);
  };

  const handleIadeStatusChange = async (iadeId, orderId, newStatus) => {
    try {
      const resp = await authFetch('/api/iade/' + iadeId + '/status', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (resp.ok) {
        fetchIadeler(orderId);
        fetchAdminOrders(adminOrdersSearch, adminOrdersStatus, adminOrdersEmail, adminOrdersPage, adminOrdersPaymentStatus);
      } else { const d = await resp.json().catch(() => ({})); alert(d.error || 'Güncelleme başarısız'); }
    } catch (e) { alert('Hata: ' + e.message); }
  };

  useEffect(() => {
    if (user?.role === 'admin' && mainView === 'cariler') {
      fetchCariler(carilerSearch, carilerSehir, carilerPage);
      fetchCarilerStats();
    }
  }, [user, mainView, carilerSearch, carilerSehir, carilerPage, fetchCariler, fetchCarilerStats]);

  // Aktivite logları yükleme
  useEffect(() => {
    if (user?.role === 'admin' && mainView === 'activityLogs') {
      fetchActivityLogs();
      fetchActivityLogsStats();
    }
  }, [user, mainView, fetchActivityLogs, fetchActivityLogsStats]);

  useEffect(() => {
    if (user?.role === 'admin' && mainView === 'adminOrders') {
      fetchAdminOrders(adminOrdersSearch, adminOrdersStatus, adminOrdersEmail, adminOrdersPage, adminOrdersPaymentStatus);
      fetchAdminOrdersStats();
    }
  }, [user, mainView, adminOrdersSearch, adminOrdersStatus, adminOrdersPaymentStatus, adminOrdersEmail, adminOrdersPage, fetchAdminOrders, fetchAdminOrdersStats]);

  // Bayi Stok Yönetimi - veri yükleme
  const fetchBayiStokData = async (firmaId) => {
    if (!firmaId) return;
    setBayiStokLoading(true);
    try {
      const [teslimResp, stokResp, dagResp, illerResp] = await Promise.all([
        authFetch('/api/bayi/teslim-bekleyen/' + firmaId),
        authFetch('/api/bayi/stok/' + firmaId),
        authFetch('/api/bayi/dagitimlar/' + firmaId),
        fetch('/api/hastaneler/iller')
      ]);
      if (teslimResp.ok) setBayiTeslimBekleyen(await teslimResp.json());
      if (stokResp.ok) setBayiStok(await stokResp.json());
      if (dagResp.ok) setBayiDagitimlar(await dagResp.json());
      if (illerResp.ok) setHastaneIller(await illerResp.json());
    } catch (e) { console.error('Bayi stok yükleme hatası:', e); }
    setBayiStokLoading(false);
  };

  const fetchHastaneler = async (il, tipi) => {
    try {
      let url = '/api/hastaneler?';
      if (il) url += 'il=' + encodeURIComponent(il) + '&';
      if (tipi) url += 'tipi=' + encodeURIComponent(tipi);
      const resp = await fetch(url);
      if (resp.ok) setHastaneListesi(await resp.json());
    } catch (e) { console.error('Hastane yükleme hatası:', e); }
  };

  // ── UTS Fonksiyonları ──
  const fetchUtsDurum = async (firmaId) => {
    try {
      const resp = await authFetch('/api/uts/durum/' + firmaId);
      if (resp.ok) { const d = await resp.json(); setUtsDurum(d); if (d.kayitli) { setUtsForm({ uts_token: '', uts_kurum_no: d.uts_kurum_no || '', is_test: !!d.is_test }); } }
    } catch (e) { console.error('UTS durum hatası:', e); }
  };

  const handleUtsTokenKaydet = async () => {
    if (!selectedFirma?.id || !utsForm.uts_token) { alert('Token zorunludur'); return; }
    setUtsLoading(true);
    try {
      const resp = await authFetch('/api/uts/token-kaydet', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ firma_id: selectedFirma.id, ...utsForm }) });
      const data = await resp.json();
      if (resp.ok) { alert(data.message); fetchUtsDurum(selectedFirma.id); } else { alert(data.error || 'Hata'); }
    } catch (e) { alert('Hata: ' + e.message); }
    setUtsLoading(false);
  };

  const handleUtsTestBaglanti = async () => {
    if (!selectedFirma?.id) return;
    setUtsLoading(true);
    try {
      const resp = await authFetch('/api/uts/test-baglanti', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ firma_id: selectedFirma.id }) });
      const data = await resp.json();
      if (data.success) {
        alert('UTS bağlantısı başarılı!\n\nOrtam: ' + (data.isTest ? 'Test (utstest.saglik.gov.tr)' : 'Gerçek (utsuygulama.saglik.gov.tr)') + '\nHTTP Durum: ' + data.statusCode);
      } else {
        const detay = data.detail?.errorCode || data.detail || '';
        const errorMsg = data.error || data.message || 'Bilinmeyen hata';
        let msg = 'UTS bağlantı hatası!\n\n' + errorMsg;
        if (detay) msg += '\n\nHata Kodu: ' + detay;
        if (data.isTimeout) msg += '\n\n⚠️ Sunucu IP adresinin UTS sisteminde tanımlı olduğundan emin olun.';
        if (data.suggestion) msg += '\n\nÖneri: ' + data.suggestion;
        alert(msg);
      }
    } catch (e) { alert('Bağlantı hatası: ' + e.message); }
    setUtsLoading(false);
  };

  const fetchUtsBildirimGecmisi = async (firmaId) => {
    try {
      const resp = await authFetch('/api/uts/bildirim-gecmisi/' + firmaId);
      if (resp.ok) { const d = await resp.json(); setUtsBildirimGecmisi(d.bildirimler || []); }
    } catch (e) { console.error(e); }
  };

  const fetchUtsBekleyenAlmalar = async (firmaId) => {
    try {
      const resp = await authFetch('/api/uts/bekleyen-almalar/' + firmaId);
      if (resp.ok) { const d = await resp.json(); setUtsBekleyenAlmalar(d.data); }
    } catch (e) { console.error(e); }
  };

  const handleUtsBildirimGonder = async () => {
    if (!selectedFirma?.id || !utsBildirimTipi) { alert('Bildirim tipi seçiniz'); return; }
    setUtsLoading(true);
    try {
      const resp = await authFetch('/api/uts/bildirim', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ firma_id: selectedFirma.id, bildirim_tipi: utsBildirimTipi, data: utsBildirimForm }) });
      const data = await resp.json();
      if (resp.ok && data.success) { alert('Bildirim başarıyla gönderildi!'); fetchUtsBildirimGecmisi(selectedFirma.id); setUtsBildirimForm({}); }
      else { alert('UTS Hatası: ' + (data.error || JSON.stringify(data.data))); }
    } catch (e) { alert('Hata: ' + e.message); }
    setUtsLoading(false);
  };

  const handleUtsSorgula = async () => {
    if (!selectedFirma?.id) return;
    setUtsLoading(true);
    try {
      const resp = await authFetch('/api/uts/tekil-urun-sorgula', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ firma_id: selectedFirma.id, ...utsSorguForm }) });
      if (resp.ok) { const d = await resp.json(); setUtsSorguSonuc(d.data); }
    } catch (e) { alert('Sorgulama hatası: ' + e.message); }
    setUtsLoading(false);
  };

  // Admin UTS fonksiyonları
  const fetchUtsAdminBayiler = async () => {
    try {
      const resp = await authFetch('/api/admin/uts/bayiler');
      if (resp.ok) setUtsAdminBayiler(await resp.json());
    } catch (e) { console.error(e); }
  };

  const fetchUtsAdminLoglar = async () => {
    try {
      const resp = await authFetch('/api/admin/uts/loglar');
      if (resp.ok) { const d = await resp.json(); setUtsAdminLoglar(d.loglar || []); }
    } catch (e) { console.error(e); }
  };

  const fetchUtsAdminBayiIzinler = async (firmaId) => {
    try {
      const resp = await authFetch('/api/admin/uts/bayi/' + firmaId + '/izinler');
      if (resp.ok) setUtsAdminBayiIzinler(await resp.json());
    } catch (e) { console.error(e); }
  };

  const handleUtsAdminOnay = async (firmaId, onay, not) => {
    try {
      const resp = await authFetch('/api/admin/uts/bayi/' + firmaId + '/onay', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ onay, not }) });
      if (resp.ok) { alert(`UTS erişimi ${onay === 'onaylandi' ? 'onaylandı' : 'reddedildi'}`); fetchUtsAdminBayiler(); }
    } catch (e) { alert('Hata: ' + e.message); }
  };

  const handleUtsAdminIzinGuncelle = async (firmaId, bildirimTipi, izinDurumu) => {
    try {
      await authFetch('/api/admin/uts/bayi/' + firmaId + '/izinler', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ izinler: [{ bildirim_tipi: bildirimTipi, izin_durumu: izinDurumu }] }) });
      fetchUtsAdminBayiIzinler(firmaId);
    } catch (e) { console.error(e); }
  };

  const handleUtsAdminAktifPasif = async (firmaId, aktif) => {
    try {
      await authFetch('/api/admin/uts/bayi/' + firmaId + '/aktif', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ aktif }) });
      fetchUtsAdminBayiler();
    } catch (e) { alert('Hata: ' + e.message); }
  };

  const handleTeslimAl = async (sevkiyatId, orderId) => {
    if (!selectedFirma?.id) return;
    if (!window.confirm('Bu sevkiyatı teslim aldığınızı onaylıyor musunuz?')) return;
    try {
      const resp = await authFetch('/api/bayi/teslim-al', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sevkiyat_id: sevkiyatId, firma_id: selectedFirma.id, teslim_notu: '' })
      });
      const data = await resp.json();
      if (resp.ok) {
        alert('Teslim alındı! Ürünler stoğunuza eklendi.');
        // Stok yönetimi verilerini güncelle
        fetchBayiStokData(selectedFirma.id);
        // Siparişlerim sayfasını güncelle
        if (orderId) fetchSevkiyatlar(orderId);
        fetchUserOrders();

        // UTS otomatik alma bildirimi önerisi
        if (utsDurum?.kayitli) {
          const utsOneri = window.confirm('Ürün teslim alındı. Bu teslim almayı UTS sistemine de bildirmek ister misiniz?\n\n(UTS Alma Bildirimi)');
          if (utsOneri) {
            setMainView('stokYonetimi');
            navigate('/dash/stok-yonetimi');
            setUtsTab('bildirim');
            setUtsBildirimTipi('alma');
          }
        }
      } else { alert(data.error || 'Teslim alma başarısız'); }
    } catch (e) { alert('Hata: ' + e.message); }
  };

  const handleDagitimKaydet = async () => {
    if (!selectedFirma?.id || !dagitimModal) return;
    if (!dagitimForm.hastane_kurum || !dagitimForm.operasyon_tarihi) {
      alert('Hastane ve Operasyon Tarihi zorunludur');
      return;
    }
    const seriNolar = dagitimForm.seri_nolar || [];
    if (dagitimModal.seri_nolar && dagitimModal.seri_nolar.length > 0 && seriNolar.length === 0) {
      alert('Lütfen dağıtılacak seri numaralarını seçin');
      return;
    }
    setDagitimLoading(true);
    try {
      const resp = await authFetch('/api/bayi/dagitim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firma_id: selectedFirma.id,
          barcode: dagitimModal.barcode,
          product_name: dagitimModal.product_name,
          measure: dagitimModal.measure,
          lot_no: dagitimModal.lot_no,
          seri_nolar: seriNolar,
          qty: seriNolar.length > 0 ? seriNolar.length : 1,
          il: dagitimForm.il,
          hastane_tipi: dagitimForm.hastane_tipi,
          hastane_kurum: dagitimForm.hastane_kurum,
          operasyon_tarihi: dagitimForm.operasyon_tarihi,
          operasyon_notu: dagitimForm.operasyon_notu
        })
      });
      const data = await resp.json();
      if (resp.ok) {
        const savedModal = dagitimModal; // UTS bildirimi için kaydet
        setDagitimModal(null);
        setDagitimForm({ il: '', hastane_tipi: '', hastane_kurum: '', operasyon_tarihi: '', seri_nolar: [], operasyon_notu: '' });
        setHastaneListesi([]);
        fetchBayiStokData(selectedFirma.id);

        // UTS otomatik kullanım/verme bildirimi önerisi
        if (utsDurum?.kayitli) {
          const utsOneri = window.confirm('Dağıtım kaydedildi! Bu dağıtımı UTS sistemine de bildirmek ister misiniz?\n\n(UTS Kullanım/Verme Bildirimi)');
          if (utsOneri) {
            setUtsTab('bildirim');
            setUtsBildirimTipi('kullanim');
            // Önceki dağıtım bilgilerini forma otomatik doldur
            setUtsBildirimForm({
              UNO: savedModal?.barcode || '',
              LNO: savedModal?.lot_no || '',
              SNO: seriNolar.length > 0 ? seriNolar[0] : '',
              ADT: seriNolar.length > 0 ? seriNolar.length : 1,
              HAA: dagitimForm.hastane_kurum || '',
            });
          }
        }
      } else { alert(data.error || 'Dağıtım kaydedilemedi'); }
    } catch (e) { alert('Hata: ' + e.message); }
    setDagitimLoading(false);
  };

  // Admin Dağıtım Ağı - veri yükleme
  const fetchDagitimAgi = async () => {
    setDagitimAgiLoading(true);
    try {
      const resp = await authFetch('/api/admin/dagitim-agi');
      if (resp.ok) {
        const data = await resp.json();
        setDagitimAgiBayiler(data.bayiler || []);
        setDagitimAgiStats(data.stats || {});
      }
    } catch (e) { console.error('Dağıtım ağı yükleme hatası:', e); }
    setDagitimAgiLoading(false);
  };

  const fetchDagitimAgiDetay = async (firmaId) => {
    try {
      const resp = await authFetch('/api/admin/dagitim-agi/' + firmaId);
      if (resp.ok) setDagitimAgiDetay(await resp.json());
    } catch (e) { console.error('Dağıtım detay yükleme hatası:', e); }
  };

  useEffect(() => {
    if (mainView === 'stokYonetimi' && selectedFirma?.id) {
      fetchBayiStokData(selectedFirma.id);
    }
  }, [mainView, selectedFirma]);

  useEffect(() => {
    if (user?.role === 'admin' && mainView === 'dagitimAgi') {
      fetchDagitimAgi();
    }
  }, [user, mainView]);

  // UTS: Hesabım veya stokYonetimi açıldığında UTS durumunu yükle
  useEffect(() => {
    if (selectedFirma?.id && (mainView === 'hesabim' || mainView === 'stokYonetimi')) {
      fetchUtsDurum(selectedFirma.id);
      authFetch('/api/uts/bildirim-tipleri').then(r => r.ok ? r.json() : []).then(d => setUtsBildirimTipleri(d)).catch(() => {});
    }
  }, [mainView, selectedFirma]);

  // UTS: Admin UTS Yönetimi görünümü
  useEffect(() => {
    if (user?.role === 'admin' && mainView === 'utsYonetimi') {
      fetchUtsAdminBayiler();
      fetchUtsAdminLoglar();
      authFetch('/api/uts/bildirim-tipleri').then(r => r.ok ? r.json() : []).then(d => setUtsBildirimTipleri(d)).catch(() => {});
    }
  }, [user, mainView]);

  // Admin Ürün Grupları verisi çek
  useEffect(() => {
    if (user?.role === 'admin' && mainView === 'urunGruplari') {
      setUrunGruplariLoading(true);
      authFetch('/api/admin/product-groups')
        .then(r => r.json())
        .then(data => { setUrunGruplari(data); setUrunGruplariLoading(false); })
        .catch(() => setUrunGruplariLoading(false));
    }
  }, [user, mainView]);

  // Infinite scroll için event handler
  useEffect(() => {
    if (category !== 'Tüm Kategoriler' && category !== 'Orthopedics') return;
    function handleScroll() {
      if (
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 200 &&
        visibleCount < filtered.length
      ) {
        setVisibleCount(prev => prev + PAGE_SIZE);
      }
    }
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [category, visibleCount, filtered.length]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-r from-[#e0e7ff] via-[#f7f8fa] to-[#c7d2fe] animate-gradient-move relative overflow-hidden w-full">
      <style>{`
        @keyframes gradient-move {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient-move {
          background-size: 200% 200%;
          animation: gradient-move 12s ease-in-out infinite;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
      {/* NAVBAR */}
      <nav className="w-full bg-white px-0 py-0 shadow-sm border-b border-[#e3e6ef] flex items-center justify-between h-14 md:h-16" data-tour="navbar">
        {/* Sol: Hamburger (mobil) + Logo */}
        <div className="flex items-center gap-2 h-full pl-3 md:pl-6">
          {/* Hamburger menü butonu - sadece mobil */}
          {user && (
            <button className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition" onClick={() => setMobileMenuOpen(v => !v)}>
              {mobileMenuOpen ? (
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              ) : (
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>
              )}
            </button>
          )}
          <div className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate('/dash')}>
            <img src="/invamedorder.svg" alt="INVAMED Order" className="h-7 md:h-10 w-auto" style={{objectFit:'contain'}} />
          </div>
        </div>
        {/* Sağ: Dil (kısa) + Kullanıcı (desktop) */}
        <div className="flex items-center gap-2 md:gap-4 pr-3 md:pr-6 ml-auto">
            <div className="flex items-center gap-2">
              {/* Firma Seçici - Birden fazla firma yetkisi varsa */}
              {user?.firmalar && user.firmalar.length > 1 && selectedFirma && (
                <div className="relative">
                  <button
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 transition text-amber-800 font-medium text-xs border border-amber-200"
                    onClick={() => {
                      const el = document.getElementById('teklifFirmaDropdown');
                      if (el) el.classList.toggle('hidden');
                    }}
                  >
                    <svg className="w-3.5 h-3.5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 0h.008v.008h-.008V7.5Z" />
                    </svg>
                    <span className="max-w-[100px] truncate">{selectedFirma.firma}</span>
                    <svg className="w-3 h-3 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  <div id="teklifFirmaDropdown" className="hidden absolute right-0 mt-1 w-56 bg-white border border-amber-100 rounded-xl shadow-xl py-1 z-50">
                    <div className="px-3 py-1 text-[9px] font-bold text-amber-600 uppercase tracking-wider">Firma Değiştir</div>
                    {user.firmalar.map((f) => (
                      <button
                        key={f.id}
                        onClick={() => {
                          selectFirma(f);
                          document.getElementById('teklifFirmaDropdown')?.classList.add('hidden');
                        }}
                        className={`w-full text-left px-3 py-1.5 flex items-center gap-2 transition text-xs ${
                          selectedFirma?.id === f.id ? 'bg-amber-50 text-amber-900 font-semibold' : 'text-gray-700 hover:bg-amber-50'
                        }`}
                      >
                        <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[9px] font-bold ${selectedFirma?.id === f.id ? 'bg-amber-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                          {(f.firma || '?')[0].toUpperCase()}
                        </span>
                        <span className="truncate">{f.firma}</span>
                        {selectedFirma?.id === f.id && <svg className="w-3 h-3 text-amber-600 ml-auto" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z" clipRule="evenodd" /></svg>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="relative hidden md:block" ref={userDropdownRef}>
                {user && (
                  <button
                    className="flex items-center gap-2 px-3 py-1.5 border border-[#e3e6ef] text-[#143b6b] rounded-lg font-semibold bg-white hover:bg-[#e3e6ef] transition text-sm focus:outline-none"
                    onClick={() => setUserDropdownOpen(v => !v)}
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-200 text-blue-900 font-bold text-base select-none">
                      {user.firma?.[0] || user.email?.[0] || 'K'}
                    </span>
                    <span className="flex flex-col items-start">
                      <span className="text-blue-900 font-semibold text-xs">{user.firma || user.email}</span>
                      <span className="text-blue-700 text-xs">{user.name || ''}</span>
                    </span>
                    {user.type === 'guest' ? (
                      <span className="text-yellow-600 text-xs font-bold border border-yellow-400 rounded px-2 py-0.5 ml-2">Onaysız Profil</span>
                    ) : (
                      <span className="text-green-600 text-xs font-bold border border-green-400 rounded px-2 py-0.5 ml-2">Üye</span>
                    )}
                    <svg className={`w-4 h-4 ml-1 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                  </button>
                )}
                {userDropdownOpen && user && (
                  <div className="absolute right-0 mt-2 w-52 bg-white border border-blue-100 rounded-xl shadow-lg py-2 z-50 animate-fade-in">
                    <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/hesabim'); setUserDropdownOpen(false); }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0" /></svg>
                      Hesabım
                    </button>
                    <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/sepetim'); setUserDropdownOpen(false); }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" /></svg>
                      Sepetim {cart.length > 0 && <span className="ml-auto bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{cart.length}</span>}
                    </button>
                    <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/siparislerim'); setUserDropdownOpen(false); }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" /></svg>
                      Geçmiş Siparişlerim
                    </button>
                    <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/odeme'); setUserDropdownOpen(false); }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" /></svg>
                      Ödeme Detayları
                    </button>
                    {user?.role !== 'admin' && (
                    <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/stok-yonetimi'); setUserDropdownOpen(false); }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                      Ürün Yönetimi
                    </button>
                    )}
                    {user?.role === 'admin' && (
                    <>
                    <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/siparis-yonetimi'); setUserDropdownOpen(false); }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z" /></svg>
                      Sipariş Yönetimi
                    </button>
                    <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/cariler'); setUserDropdownOpen(false); }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" /></svg>
                      Cariler
                    </button>
                    <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/fiyat-ayarlari'); setUserDropdownOpen(false); }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" /></svg>
                      Fiyat Ayarları
                    </button>
                    <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/dagitim-agi'); setUserDropdownOpen(false); }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" /></svg>
                      Dağıtım Ağı
                    </button>
                    <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/urun-gruplari'); setUserDropdownOpen(false); }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 0 3Z" /></svg>
                      Ürün Grupları & Resimler
                    </button>
                    <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/admin/mailing'); setUserDropdownOpen(false); }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" /></svg>
                      Mail Gönderimi
                    </button>
                    </>
                    )}
                    <div className="border-t border-blue-100 my-1"></div>
                    <button className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 hover:text-red-700 transition cursor-pointer flex items-center gap-2" onClick={logout}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" /></svg>
                      Çıkış Yap
                    </button>
                  </div>
                )}
              </div>
              <div className="relative">
                <button className="flex items-center gap-1 px-2 md:px-3 py-1.5 border border-[#e3e6ef] text-[#143b6b] rounded-lg font-semibold bg-white hover:bg-[#e3e6ef] transition text-sm" onClick={() => setLangDropdown(v => !v)}>
                  <HiOutlineGlobeAlt className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="hidden md:inline">{(LANGS.find(l => l.code === lang)?.label) || t('dil')}</span>
                  <span className="md:hidden text-xs font-bold">{(LANGS.find(l => l.code === lang)?.short) || lang.toUpperCase()}</span>
                  <svg className={`w-3 h-3 transition-transform ${langDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
                {langDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-[#e3e6ef] rounded-lg shadow-lg z-50 p-2">
                    <div className="grid grid-cols-3 gap-2">
                      {LANGS.map(l => (
                        <button
                          key={l.code}
                          className={`flex items-center justify-center px-2 py-2 rounded-md text-[#143b6b] font-medium border border-transparent hover:bg-[#e3e6ef] transition text-sm ${lang === l.code ? 'bg-[#e3e6ef] border-[#143b6b] font-bold' : ''}`}
                          onClick={() => { setLang(l.code); changeLanguage(l.code); setLangDropdown(false); }}
                        >
                          {l.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          {!user && (
            <button className="px-3 md:px-4 py-1.5 border border-[#143b6b] text-[#143b6b] rounded-lg font-semibold hover:bg-[#143b6b] hover:text-white transition text-xs md:text-sm" onClick={() => setShowBayiModal(true)}>{t('Üye Ol')}</button>
          )}
        </div>
      </nav>
      {/* === MOBİL HAMBURGER MENÜ (full width) === */}
      {mobileMenuOpen && user && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setMobileMenuOpen(false)}>
          <div className="bg-white w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} style={{animation: 'slideDown 0.2s ease-out'}}>
            <style>{`@keyframes slideDown { from { transform: translateY(-100%); } to { transform: translateY(0); } }`}</style>
            {/* Kullanıcı bilgisi */}
            <div className="flex items-center gap-3 p-4 border-b border-gray-100 bg-blue-50/50">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-200 text-blue-900 font-bold text-lg select-none">
                {user.firma?.[0] || user.email?.[0] || 'K'}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-blue-900 truncate">{user.firma || user.email}</div>
                <div className="text-xs text-gray-500">{user.name || user.email}</div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {/* Menü öğeleri */}
            <div className="py-2">
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/teklif-olustur'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                Sipariş Oluştur
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/hesabim'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0" /></svg>
                Hesabım
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/sepetim'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" /></svg>
                Sepetim {cart.length > 0 && <span className="ml-auto bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{cart.length}</span>}
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/siparislerim'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" /></svg>
                Siparişlerim
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/odeme'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" /></svg>
                Ödeme Detayları
              </button>
              {user?.role !== 'admin' && (
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/stok-yonetimi'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                Ürün Yönetimi
              </button>
              )}
              {user?.role === 'admin' && (<>
              <div className="border-t border-gray-100 my-1 mx-4"></div>
              <div className="px-5 py-1.5 text-[10px] text-gray-400 font-bold uppercase tracking-wider">Yönetim</div>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/siparis-yonetimi'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z" /></svg>
                Sipariş Yönetimi
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/cariler'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" /></svg>
                Cariler
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/fiyat-ayarlari'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" /></svg>
                Fiyat Ayarları
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/admin/mailing'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" /></svg>
                Mail Gönderimi
              </button>
              </>)}
              <div className="border-t border-gray-100 my-1 mx-4"></div>
              <button className="w-full text-left px-5 py-3 text-sm text-red-600 hover:bg-red-50 transition flex items-center gap-3" onClick={() => { logout(); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" /></svg>
                Çıkış Yap
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Mobilde Giriş/Üyelik Paneli - Üstte */}
      <div className="lg:hidden w-full px-4 mt-4">
        {!user && (
          <div className="bg-white rounded-2xl shadow-xl p-4 mb-4">
            <div className="flex mb-3 bg-blue-50 rounded-lg p-1">
              <button className={`flex-1 py-2 rounded-md text-sm font-semibold transition ${loginTab === 'login' ? 'bg-white text-blue-900 shadow-sm' : 'text-blue-600'}`} onClick={() => setLoginTab('login')}>Giriş Yap</button>
              <button className={`flex-1 py-2 rounded-md text-sm font-semibold transition ${loginTab === 'register' ? 'bg-white text-blue-900 shadow-sm' : 'text-blue-600'}`} onClick={() => setLoginTab('register')}>Kayıt Ol</button>
            </div>
            {loginTab === 'login' ? (
              <form className="flex flex-col gap-3" onSubmit={async e => {
                e.preventDefault();
                setLoginError('');
                setLoginLoading(true);
                try {
                  const res = await fetch('/api/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: loginEmail, password: loginPassword }) });
                  if (res.ok) {
                    const userData = await res.json();
                    trackLogin({ email: loginEmail, firma: userData.firma, role: userData.rol, source: 'product_page_mobile' });
                    if (userData.must_change_password) {
                      setPwChangeEmail(loginEmail);
                      setPwChangeCurrent(loginPassword);
                      setPwChangePendingUser(userData);
                      setShowPasswordChange(true);
                    } else {
                      localStorage.setItem('user', JSON.stringify(userData));
                      login({ ...userData, role: userData.rol });
                      setFirma(userData.firma || ''); setEmail(userData.email || ''); setTelefon(userData.telefon || ''); setYetkili(userData.yetkili || '');
                      setBilgilerIletildi(true); setShowIletildiUyarisi(false); setRightPanelView('cart');
                    }
                  } else { const data = await res.json(); setLoginError(data.error || 'Giriş başarısız.'); }
                } catch { setLoginError('Sunucuya bağlanılamadı.'); }
                setLoginLoading(false);
              }}>
                {loginError && <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs text-center">{loginError}</div>}
                <input type="email" className="px-3 py-2 border border-blue-200 rounded-lg text-blue-900 bg-white shadow-sm text-sm" placeholder="E-Posta *" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} required />
                <input type="password" className="px-3 py-2 border border-blue-200 rounded-lg text-blue-900 bg-white shadow-sm text-sm" placeholder="Şifre *" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} required />
                <button type="submit" disabled={loginLoading} className="bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition font-semibold text-sm disabled:opacity-60">{loginLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}</button>
              </form>
            ) : (
              regSuccess ? (
                <div className="text-center py-3">
                  <div className="w-12 h-12 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-2">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>
                  </div>
                  <div className="text-base font-bold text-green-800 mb-1">Başvurunuz Alındı!</div>
                  <div className="text-xs text-gray-600 mb-3">Onaylandığında giriş yapabileceksiniz.</div>
                  <button className="w-full bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition font-semibold text-sm" onClick={() => { setRegSuccess(false); setLoginTab('login'); setLoginEmail(regEmail); }}>Giriş Yap Sayfasına Dön</button>
                </div>
              ) : (
              <form className="flex flex-col gap-3" onSubmit={async e => {
                e.preventDefault();
                setRegError('');
                if (regPassword.length < 6) { setRegError('Şifre en az 6 karakter olmalıdır.'); return; }
                if (!/[A-Z]/.test(regPassword)) { setRegError('Şifre en az 1 büyük harf içermelidir.'); return; }
                if (!/[0-9]/.test(regPassword)) { setRegError('Şifre en az 1 rakam içermelidir.'); return; }
                if (regPassword !== regPasswordConfirm) { setRegError('Şifreler eşleşmiyor.'); return; }
                setRegLoading(true);
                try {
                  const res = await fetch('/api/register', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ firma: regFirma, email: regEmail, password: regPassword, telefon: regTelefon, yetkili: regYetkili, name: regYetkili }) });
                  if (res.ok) { setRegSuccess(true); }
                  else { const data = await res.json(); setRegError(data.error || 'Kayıt başarısız.'); }
                } catch { setRegError('Sunucuya bağlanılamadı.'); }
                setRegLoading(false);
              }}>
                {regError && <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs text-center">{regError}</div>}
                <input type="text" className="px-3 py-2 border border-blue-200 rounded-lg text-blue-900 bg-white shadow-sm text-sm" placeholder="Firma / Kurum Adı *" value={regFirma} onChange={e => setRegFirma(e.target.value)} required />
                <input type="text" className="px-3 py-2 border border-blue-200 rounded-lg text-blue-900 bg-white shadow-sm text-sm" placeholder="Yetkili Kişi *" value={regYetkili} onChange={e => setRegYetkili(e.target.value)} required />
                <input type="email" className="px-3 py-2 border border-blue-200 rounded-lg text-blue-900 bg-white shadow-sm text-sm" placeholder="E-Posta *" value={regEmail} onChange={e => setRegEmail(e.target.value)} required />
                <input type="tel" className="px-3 py-2 border border-blue-200 rounded-lg text-blue-900 bg-white shadow-sm text-sm" placeholder="Telefon *" value={regTelefon} onChange={e => setRegTelefon(e.target.value)} required />
                <div>
                  <input type="password" className="w-full px-3 py-2 border border-blue-200 rounded-lg text-blue-900 bg-white shadow-sm text-sm" placeholder="Şifre *" value={regPassword} onChange={e => setRegPassword(e.target.value)} required minLength={6} />
                  {regPassword && (
                    <div className="mt-1 flex gap-1 flex-wrap">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${regPassword.length >= 6 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>6+ karakter</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${/[A-Z]/.test(regPassword) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>Büyük harf</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${/[0-9]/.test(regPassword) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>Rakam</span>
                    </div>
                  )}
                </div>
                <input type="password" className={`px-3 py-2 border rounded-lg text-blue-900 bg-white shadow-sm text-sm ${regPasswordConfirm && regPassword !== regPasswordConfirm ? 'border-red-400' : 'border-blue-200'}`} placeholder="Şifre Tekrar *" value={regPasswordConfirm} onChange={e => setRegPasswordConfirm(e.target.value)} required minLength={6} />
                {regPasswordConfirm && regPassword !== regPasswordConfirm && <div className="text-red-500 text-xs -mt-2">Şifreler eşleşmiyor</div>}
                <button type="submit" disabled={regLoading || (regPassword !== regPasswordConfirm)} className="bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition font-semibold text-sm disabled:opacity-60">{regLoading ? 'Kayıt yapılıyor...' : 'Kayıt Ol'}</button>
                <div className="text-center text-xs text-gray-500 -mt-1">Kayıt sonrası hesabınız onay sürecine alınacaktır.</div>
              </form>
              )
            )}
            {/* Mobil Yardım Merkezi Linki */}
            <div className="mt-3 pt-3 border-t border-blue-100">
              <button
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg font-semibold hover:bg-blue-100 transition text-sm border border-blue-200"
                onClick={() => navigate('/yardim')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                </svg>
                Yardım Merkezi
              </button>
            </div>
          </div>
        )}
        {/* Hoş geldiniz bölümü kaldırıldı */}
      </div>

      <div className="w-full max-w-none flex flex-col lg:flex-row gap-4 mt-4 md:mt-8 px-4 md:px-8">
        {/* === SOL ANA ALAN === */}
        <div className="bg-white rounded-2xl shadow-xl p-4 md:p-6 flex-1">

          {/* === HESABIM GÖRÜNÜMÜ === */}
          {mainView === 'hesabim' && user && (() => {
            // Profil tamamlama yüzdesi hesapla
            const profileFields = [user.firma, user.email, user.yetkili || user.name, user.telefon, user.vergiNo, user.sektor, user.adres, user.sicil, user.web, user.ulke, user.sehir];
            const filledFields = profileFields.filter(f => f && String(f).trim() !== '').length;
            const profilePercent = Math.round((filledFields / profileFields.length) * 100);
            const percentColor = profilePercent >= 80 ? '#059669' : profilePercent >= 50 ? '#d97706' : '#ef4444';

            return (
            <div data-tour="hesabim-page">
              <div className="flex items-center gap-3 mb-6">
                <button onClick={() => navigate('/dash')} className="flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium text-sm transition">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
                  Geri
                </button>
                <h1 className="text-2xl font-bold text-blue-900">Hesabım</h1>
              </div>

              <div className="w-full space-y-5">

                {/* ── Profil Banneri ── */}
                <div className="bg-gradient-to-r from-[#143b6b] to-[#1e5199] rounded-2xl p-6 text-white relative overflow-hidden" data-tour="profil-banner">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
                  <div className="relative flex flex-col sm:flex-row items-center sm:items-start gap-5">
                    <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0 border border-white/30">
                      <span className="text-3xl font-bold text-white">{(user.firma || user.email || '?')[0].toUpperCase()}</span>
                    </div>
                    <div className="text-center sm:text-left flex-1">
                      <div className="text-xl font-bold">{user.firma || '-'}</div>
                      <div className="text-blue-200 text-sm mt-0.5">{user.email || '-'}</div>
                      <div className="flex items-center gap-2 mt-2 justify-center sm:justify-start flex-wrap">
                        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${user.basvuru_durumu === 'onaylandi' ? 'bg-green-400/20 text-green-200 border border-green-400/30' : 'bg-yellow-400/20 text-yellow-200 border border-yellow-400/30'}`}>
                          {user.basvuru_durumu === 'onaylandi' ? 'Onaylı Üye' : user.basvuru_durumu === 'beklemede' ? 'Onay Bekliyor' : user.basvuru_durumu || 'Aktif'}
                        </span>
                        {user.role === 'admin' && <span className="text-xs font-semibold px-3 py-1 rounded-full bg-purple-400/20 text-purple-200 border border-purple-400/30">Admin</span>}
                        {user.type === 'guest' && <span className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/30">Misafir</span>}
                      </div>
                    </div>
                    {/* Profil Tamamlama */}
                    <div className="flex flex-col items-center gap-1 flex-shrink-0">
                      <div className="relative w-16 h-16">
                        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                          <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="5" />
                          <circle cx="32" cy="32" r="28" fill="none" stroke={percentColor} strokeWidth="5" strokeLinecap="round"
                            strokeDasharray={`${(profilePercent / 100) * 175.9} 175.9`} />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white">%{profilePercent}</span>
                      </div>
                      <span className="text-[10px] text-blue-200 font-medium">Profil</span>
                    </div>
                  </div>
                </div>

                {/* ── İki Sütun Layout ── */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

                  {/* SOL: Kişisel Bilgiler */}
                  <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden" data-tour="kisisel-bilgiler">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0" /></svg>
                      <h3 className="text-sm font-semibold text-gray-800">Kişisel Bilgiler</h3>
                    </div>
                    <div className="p-5 space-y-4">
                      {[
                        { label: 'Firma Adı', value: user.firma, icon: 'M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21' },
                        { label: 'Yetkili Kişi', value: user.yetkili || user.name, icon: 'M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0' },
                        { label: 'E-posta', value: user.email, icon: 'M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75' },
                        { label: 'Telefon', value: user.telefon, icon: 'M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z' },
                        { label: 'Ülke', value: user.ulke, icon: 'M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418' },
                        { label: 'Şehir', value: user.sehir, icon: 'M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z' },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={item.icon} /></svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">{item.label}</div>
                            <div className="text-sm text-gray-800 font-medium truncate">{item.value || <span className="text-gray-300 italic">Belirtilmemiş</span>}</div>
                          </div>
                        </div>
                      ))}
                      {user.created_at && (
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" /></svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Üyelik Tarihi</div>
                            <div className="text-sm text-gray-800 font-medium">{new Date(user.created_at).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* SAĞ: Şirket Detayları */}
                  <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden" data-tour="sirket-detaylari">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 0h.008v.008h-.008V7.5Z" /></svg>
                        <h3 className="text-sm font-semibold text-gray-800">Şirket Detayları</h3>
                      </div>
                      {!profileEditing ? (
                        <button className="text-xs text-blue-600 hover:text-blue-800 font-semibold px-3 py-1 rounded-lg hover:bg-blue-50 transition"
                          onClick={() => { setProfileEditing(true); setProfileForm({ vergiNo: user.vergiNo || '', adres: user.adres || '', sektor: user.sektor || '', sicil: user.sicil || '', web: user.web || '', name: user.name || '', yetkili: user.yetkili || '', telefon: user.telefon || '', ulke: user.ulke || '', sehir: user.sehir || '' }); }}>
                          <svg className="w-3.5 h-3.5 inline mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" /></svg>
                          Düzenle
                        </button>
                      ) : (
                        <div className="flex gap-2">
                          <button className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1 rounded-lg hover:bg-gray-50 transition" onClick={() => setProfileEditing(false)}>İptal</button>
                          <button className="text-xs text-white bg-blue-600 hover:bg-blue-700 font-semibold px-3 py-1 rounded-lg transition" disabled={profileSaving} onClick={async () => {
                            setProfileSaving(true);
                            try {
                              const res = await fetch(`/api/users/${user.id}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ ...user, ...profileForm })
                              });
                              if (res.ok) {
                                const updatedUser = { ...user, ...profileForm };
                                login(updatedUser);
                                localStorage.setItem('user', JSON.stringify(updatedUser));
                                setProfileEditing(false);
                              }
                            } catch (err) { console.error(err); }
                            setProfileSaving(false);
                          }}>{profileSaving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      {!profileEditing ? (
                        <div className="space-y-4">
                          {[
                            { label: 'Vergi No', value: user.vergiNo },
                            { label: 'Sektör', value: user.sektor },
                            { label: 'Ticaret Sicil', value: user.sicil },
                            { label: 'Web Sitesi', value: user.web },
                          ].map((item, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                              <span className="text-xs text-gray-500 font-medium">{item.label}</span>
                              <span className="text-sm text-gray-800 font-medium">{item.value || <span className="text-gray-300 italic text-xs">Belirtilmemiş</span>}</span>
                            </div>
                          ))}
                          <div className="pt-1">
                            <span className="text-xs text-gray-500 font-medium">Adres</span>
                            <div className="text-sm text-gray-800 mt-1">{user.adres || <span className="text-gray-300 italic text-xs">Belirtilmemiş</span>}</div>
                          </div>
                          {(!user.vergiNo && !user.sektor && !user.adres) && (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700 flex items-center gap-2">
                              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" /></svg>
                              Şirket bilgilerinizi doldurarak profilinizi tamamlayabilirsiniz.
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="text-[11px] text-gray-500 font-medium mb-1 block">Yetkili Kişi</label>
                              <input type="text" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition" placeholder="Yetkili Kişi" value={profileForm.yetkili || ''} onChange={e => setProfileForm(f => ({ ...f, yetkili: e.target.value }))} />
                            </div>
                            <div>
                              <label className="text-[11px] text-gray-500 font-medium mb-1 block">Telefon</label>
                              <input type="text" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition" placeholder="Telefon" value={profileForm.telefon || ''} onChange={e => setProfileForm(f => ({ ...f, telefon: e.target.value }))} />
                            </div>
                            <div>
                              <label className="text-[11px] text-gray-500 font-medium mb-1 block">Vergi No</label>
                              <input type="text" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition" placeholder="Vergi No" value={profileForm.vergiNo || ''} onChange={e => setProfileForm(f => ({ ...f, vergiNo: e.target.value }))} />
                            </div>
                            <div>
                              <label className="text-[11px] text-gray-500 font-medium mb-1 block">Sektör</label>
                              <input type="text" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition" placeholder="Sektör" value={profileForm.sektor || ''} onChange={e => setProfileForm(f => ({ ...f, sektor: e.target.value }))} />
                            </div>
                            <div>
                              <label className="text-[11px] text-gray-500 font-medium mb-1 block">Ticaret Sicil No</label>
                              <input type="text" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition" placeholder="Ticaret Sicil No" value={profileForm.sicil || ''} onChange={e => setProfileForm(f => ({ ...f, sicil: e.target.value }))} />
                            </div>
                            <div>
                              <label className="text-[11px] text-gray-500 font-medium mb-1 block">Web Sitesi</label>
                              <input type="text" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition" placeholder="Web Sitesi" value={profileForm.web || ''} onChange={e => setProfileForm(f => ({ ...f, web: e.target.value }))} />
                            </div>
                            <div>
                              <label className="text-[11px] text-gray-500 font-medium mb-1 block">Ülke</label>
                              <input type="text" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition" placeholder="Ülke" value={profileForm.ulke || ''} onChange={e => setProfileForm(f => ({ ...f, ulke: e.target.value }))} />
                            </div>
                            <div>
                              <label className="text-[11px] text-gray-500 font-medium mb-1 block">Şehir</label>
                              <input type="text" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition" placeholder="Şehir" value={profileForm.sehir || ''} onChange={e => setProfileForm(f => ({ ...f, sehir: e.target.value }))} />
                            </div>
                          </div>
                          <div>
                            <label className="text-[11px] text-gray-500 font-medium mb-1 block">Adres</label>
                            <textarea className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition" rows={3} placeholder="Adres" value={profileForm.adres || ''} onChange={e => setProfileForm(f => ({ ...f, adres: e.target.value }))} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Şifre Değiştirme ── */}
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden" data-tour="sifre-degistir">
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" /></svg>
                    <h3 className="text-sm font-semibold text-gray-800">Şifre Değiştir</h3>
                  </div>
                  <div className="p-5">
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.target;
                      const currentPassword = form.currentPassword.value;
                      const newPassword = form.newPassword.value;
                      const confirmPassword = form.confirmPassword.value;
                      if (newPassword !== confirmPassword) { alert('Yeni şifreler eşleşmiyor'); return; }
                      if (newPassword.length < 4) { alert('Şifre en az 4 karakter olmalı'); return; }
                      try {
                        const res = await fetch(`/api/users/${user.id}/password`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ currentPassword, newPassword })
                        });
                        const data = await res.json();
                        if (res.ok) { alert('Şifre başarıyla değiştirildi'); form.reset(); }
                        else { alert(data.error || 'Şifre değiştirilemedi'); }
                      } catch (err) { alert('Bir hata oluştu'); }
                    }} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-[11px] text-gray-500 font-medium mb-1 block">Mevcut Şifre</label>
                        <input name="currentPassword" type="password" required className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition" placeholder="••••••" />
                      </div>
                      <div>
                        <label className="text-[11px] text-gray-500 font-medium mb-1 block">Yeni Şifre</label>
                        <input name="newPassword" type="password" required className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition" placeholder="••••••" />
                      </div>
                      <div>
                        <label className="text-[11px] text-gray-500 font-medium mb-1 block">Şifre Tekrar</label>
                        <div className="flex gap-2">
                          <input name="confirmPassword" type="password" required className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition" placeholder="••••••" />
                          <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition flex-shrink-0">Değiştir</button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                {/* ── Yetkili Firmalar & Firma Talebi ── */}
                {(() => {
                  const handleFirmaSearch = (val) => {
                    setFirmaArama(val);
                    setTalepSuccess('');
                    setTalepError('');
                    if (firmaSearchTimeout.current) clearTimeout(firmaSearchTimeout.current);
                    if (val.length < 2) { setAramaResults([]); return; }
                    setAramaLoading(true);
                    firmaSearchTimeout.current = setTimeout(() => {
                      fetch(`/api/firma-ara?q=${encodeURIComponent(val)}`)
                        .then(r => r.ok ? r.json() : [])
                        .then(data => {
                          const yetkiliIds = (user.firmalar || []).map(f => f.id);
                          setAramaResults(data.filter(f => !yetkiliIds.includes(f.id)));
                        })
                        .catch(() => setAramaResults([]))
                        .finally(() => setAramaLoading(false));
                    }, 400);
                  };

                  const handleTalepGonder = async (firmaId) => {
                    setTalepGonderiliyor(true);
                    setTalepError('');
                    setTalepSuccess('');
                    try {
                      const res = await fetch('/api/firma-talep', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ user_id: user.id, firma_id: firmaId, mesaj: talepMesaj })
                      });
                      const data = await res.json();
                      if (res.ok) {
                        setTalepSuccess('Talebiniz başarıyla gönderildi!');
                        setFirmaArama('');
                        setAramaResults([]);
                        setTalepMesaj('');
                        const r = await fetch(`/api/firma-talep/${user.id}`);
                        if (r.ok) setTalepler(await r.json());
                      } else {
                        setTalepError(data.error || 'Talep gönderilemedi');
                      }
                    } catch { setTalepError('Bir hata oluştu'); }
                    setTalepGonderiliyor(false);
                  };

                  const durumBadge = (durum) => {
                    const map = {
                      beklemede: 'bg-amber-100 text-amber-700 border-amber-200',
                      onaylandi: 'bg-green-100 text-green-700 border-green-200',
                      reddedildi: 'bg-red-100 text-red-700 border-red-200',
                    };
                    const labels = { beklemede: 'Beklemede', onaylandi: 'Onaylandı', reddedildi: 'Reddedildi' };
                    return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${map[durum] || 'bg-gray-100 text-gray-600'}`}>{labels[durum] || durum}</span>;
                  };

                  return (
                  <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 0h.008v.008h-.008V7.5Z" /></svg>
                      <h3 className="text-sm font-semibold text-gray-800">Yetkili Firmalar</h3>
                      {user.firmalar && <span className="text-xs text-gray-400 font-medium">{user.firmalar.length} firma</span>}
                      <button onClick={() => setShowTalepForm(v => !v)} className="ml-auto text-xs font-semibold text-blue-600 hover:text-blue-800 px-3 py-1 rounded-lg hover:bg-blue-50 transition flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                        Firma Talebi
                      </button>
                    </div>

                    {/* Mevcut Firmalar */}
                    {user.firmalar && user.firmalar.length > 0 && (
                      <div className="divide-y divide-gray-50">
                        {user.firmalar.map((f, i) => (
                          <div key={f.id || i} className={`px-5 py-3 flex items-center gap-3 ${selectedFirma?.id === f.id ? 'bg-blue-50/50' : 'hover:bg-gray-50'} transition`}>
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${selectedFirma?.id === f.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                              {(f.firma || '?')[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-800 truncate">{f.firma}</div>
                              {f.email && <div className="text-xs text-gray-400 truncate">{f.email}</div>}
                            </div>
                            {selectedFirma?.id === f.id && (
                              <span className="text-[10px] font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">Aktif</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Firma Talep Formu */}
                    {showTalepForm && (
                      <div className="p-5 border-t border-gray-100 bg-gray-50/50">
                        <div className="text-xs font-semibold text-gray-600 mb-3">Yeni Firma Yetkisi Talep Et</div>

                        {talepSuccess && <div className="mb-3 p-2.5 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700 font-medium">{talepSuccess}</div>}
                        {talepError && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium">{talepError}</div>}

                        <div className="relative mb-3">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg>
                          </div>
                          <input
                            type="text"
                            className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition"
                            placeholder="Firma adı, vergi no veya e-posta ile arayın..."
                            value={firmaArama}
                            onChange={e => handleFirmaSearch(e.target.value)}
                          />
                        </div>

                        {aramaLoading && <div className="text-xs text-gray-400 py-2 text-center">Aranıyor...</div>}

                        {aramaResults.length > 0 && (
                          <div className="border border-gray-200 rounded-lg overflow-hidden mb-3 max-h-48 overflow-y-auto">
                            {aramaResults.map(f => {
                              const pendingTalep = talepler.find(t => t.firma_id === f.id && t.durum === 'beklemede');
                              return (
                                <div key={f.id} className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50 border-b border-gray-50 last:border-0">
                                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-xs font-bold text-emerald-700 flex-shrink-0">
                                    {(f.firma || '?')[0].toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-800 truncate">{f.firma}</div>
                                    <div className="text-[11px] text-gray-400">{[f.vergiNo, f.sehir].filter(Boolean).join(' · ')}</div>
                                  </div>
                                  {pendingTalep ? (
                                    <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-lg">Talep Edildi</span>
                                  ) : (
                                    <button
                                      disabled={talepGonderiliyor}
                                      onClick={() => handleTalepGonder(f.id)}
                                      className="text-[11px] font-semibold text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg transition disabled:opacity-50 flex items-center gap-1"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" /></svg>
                                      Talep Et
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {firmaArama.length >= 2 && !aramaLoading && aramaResults.length === 0 && (
                          <div className="text-xs text-gray-400 py-3 text-center border border-dashed border-gray-200 rounded-lg mb-3">Sonuç bulunamadı</div>
                        )}

                        <textarea
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition resize-none"
                          rows={2}
                          placeholder="Talep mesajınız (isteğe bağlı)..."
                          value={talepMesaj}
                          onChange={e => setTalepMesaj(e.target.value)}
                        />
                      </div>
                    )}

                    {/* Bekleyen Talepler */}
                    {talepler.length > 0 && (
                      <div className="border-t border-gray-100">
                        <div className="px-5 py-3 bg-gray-50/80">
                          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Taleplerim</div>
                        </div>
                        <div className="divide-y divide-gray-50">
                          {talepler.map(t => (
                            <div key={t.id} className="px-5 py-3 flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
                                {(t.firma || '?')[0].toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-800 truncate">{t.firma}</div>
                                <div className="text-[11px] text-gray-400">{new Date(t.created_at).toLocaleDateString('tr-TR')}{t.vergiNo ? ` · ${t.vergiNo}` : ''}</div>
                              </div>
                              {durumBadge(t.durum)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Boş durum */}
                    {(!user.firmalar || user.firmalar.length === 0) && talepler.length === 0 && !showTalepForm && (
                      <div className="p-8 text-center">
                        <svg className="w-10 h-10 text-gray-200 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 0h.008v.008h-.008V7.5Z" /></svg>
                        <div className="text-sm text-gray-500 font-medium">Henüz yetkili firmanız yok</div>
                        <div className="text-xs text-gray-400 mt-1">Firma Talebi butonuna tıklayarak yetki talebinde bulunabilirsiniz.</div>
                      </div>
                    )}
                  </div>
                  );
                })()}

                {/* ── UTS Ayarları ── */}
                {user?.role !== 'admin' && (
                <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-emerald-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m9.068-9.068a4.5 4.5 0 0 1 6.364 6.364l-4.5 4.5a4.5 4.5 0 0 1-7.244-1.242" /></svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">UTS (Ürün Takip Sistemi) Bağlantısı</h3>
                      <p className="text-xs text-gray-500">TİTCK ÜTS API entegrasyonu</p>
                    </div>
                    {utsDurum?.kayitli && (
                      <span className="ml-auto text-[10px] font-bold px-2.5 py-1 rounded-full bg-green-100 text-green-700">
                        Bağlı
                      </span>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">UTS Token</label>
                      <input type="password" value={utsForm.uts_token} onChange={e => setUtsForm(f => ({ ...f, uts_token: e.target.value }))} placeholder={utsDurum?.kayitli ? '••••••••• (kayıtlı - değiştirmek için yeni token girin)' : 'UTS portalından aldığınız token'} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 outline-none" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Kurum Numarası</label>
                        <input type="text" value={utsForm.uts_kurum_no} onChange={e => setUtsForm(f => ({ ...f, uts_kurum_no: e.target.value }))} placeholder="ÜTS Kurum No" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 outline-none" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Ortam</label>
                        <div className="flex items-center gap-2 mt-1">
                          <button onClick={() => setUtsForm(f => ({ ...f, is_test: true }))} className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border transition ${utsForm.is_test ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}>Test</button>
                          <button onClick={() => setUtsForm(f => ({ ...f, is_test: false }))} className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border transition ${!utsForm.is_test ? 'bg-green-50 border-green-300 text-green-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}>Gerçek</button>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={handleUtsTokenKaydet} disabled={utsLoading} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition disabled:opacity-50">
                        {utsLoading ? 'Kaydediliyor...' : (utsDurum?.kayitli ? 'Güncelle' : 'Kaydet')}
                      </button>
                      {utsDurum?.kayitli && (
                        <button onClick={handleUtsTestBaglanti} disabled={utsLoading} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-100 transition border border-blue-200 disabled:opacity-50">
                          {utsLoading ? 'Test...' : 'Bağlantı Test Et'}
                        </button>
                      )}
                    </div>
                    {utsDurum?.kayitli && (
                      <div className="text-[10px] text-gray-400 space-y-0.5">
                        <p>Toplam UTS İşlem: <span className="font-semibold text-gray-600">{utsDurum.toplamIslem || 0}</span></p>
                        {utsDurum.sonIslem && <p>Son İşlem: {utsDurum.sonIslem.bildirim_tipi} - {new Date(utsDurum.sonIslem.created_at).toLocaleDateString('tr-TR')}</p>}
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* ── Hızlı İşlemler ── */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <button onClick={() => navigate('/dash/siparislerim')} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col items-center gap-2 hover:shadow-md hover:border-blue-200 transition group">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" /></svg>
                    </div>
                    <span className="text-xs font-semibold text-gray-700">Siparişlerim</span>
                  </button>
                  <button onClick={() => navigate('/dash/odeme')} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col items-center gap-2 hover:shadow-md hover:border-amber-200 transition group">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" /></svg>
                    </div>
                    <span className="text-xs font-semibold text-gray-700">Ödemeler</span>
                  </button>
                  <button onClick={() => navigate('/dash/sepetim')} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col items-center gap-2 hover:shadow-md hover:border-emerald-200 transition group">
                    <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" /></svg>
                    </div>
                    <span className="text-xs font-semibold text-gray-700">Ürün Kataloğu</span>
                  </button>
                  {user?.role !== 'admin' && (
                  <button onClick={() => navigate('/dash/stok-yonetimi')} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col items-center gap-2 hover:shadow-md hover:border-violet-200 transition group">
                    <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                    </div>
                    <span className="text-xs font-semibold text-gray-700">Ürün Yönetimi</span>
                  </button>
                  )}
                  <button onClick={() => navigate('/dash')} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col items-center gap-2 hover:shadow-md hover:border-violet-200 transition group">
                    <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>
                    </div>
                    <span className="text-xs font-semibold text-gray-700">Ana Sayfa</span>
                  </button>
                </div>

                {/* ── Çıkış Yap ── */}
                <button onClick={() => { logout(); navigate('/login'); }} className="w-full px-4 py-3 rounded-xl bg-red-50 text-red-600 border border-red-200 font-semibold text-sm hover:bg-red-100 transition flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" /></svg>
                  Çıkış Yap
                </button>

              </div>
            </div>
            );
          })()}

          {/* === GEÇMİŞ SİPARİŞLERİM GÖRÜNÜMÜ === */}
          {mainView === 'siparislerim' && user && (() => {
            const allOrders = userOrders || [];
            const totalItems = allOrders.reduce((s, o) => {
              const items = (() => { try { return typeof o.items === 'string' ? JSON.parse(o.items) : o.items; } catch { return []; } })();
              return s + items.reduce((a, it) => a + (it.qty || 1), 0);
            }, 0);
            const totalAmount = allOrders.reduce((s, o) => {
              const items = (() => { try { return typeof o.items === 'string' ? JSON.parse(o.items) : o.items; } catch { return []; } })();
              return s + items.reduce((a, it) => a + ((it.price || 0) * (it.qty || 1)), 0);
            }, 0);
            const pendingCount = allOrders.filter(o => o.status === 'pending').length;
            const approvedCount = allOrders.filter(o => o.status === 'approved').length;
            const shippingCount = allOrders.filter(o => o.status === 'shipping').length;
            const deliveredCount = allOrders.filter(o => o.status === 'delivered').length;

            return (
            <div data-tour="siparislerim-page">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <button onClick={() => setMainView('products')} className="flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium text-sm transition">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
                    Geri
                  </button>
                  <h1 className="text-2xl font-bold text-blue-900">Geçmiş Siparişlerim</h1>
                  {selectedFirma && (
                    <span className="ml-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-lg">
                      {selectedFirma.firma}
                    </span>
                  )}
                </div>
                {allOrders.length > 0 && (
                  <button onClick={() => fetchUserOrders()} className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" /></svg>
                    Yenile
                  </button>
                )}
              </div>

              {userOrdersLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-700 mb-4"></div>
                  <div className="text-blue-600 text-sm">Siparişler yükleniyor...</div>
                </div>
              ) : allOrders.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 mx-auto bg-blue-50 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-10 h-10 text-blue-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" /></svg>
                  </div>
                  <div className="text-blue-800 text-lg font-semibold mb-1">Henüz siparişiniz bulunmuyor</div>
                  <div className="text-blue-400 text-sm mb-5">İlk siparişinizi oluşturmak için ürün listesine gidin.</div>
                  <button onClick={() => setMainView('products')} className="px-6 py-2.5 bg-blue-700 text-white rounded-xl font-semibold text-sm hover:bg-blue-800 transition shadow">Ürünlere Dön</button>
                </div>
              ) : (
                <>
                  {/* Özet Kartları */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-tour="siparis-ozet-kartlari">
                    <div className="bg-white border border-blue-100 rounded-xl p-3 text-center shadow-sm">
                      <div className="text-2xl font-bold text-blue-900">{allOrders.length}</div>
                      <div className="text-[10px] text-blue-500 mt-1 font-medium">Toplam Sipariş</div>
                    </div>
                    <div className="bg-white border border-yellow-100 rounded-xl p-3 text-center shadow-sm">
                      <div className="text-2xl font-bold text-yellow-600">{pendingCount}</div>
                      <div className="text-[10px] text-yellow-500 mt-1 font-medium">Onay Bekliyor</div>
                    </div>
                    <div className="bg-white border border-indigo-100 rounded-xl p-3 text-center shadow-sm">
                      <div className="text-2xl font-bold text-indigo-600">{approvedCount}</div>
                      <div className="text-[10px] text-indigo-500 mt-1 font-medium">Hazırlanıyor</div>
                    </div>
                    <div className="bg-white border border-orange-100 rounded-xl p-3 text-center shadow-sm">
                      <div className="text-2xl font-bold text-orange-600">{shippingCount}</div>
                      <div className="text-[10px] text-orange-500 mt-1 font-medium">Teslimat</div>
                    </div>
                    <div className="bg-white border border-green-100 rounded-xl p-3 text-center shadow-sm">
                      <div className="text-2xl font-bold text-green-600">{deliveredCount}</div>
                      <div className="text-[10px] text-green-500 mt-1 font-medium">Teslim Edildi</div>
                    </div>
                  </div>

                  {/* Sipariş Listesi */}
                  <div className="flex flex-col gap-4" data-tour="siparis-listesi">
                    {allOrders.map((order, idx) => {
                      const items = (() => { try { return typeof order.items === 'string' ? JSON.parse(order.items) : order.items; } catch { return []; } })();
                      const statusMap = {
                        pending: { label: 'Onay Bekleniyor', cls: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: '⏳', step: 1 },
                        approved: { label: 'Onaylandı, Hazırlanıyor', cls: 'bg-indigo-100 text-indigo-800 border-indigo-300', icon: '✓', step: 2 },
                        shipping: { label: 'Teslimat Sürecinde', cls: 'bg-orange-100 text-orange-800 border-orange-300', icon: '🚚', step: 3 },
                        delivered: { label: 'Teslim Edildi', cls: 'bg-green-100 text-green-800 border-green-300', icon: '✓', step: 4 },
                        cancelled: { label: 'İptal Edildi', cls: 'bg-red-100 text-red-800 border-red-300', icon: '✕', step: 0 }
                      };
                      const st = statusMap[order.status] || statusMap.pending;
                      const orderSteps = [
                        { key: 'pending', label: 'Onay Bekleniyor', shortLabel: 'Onay' },
                        { key: 'approved', label: 'Hazırlanıyor', shortLabel: 'Hazırlık' },
                        { key: 'shipping', label: 'Teslimat Sürecinde', shortLabel: 'Teslimat' },
                        { key: 'delivered', label: 'Teslim Edildi', shortLabel: 'Teslim' }
                      ];
                      const currentStep = st.step;
                      const isExpanded = expandedOrder === (order.id || idx);
                      const subtotalRaw = items.reduce((s, it) => s + ((it.price || 0) * (it.qty || 1)), 0);
                      // Ürün bazlı indirim
                      const itemDiscountsRaw = items.reduce((s, it) => {
                        const lt = (parseFloat(it.price) || 0) * (parseInt(it.qty) || 1);
                        if (it.discount_type === 'percent') return s + lt * (parseFloat(it.discount_value) || 0) / 100;
                        if (it.discount_type === 'amount') return s + (parseFloat(it.discount_value) || 0);
                        return s;
                      }, 0);
                      // Sipariş geneli indirim
                      let orderDiscountRaw = 0;
                      if (order.discount_type === 'percent') orderDiscountRaw = (subtotalRaw - itemDiscountsRaw) * (parseFloat(order.discount_value) || 0) / 100;
                      else if (order.discount_type === 'amount') orderDiscountRaw = parseFloat(order.discount_value) || 0;
                      const totalDiscountRaw = itemDiscountsRaw + orderDiscountRaw;
                      const orderTotal = subtotalRaw - totalDiscountRaw;
                      const orderQty = items.reduce((s, it) => s + (it.qty || 1), 0);
                      const orderDate = order.created_at ? new Date(order.created_at) : null;
                      
                      // Ürün kalemi bazlı durum istatistikleri
                      const itemStatuses = items.map(it => it.status || order.status || 'pending');
                      const activeItems = itemStatuses.filter(s => s !== 'cancelled');
                      const deliveredItems = itemStatuses.filter(s => s === 'delivered').length;
                      const shippingItems = itemStatuses.filter(s => s === 'shipping').length;
                      const approvedItems = itemStatuses.filter(s => s === 'approved').length;
                      const cancelledItems = itemStatuses.filter(s => s === 'cancelled').length;
                      const isPartial = activeItems.length > 0 && !activeItems.every(s => s === activeItems[0]);
                      const progressPercent = activeItems.length > 0 ? Math.round(((deliveredItems * 3 + shippingItems * 2 + approvedItems * 1) / (activeItems.length * 3)) * 100) : 0;

                      return (
                        <div key={order.id || idx} className={`bg-white rounded-xl border overflow-hidden transition-shadow ${isExpanded ? 'shadow-lg border-blue-300' : 'shadow-sm border-blue-100 hover:shadow-md'}`}>
                          {/* Sipariş Başlığı */}
                          <button className="w-full text-left p-4 sm:p-5 transition" onClick={() => setExpandedOrder(isExpanded ? null : (order.id || idx))}>
                            {/* Üst Satır: Sipariş No, Durum, Fiyat, Ok */}
                            <div className="flex items-start sm:items-center justify-between gap-3">
                              <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${st.cls}`}>
                                  {st.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-base font-bold text-blue-900">Sipariş #{order.id}</span>
                                    {order.siparis_tipi === 'devir' && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-indigo-100 text-indigo-700 border-indigo-200">Devir</span>}
                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-blue-500">
                                    {order.siparis_tipi === 'devir' ? (
                                      <span className="flex items-center gap-1 text-indigo-600">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 0 0-3.7-3.7 48.678 48.678 0 0 0-7.324 0 4.006 4.006 0 0 0-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 0 0 3.7 3.7 48.656 48.656 0 0 0 7.324 0 4.006 4.006 0 0 0 3.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3-3 3" /></svg>
                                        Devir (Geçmiş Dönem)
                                      </span>
                                    ) : (
                                      <>
                                    {orderDate && (
                                      <span className="flex items-center gap-1">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" /></svg>
                                        {orderDate.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}
                                      </span>
                                    )}
                                    {orderDate && (
                                      <span className="text-blue-400 text-xs">{orderDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                                    )}
                                      </>
                                    )}
                                    <span className="flex items-center gap-1">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0-3-3m3 3 3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" /></svg>
                                      {items.length} kalem, {orderQty} adet
                                    </span>
                                    {/* Kısmi teslimat göstergesi */}
                                    {deliveredItems > 0 && deliveredItems < activeItems.length && (
                                      <span className="flex items-center gap-1 text-green-600 font-medium">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>
                                        {deliveredItems}/{activeItems.length} teslim
                                      </span>
                                    )}
                                    {shippingItems > 0 && shippingItems < activeItems.length && (
                                      <span className="flex items-center gap-1 text-orange-600 font-medium">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0H21M3.375 14.25h3.75m0 0V11.25m0 3H21M7.125 11.25h2.25m0 0V7.5m0 3.75h7.5" /></svg>
                                        {shippingItems} yolda
                                      </span>
                                    )}
                                    {cancelledItems > 0 && (
                                      <span className="flex items-center gap-1 text-red-500 font-medium">
                                        {cancelledItems} iptal
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                {orderTotal > 0 && (
                                  <div className="text-right hidden sm:block">
                                    <div className="text-base font-bold text-blue-900">{formatPrice(orderTotal)}</div>
                                  </div>
                                )}
                                <svg className={`w-5 h-5 text-blue-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>
                              </div>
                            </div>
                            {/* Mobilde fiyat */}
                            {orderTotal > 0 && (
                              <div className="sm:hidden mt-2 text-right text-sm font-bold text-blue-900">{formatPrice(orderTotal)}</div>
                            )}
                            {/* 4 Aşamalı Progress Bar */}
                            {order.status !== 'cancelled' && (
                              <div className="mt-4 pt-3 border-t border-gray-100">
                                <div className="flex items-center justify-between relative">
                                  {/* Progress çizgisi (arka plan) */}
                                  <div className="absolute top-4 left-[calc(12.5%)] right-[calc(12.5%)] h-1 bg-gray-200 rounded-full z-0"></div>
                                  {/* Progress çizgisi (aktif) */}
                                  <div className="absolute top-4 left-[calc(12.5%)] h-1 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full z-0 transition-all duration-500" style={{ width: currentStep >= 4 ? '75%' : currentStep >= 3 ? '50%' : currentStep >= 2 ? '25%' : '0%' }}></div>
                                  {orderSteps.map((step, stepIdx) => {
                                    const stepNum = stepIdx + 1;
                                    const isCompleted = currentStep > stepNum;
                                    const isCurrent = currentStep === stepNum;
                                    const isUpcoming = currentStep < stepNum;
                                    return (
                                      <div key={step.key} className="flex flex-col items-center z-10 flex-1">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-300 ${
                                          isCompleted ? 'bg-blue-600 border-blue-600 text-white' :
                                          isCurrent ? 'bg-white border-blue-600 text-blue-600 ring-4 ring-blue-100' :
                                          'bg-white border-gray-300 text-gray-400'
                                        }`}>
                                          {isCompleted ? (
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                                          ) : (
                                            stepNum
                                          )}
                                        </div>
                                        <span className={`text-[10px] mt-1.5 font-medium text-center leading-tight ${
                                          isCompleted ? 'text-blue-600' :
                                          isCurrent ? 'text-blue-700 font-bold' :
                                          'text-gray-400'
                                        }`}>
                                          <span className="hidden sm:inline">{step.label}</span>
                                          <span className="sm:hidden">{step.shortLabel}</span>
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {/* Kısmi teslimat ilerleme çubuğu */}
                            {isPartial && order.status !== 'cancelled' && (
                              <div className="mt-3 pt-2 border-t border-gray-100">
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Kalem İlerleme Durumu</span>
                                  <span className="text-[10px] font-bold text-blue-700">{deliveredItems}/{activeItems.length} teslim edildi</span>
                                </div>
                                <div className="flex gap-1">
                                  {items.map((it, ii) => {
                                    const s = it.status || order.status || 'pending';
                                    const barColor = s === 'delivered' ? 'bg-green-500' : s === 'shipping' ? 'bg-orange-400' : s === 'approved' ? 'bg-indigo-400' : s === 'cancelled' ? 'bg-red-300' : 'bg-gray-300';
                                    const sLabel = { pending: 'Onay Bekleniyor', approved: 'Hazırlanıyor', shipping: 'Teslimat Sürecinde', delivered: 'Teslim Edildi', cancelled: 'İptal' };
                                    return <div key={ii} className={`h-2 flex-1 rounded-full ${barColor} transition-all`} title={`${it.name}: ${sLabel[s] || s}`}></div>;
                                  })}
                                </div>
                                <div className="flex items-center gap-3 mt-1.5 text-[9px] text-gray-400">
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300"></span>Bekliyor</span>
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-400"></span>Hazırlanıyor</span>
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400"></span>Yolda</span>
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500"></span>Teslim</span>
                                </div>
                              </div>
                            )}
                            {/* İptal durumu */}
                            {order.status === 'cancelled' && (
                              <div className="mt-3 pt-2 border-t border-red-100">
                                <div className="flex items-center gap-2 text-red-600 text-sm font-medium">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                                  Bu sipariş iptal edilmiştir
                                </div>
                              </div>
                            )}
                          </button>

                          {/* Sipariş Detayı (Accordion) */}
                          {isExpanded && (
                            <div className="border-t border-blue-100">
                              {/* Ürün Listesi - Kompakt Tablo */}
                              <div className="px-4 sm:px-5 py-3">
                                <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">Sipariş Kalemleri ({items.length} kalem)</div>
                                {/* Tablo Başlığı */}
                                <div className="hidden sm:grid grid-cols-[32px_1fr_140px_60px_80px_90px] gap-2 px-2 pb-1.5 border-b border-gray-200 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                                  <span>#</span>
                                  <span>Ürün</span>
                                  <span>Ölçü</span>
                                  <span className="text-center">Adet</span>
                                  <span className="text-right">Fiyat</span>
                                  <span className="text-center">Durum</span>
                                </div>
                                <div className="flex flex-col divide-y divide-gray-100">
                                  {items.map((it, i) => {
                                    const itemStatus = it.status || order.status || 'pending';
                                    const itemStatusMap = {
                                      pending: { label: 'Bekliyor', cls: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
                                      approved: { label: 'Hazırlanıyor', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
                                      shipping: { label: 'Teslimat', cls: 'bg-orange-100 text-orange-700 border-orange-200' },
                                      delivered: { label: 'Teslim', cls: 'bg-green-100 text-green-700 border-green-200' },
                                      cancelled: { label: 'İptal', cls: 'bg-red-100 text-red-700 border-red-200' }
                                    };
                                    const ist = itemStatusMap[itemStatus] || itemStatusMap.pending;
                                    const measureText = formatMeasureDisplay(it.measure);
                                    return (
                                    <div key={i} className={`group ${itemStatus === 'cancelled' ? 'opacity-50' : ''}`}>
                                      {/* Ana Satır */}
                                      <div className={`grid grid-cols-[32px_1fr_140px_60px_80px_90px] gap-2 items-center px-2 py-2 hover:bg-blue-50/40 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                                        <span className="text-[11px] font-bold text-gray-400">{i + 1}</span>
                                        <div className="min-w-0">
                                          <div className="text-[12px] font-semibold text-gray-800 truncate" title={it.name}>{it.name || 'Ürün'}</div>
                                          <div className="flex items-center gap-2 text-[10px] text-gray-400 truncate">
                                            {it.marka && <span>{it.marka}</span>}
                                            {it.barcode && <span className="font-mono">{it.barcode}</span>}
                                            {it.sutCode && it.sutCode !== '-' && <span className="text-emerald-600 font-semibold">{it.sutCode}</span>}
                                          </div>
                                        </div>
                                        <div className="text-[11px] text-blue-700 font-medium truncate" title={measureText}>{measureText || '-'}</div>
                                        <div className="text-[12px] font-bold text-blue-900 text-center">{it.qty || 1}</div>
                                        <div className="text-[11px] text-right text-gray-700">{it.price > 0 ? formatPrice(it.price) : '-'}</div>
                                        <div className="flex justify-center">
                                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${ist.cls}`}>{ist.label}</span>
                                        </div>
                                      </div>
                                      {/* Kalem durum güncelleme bayi tarafında kaldırıldı - sadece admin panelinden */}
                                    </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Sevkiyat & İade Özeti (Firma/Kullanıcı Görünümü) */}
                              {(() => {
                                const hasShipped = items.some(it => parseInt(it.shipped_qty) > 0);
                                const hasReturned = items.some(it => parseInt(it.returned_qty) > 0);
                                if (!hasShipped && !hasReturned) return null;
                                // Sevkiyat detaylarını yükle (henüz yüklenmemişse)
                                if (!sevkiyatlar[order.id]) { fetchSevkiyatlar(order.id); }
                                const orderSevkList = sevkiyatlar[order.id] || [];
                                const sevkStatusLabels = { hazirlaniyor: 'Hazırlanıyor', sevk_edildi: 'Sevk Edildi', teslim_edildi: 'Teslim Edildi', iptal: 'İptal', durduruldu: 'Durduruldu' };
                                const sevkStatusCls = { hazirlaniyor: 'bg-amber-100 text-amber-700 border-amber-200', sevk_edildi: 'bg-violet-100 text-violet-700 border-violet-200', teslim_edildi: 'bg-emerald-100 text-emerald-700 border-emerald-200', iptal: 'bg-rose-100 text-rose-700 border-rose-200', durduruldu: 'bg-gray-200 text-gray-600 border-gray-300' };
                                return (
                                <div className="px-4 sm:px-5 py-3 border-t border-violet-100 bg-violet-50/30">
                                  <div className="text-xs font-semibold text-violet-600 uppercase tracking-wide mb-2">Sevkiyat & İade Durumu</div>
                                  {/* Kalem Özet Tablosu */}
                                  <div className="space-y-1 mb-3">
                                    {items.map((it, i) => {
                                      const shipped = parseInt(it.shipped_qty) || 0;
                                      const returned = parseInt(it.returned_qty) || 0;
                                      if (shipped === 0 && returned === 0) return null;
                                      const total = parseInt(it.qty) || 1;
                                      const remaining = total - shipped + returned;
                                      return (
                                        <div key={i} className="flex items-center justify-between px-2 py-1 bg-white rounded border border-violet-100 text-[11px]">
                                          <span className="text-gray-700 truncate flex-1">{it.name}</span>
                                          <div className="flex items-center gap-3 flex-shrink-0">
                                            <span className="text-violet-700 font-semibold">Sevk: {shipped}/{total}</span>
                                            {returned > 0 && <span className="text-orange-600 font-semibold">İade: {returned}</span>}
                                            <span className="text-gray-500">Kalan: {remaining}</span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {/* Sevkiyat Geçmişi (Lot bilgisi dahil) */}
                                  {orderSevkList.length > 0 && (
                                    <div className="mb-3">
                                      <div className="text-[10px] font-semibold text-violet-500 uppercase tracking-wide mb-1.5">Sevkiyat Geçmişi</div>
                                      <div className="space-y-1.5">
                                        {orderSevkList.filter(s => s.status !== 'iptal').map(s => (
                                          <div key={s.id} className="bg-white rounded-lg border border-violet-100 overflow-hidden">
                                            <div className="flex items-center flex-wrap gap-2 px-2 py-1.5 bg-gray-50/50">
                                              <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded border ${sevkStatusCls[s.status] || 'bg-gray-100'}`}>{sevkStatusLabels[s.status] || s.status}</span>
                                              {s.kargo_firmasi && <span className="text-[8px] font-semibold px-1.5 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded">{s.kargo_firmasi}</span>}
                                              {s.tracking_no && (
                                                <span className="text-[9px] text-blue-600 font-mono flex items-center gap-1">
                                                  <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                                  Takip No: {s.tracking_no}
                                                </span>
                                              )}
                                              <span className="text-[9px] text-gray-400">{new Date(s.created_at).toLocaleDateString('tr-TR')} {new Date(s.created_at).toLocaleTimeString('tr-TR', {hour:'2-digit',minute:'2-digit'})}</span>
                                            </div>
                                            {/* Kargo Takip Bilgisi - Müşteri için belirgin */}
                                            {(s.kargo_firmasi || s.tracking_no) && (
                                              <div className="px-2 py-1.5 bg-blue-50/70 border-b border-blue-100 flex items-center gap-2">
                                                <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" /></svg>
                                                <div className="flex items-center gap-2 flex-wrap text-[10px]">
                                                  {s.kargo_firmasi && <span className="font-semibold text-blue-800">{s.kargo_firmasi}</span>}
                                                  {s.tracking_no && <span className="font-mono text-blue-700 bg-white px-1.5 py-0.5 rounded border border-blue-200 select-all">{s.tracking_no}</span>}
                                                </div>
                                              </div>
                                            )}
                                            <div className="px-2 py-1">
                                              {(s.kalemler || []).map((k, ki) => {
                                                const origItem = items[k.item_index];
                                                const seriNolar = k.seri_nolar ? (typeof k.seri_nolar === 'string' ? JSON.parse(k.seri_nolar) : k.seri_nolar) : [];
                                                return (
                                                  <div key={ki} className="py-0.5 text-[10px] border-b border-gray-50 last:border-0">
                                                    <div className="flex items-center justify-between">
                                                      <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
                                                        <span className="text-gray-700 truncate">{origItem ? origItem.name : `Kalem #${k.item_index + 1}`}</span>
                                                        {k.lot_no && <span className="text-[9px] font-mono px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded flex-shrink-0">LOT: {k.lot_no}</span>}
                                                        {k.uretim_tarihi && <span className="text-[9px] font-mono px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded flex-shrink-0">ÜRT: {new Date(k.uretim_tarihi).toLocaleDateString('tr-TR')}</span>}
                                                        {k.skt && <span className="text-[9px] font-mono px-1.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded flex-shrink-0">SKT: {new Date(k.skt).toLocaleDateString('tr-TR')}</span>}
                                                      </div>
                                                      <span className="font-bold text-violet-700 flex-shrink-0 ml-2">{k.qty} adet</span>
                                                    </div>
                                                    {seriNolar.length > 0 && (
                                                      <details className="mt-0.5">
                                                        <summary className="text-[9px] text-violet-600 cursor-pointer hover:text-violet-800 font-semibold">{seriNolar.length} Seri No</summary>
                                                        <div className="mt-0.5 flex flex-wrap gap-1 pl-2">
                                                          {seriNolar.map((sn, snI) => (
                                                            <span key={snI} className="text-[8px] font-mono px-1.5 py-0.5 bg-violet-50 text-violet-700 border border-violet-200 rounded">{sn}</span>
                                                          ))}
                                                        </div>
                                                      </details>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                            {/* Teslim Aldım Butonu - Sadece sevk_edildi durumundaki sevkiyatlar için */}
                                            {s.status === 'sevk_edildi' && user?.role !== 'admin' && (
                                              <div className="px-2 py-2 bg-emerald-50/50 border-t border-emerald-100 flex items-center justify-between">
                                                <span className="text-[10px] text-emerald-700 font-medium flex items-center gap-1">
                                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                                                  Bu sevkiyat size ulaştı mı?
                                                </span>
                                                <button onClick={(e) => { e.stopPropagation(); handleTeslimAl(s.id, order.id); }} className="px-3 py-1.5 bg-emerald-600 text-white text-[10px] font-semibold rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-1.5 shadow-sm">
                                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                                  Teslim Aldım
                                                </button>
                                              </div>
                                            )}
                                            {/* Teslim edildi bilgisi */}
                                            {s.status === 'teslim_edildi' && (
                                              <div className="px-2 py-1.5 bg-emerald-50/30 border-t border-emerald-100 flex items-center gap-1.5">
                                                <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                                <span className="text-[10px] text-emerald-700 font-medium">Teslim alındı - Stoğunuza eklendi</span>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {/* Firma İade Talebi Butonu */}
                                  {user?.role !== 'admin' && items.some(it => (parseInt(it.shipped_qty) || 0) - (parseInt(it.returned_qty) || 0) > 0) && (
                                    <div className="mt-2">
                                      {!iadeForm ? (
                                        <button onClick={() => {
                                          const returnableIdx = items.findIndex(it => (parseInt(it.shipped_qty) || 0) - (parseInt(it.returned_qty) || 0) > 0);
                                          if (returnableIdx >= 0) setIadeForm({ orderId: order.id, item_index: returnableIdx, qty: 1, reason: '' });
                                        }} className="px-3 py-1.5 bg-orange-500 text-white text-[11px] font-semibold rounded-lg hover:bg-orange-600 transition-colors">
                                          İade Talebi Oluştur
                                        </button>
                                      ) : iadeForm.orderId === order.id && (
                                        <div className="p-2 bg-orange-50 border border-orange-200 rounded-lg mt-1">
                                          <div className="flex items-center justify-between mb-1.5">
                                            <h4 className="text-[11px] font-bold text-orange-800">İade Talebi</h4>
                                            <button onClick={() => setIadeForm(null)} className="text-gray-400 hover:text-red-500 text-[10px]">Kapat</button>
                                          </div>
                                          <div className="grid grid-cols-[1fr_60px] gap-1.5 mb-1.5">
                                            <select value={iadeForm.item_index} onChange={(e) => setIadeForm(prev => ({ ...prev, item_index: parseInt(e.target.value), qty: 1 }))} className="text-[10px] border border-orange-200 rounded px-1.5 py-1 bg-white">
                                              {items.map((it, i) => {
                                                const maxR = (parseInt(it.shipped_qty) || 0) - (parseInt(it.returned_qty) || 0);
                                                return maxR > 0 ? <option key={i} value={i}>{it.name} (maks: {maxR})</option> : null;
                                              })}
                                            </select>
                                            <input type="number" min="1" max={(parseInt(items[iadeForm.item_index]?.shipped_qty) || 0) - (parseInt(items[iadeForm.item_index]?.returned_qty) || 0)} value={iadeForm.qty} onChange={(e) => {
                                              const mx = (parseInt(items[iadeForm.item_index]?.shipped_qty) || 0) - (parseInt(items[iadeForm.item_index]?.returned_qty) || 0);
                                              setIadeForm(prev => ({ ...prev, qty: Math.min(Math.max(1, parseInt(e.target.value) || 1), mx) }));
                                            }} className="text-center text-[10px] border border-orange-200 rounded px-1 py-1" />
                                          </div>
                                          <input type="text" placeholder="İade sebebi..." value={iadeForm.reason} onChange={(e) => setIadeForm(prev => ({ ...prev, reason: e.target.value }))} className="w-full text-[10px] border border-orange-200 rounded px-1.5 py-1 mb-1.5" />
                                          <button onClick={async () => {
                                            setIadeLoading(true);
                                            try {
                                              const resp = await authFetch('/api/iade', {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ order_id: order.id, item_index: iadeForm.item_index, qty: iadeForm.qty, reason: iadeForm.reason, requested_by: 'firma' })
                                              });
                                              const data = await resp.json();
                                              if (resp.ok) { setIadeForm(null); alert('İade talebiniz gönderildi'); fetchUserOrders(); } else alert(data.error || 'Hata');
                                            } catch (e) { alert('Hata: ' + e.message); }
                                            setIadeLoading(false);
                                          }} disabled={iadeLoading} className="px-3 py-1 bg-orange-500 text-white text-[10px] font-semibold rounded hover:bg-orange-600 disabled:opacity-50 transition-colors">
                                            {iadeLoading ? 'Gönderiliyor...' : 'İade Talebi Gönder'}
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>);
                              })()}

                              {/* Sipariş Özeti */}
                              <div className="px-4 sm:px-5 py-4 bg-gradient-to-r from-blue-50 to-gray-50 border-t border-blue-200">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                                    <span className="flex items-center gap-1.5">
                                      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0-3-3m3 3 3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" /></svg>
                                      <span className="font-medium">{items.length} kalem</span>
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L12 12.75 6.429 9.75m11.142 0 4.179 2.25-9.75 5.25-9.75-5.25 4.179-2.25" /></svg>
                                      <span className="font-medium">{orderQty} adet toplam</span>
                                    </span>
                                    {order.note && (
                                      <span className="flex items-center gap-1.5 text-gray-500">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 0 1 1.037-.443 48.282 48.282 0 0 0 5.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" /></svg>
                                        <span className="italic">Not: {order.note}</span>
                                      </span>
                                    )}
                                  </div>
                                  {subtotalRaw > 0 && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                      {totalDiscountRaw > 0 && (
                                        <>
                                          <span className="text-sm text-gray-400 line-through">{formatPrice(subtotalRaw)}</span>
                                          <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded border border-orange-200">-{formatPrice(totalDiscountRaw)} indirim</span>
                                        </>
                                      )}
                                      <span className="text-sm text-gray-500">Toplam:</span>
                                      <span className="text-lg font-bold text-blue-900 bg-white px-3 py-1 rounded-lg border border-blue-200 shadow-sm">{formatPrice(orderTotal)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Sipariş Tutarı (Minimal) */}
                              <div className="px-4 sm:px-5 py-3 border-t border-blue-100">
                                <div className="flex flex-wrap items-center gap-3 text-xs">
                                  {totalDiscountRaw > 0 && <span className="text-gray-400 line-through text-[10px]">{formatPrice(subtotalRaw)}</span>}
                                  {totalDiscountRaw > 0 && <span className="text-orange-600 text-[10px] font-semibold">-{formatPrice(totalDiscountRaw)}</span>}
                                  {orderTotal > 0 && (
                                    <span className="text-gray-600">Toplam: <strong className="text-blue-800 text-sm">{formatPrice(orderTotal)}</strong></span>
                                  )}
                                </div>
                              </div>

                              {/* Sipariş durumu güncelleme bayi tarafında kaldırıldı - sadece admin panelinden yapılabilir */}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            );
          })()}

          {/* === ÖDEME DETAYLARI GÖRÜNÜMÜ === */}
          {mainView === 'odeme' && user && (
            <div data-tour="odeme-page">
              <div className="flex items-center gap-3 mb-6">
                <button onClick={() => setMainView('products')} className="flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium text-sm transition">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
                  Geri
                </button>
                <h1 className="text-2xl font-bold text-blue-900">Ödeme Detayları</h1>
              </div>
              {userOrdersLoading ? (
                <div className="text-center py-16 text-blue-600 text-base">Yükleniyor...</div>
              ) : (
                <>
                  {/* Özet Kartları */}
                  {userSummary && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-tour="odeme-ozet-kartlari">
                      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-blue-900">{userSummary.totalOrders}</div>
                        <div className="text-xs text-blue-600 mt-1">Toplam Sipariş</div>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-yellow-700">{userSummary.pending}</div>
                        <div className="text-xs text-yellow-600 mt-1">Beklemede</div>
                      </div>
                      <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-green-700">{userSummary.completed}</div>
                        <div className="text-xs text-green-600 mt-1">Tamamlanan</div>
                      </div>
                      <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-red-700">{userSummary.cancelled}</div>
                        <div className="text-xs text-red-600 mt-1">İptal Edilen</div>
                      </div>
                    </div>
                  )}

                  {/* Sipariş Bazlı Ödeme Listesi */}
                  {userOrders.length === 0 ? (
                    <div className="text-center py-8 text-blue-500 text-sm">Ödeme geçmişi bulunamadı.</div>
                  ) : (
                    <div className="bg-white rounded-xl border border-blue-100 overflow-hidden" data-tour="odeme-listesi">
                      <div className="divide-y divide-blue-50">
                        {userOrders.map((order, idx) => {
                          const items = (() => { try { return typeof order.items === 'string' ? JSON.parse(order.items) : order.items; } catch { return []; } })();
                          const statusColors = { pending: 'text-yellow-700 bg-yellow-50 border-yellow-200', approved: 'text-indigo-700 bg-indigo-50 border-indigo-200', shipping: 'text-orange-700 bg-orange-50 border-orange-200', delivered: 'text-green-700 bg-green-50 border-green-200', cancelled: 'text-red-700 bg-red-50 border-red-200' };
                          const statusLabels = { pending: 'Onay Bekleniyor', approved: 'Hazırlanıyor', shipping: 'Teslimat Sürecinde', delivered: 'Teslim Edildi', cancelled: 'İptal' };
                          return (
                            <div key={order.id || idx} className="flex items-center justify-between p-4 hover:bg-blue-50/30 transition">
                              <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold border ${statusColors[order.status] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                                  #{order.id}
                                </div>
                                <div>
                                  <div className="text-sm font-semibold text-blue-900">{items.length} ürün</div>
                                  <div className="text-xs text-blue-500">{order.created_at ? new Date(order.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {order.siparis_tipi === 'devir' && <span className="text-[10px] font-semibold px-2 py-1 rounded-full border bg-indigo-100 text-indigo-700 border-indigo-200">Devir</span>}
                                <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${statusColors[order.status] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                                  {statusLabels[order.status] || 'Belirsiz'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* === FİYAT AYARLARI GÖRÜNÜMÜ === */}
          {mainView === 'zamayar' && user && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <button onClick={() => setMainView('products')} className="flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium text-sm transition">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
                  Geri
                </button>
                <h1 className="text-2xl font-bold text-blue-900">Fiyat Ayarları</h1>
              </div>

              <div className="max-w-xl mx-auto">
                {/* Mevcut Zam Oranı */}
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-6 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" /></svg>
                    </div>
                    <div>
                      <div className="text-sm text-orange-700 font-medium">Aktif Zam Oranı</div>
                      <div className="text-3xl font-bold text-orange-900">%{zamOrani}</div>
                    </div>
                  </div>
                  <div className="text-xs text-orange-600">
                    SUT kütüphanesindeki orijinal fiyatların üzerine %{zamOrani} zam uygulanarak müşterilere gösterilmektedir.
                  </div>
                </div>

                {/* Zam Oranı Değiştir */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <div className="text-base font-semibold text-gray-800 mb-4">Zam Oranını Güncelle</div>
                  
                  {/* Hızlı seçim butonları */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {[0, 5, 10, 15, 20, 25, 30, 50].map(val => (
                      <button
                        key={val}
                        className={`px-3 py-1.5 rounded-lg text-sm font-semibold border-2 transition ${
                          parseFloat(zamInput) === val
                            ? 'bg-orange-600 text-white border-orange-600'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                        }`}
                        onClick={() => setZamInput(String(val))}
                      >
                        %{val}
                      </button>
                    ))}
                  </div>

                  {/* Manuel giriş */}
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-600 mb-1">Özel Oran (%)</label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="200"
                          step="0.5"
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-lg font-bold text-gray-900 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                          value={zamInput}
                          onChange={e => setZamInput(e.target.value)}
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-lg">%</span>
                      </div>
                    </div>
                    <button
                      className="px-6 py-3 rounded-xl font-bold text-base shadow-lg transition bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
                      disabled={zamSaving || zamInput === '' || isNaN(parseFloat(zamInput))}
                      onClick={async () => {
                        setZamSaving(true);
                        try {
                          const res = await fetch('/api/settings/zam-orani', {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ zam_orani: parseFloat(zamInput) })
                          });
                          if (res.ok) {
                            const data = await res.json();
                            setZamOrani(data.zam_orani);
                            // Ürünleri yeniden yükle (zamlı fiyatlar güncellenmesi için)
                            fetchProducts(true, category, 0, search);
                            alert('Zam oranı başarıyla güncellendi! Ürün fiyatları yenilendi.');
                          }
                        } catch (err) {
                          alert('Hata: ' + err.message);
                        }
                        setZamSaving(false);
                      }}
                    >
                      {zamSaving ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                  </div>

                  {/* Fiyat önizleme */}
                  {parseFloat(zamInput) > 0 && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                      <div className="text-xs font-semibold text-gray-500 mb-2">Fiyat Önizleme</div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Orijinal SUT Fiyatı:</span>
                        <span className="text-gray-800 font-medium">₺1.000,00</span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-gray-600">Zam (%{zamInput}):</span>
                        <span className="text-orange-600 font-medium">+₺{((1000 * parseFloat(zamInput || 0)) / 100).toFixed(2).replace('.', ',')}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1 pt-1 border-t border-gray-200">
                        <span className="text-gray-800 font-semibold">Müşteriye Görünen:</span>
                        <span className="text-blue-900 font-bold">₺{(1000 * (1 + parseFloat(zamInput || 0) / 100)).toFixed(2).replace('.', ',')}</span>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                    <strong>Not:</strong> Zam oranı, SUT kütüphanesindeki orijinal fiyatların üzerine uygulanır. Ürün listesinde, detay ekranında ve sepette müşteriye zamlı fiyat gösterilir.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* === CARİLER GÖRÜNÜMÜ (admin) === */}
          {mainView === 'cariler' && user?.role === 'admin' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <button onClick={() => setMainView('products')} className="flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium text-sm transition">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
                    Geri
                  </button>
                  <h1 className="text-2xl font-bold text-blue-900">Cari Listesi</h1>
                  <span className="text-sm text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full font-medium">{carilerTotal} kayıt</span>
                </div>
                <button onClick={() => { fetchCariler(carilerSearch, carilerSehir, carilerPage); fetchCarilerStats(); }} className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" /></svg>
                  Yenile
                </button>
              </div>

              {/* İstatistik Kartları */}
              {carilerStats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  <div className="bg-white border border-blue-100 rounded-xl p-3 text-center shadow-sm">
                    <div className="text-2xl font-bold text-blue-900">{carilerStats.total}</div>
                    <div className="text-[10px] text-blue-500 mt-1 font-medium">Toplam Cari</div>
                  </div>
                  <div className="bg-white border border-indigo-100 rounded-xl p-3 text-center shadow-sm">
                    <div className="text-2xl font-bold text-indigo-600">{carilerStats.imported}</div>
                    <div className="text-[10px] text-indigo-500 mt-1 font-medium">Excel İmport</div>
                  </div>
                  <div className="bg-white border border-green-100 rounded-xl p-3 text-center shadow-sm">
                    <div className="text-2xl font-bold text-green-600">{carilerStats.manual}</div>
                    <div className="text-[10px] text-green-500 mt-1 font-medium">Manuel Kayıt</div>
                  </div>
                  <div className="bg-white border border-orange-100 rounded-xl p-3 text-center shadow-sm">
                    <div className="text-2xl font-bold text-orange-600">{carilerStats.sehirler?.length || 0}</div>
                    <div className="text-[10px] text-orange-500 mt-1 font-medium">Şehir</div>
                  </div>
                </div>
              )}

              {/* Arama ve Filtre */}
              <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <div className="flex-1 relative">
                  <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg>
                  <input
                    type="text"
                    placeholder="Firma adı, kart kodu, vergi no, şehir veya e-posta ile ara..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                    value={carilerSearch}
                    onChange={e => { setCarilerSearch(e.target.value); setCarilerPage(1); }}
                  />
                </div>
                <select
                  className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-400"
                  value={carilerSehir}
                  onChange={e => { setCarilerSehir(e.target.value); setCarilerPage(1); }}
                >
                  <option value="">Tüm Şehirler</option>
                  {carilerStats?.sehirler?.map(s => (
                    <option key={s.sehir} value={s.sehir}>{s.sehir} ({s.cnt})</option>
                  ))}
                </select>
              </div>

              {/* Cari Tablosu */}
              {carilerLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-700 mb-4"></div>
                  <div className="text-blue-600 text-sm">Cariler yükleniyor...</div>
                </div>
              ) : cariler.length === 0 ? (
                <div className="text-center py-16 text-gray-500">Sonuç bulunamadı.</div>
              ) : (
                <>
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" style={{minWidth: 900}}>
                        {/* Tablo Başlığı */}
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                            <th className="text-left px-3 py-2.5 w-[80px]">Kod</th>
                            <th className="text-left px-3 py-2.5" style={{maxWidth: 260}}>Firma Adı</th>
                            <th className="text-left px-3 py-2.5 w-[170px]">E-Posta</th>
                            <th className="text-left px-3 py-2.5 w-[90px]">Şehir</th>
                            <th className="text-left px-3 py-2.5 w-[100px]">Vergi No</th>
                            <th className="text-left px-3 py-2.5 w-[50px]">Tip</th>
                            <th className="text-left px-3 py-2.5 w-[60px]">Bildirim</th>
                            <th className="text-left px-3 py-2.5 w-[65px]">Durum</th>
                            <th className="text-left px-3 py-2.5 w-[110px]">İşlemler</th>
                          </tr>
                        </thead>
                        {/* Satırlar */}
                        <tbody className="divide-y divide-gray-100">
                          {cariler.map((cari) => (
                            <tr key={cari.id} className="hover:bg-blue-50/30 transition">
                              <td className="px-3 py-2.5">
                                <span className="text-[10px] font-mono text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded whitespace-nowrap">{cari.kart_kodu || '-'}</span>
                              </td>
                              <td className="px-3 py-2.5 max-w-[260px]">
                                <button className="font-semibold text-blue-900 text-xs truncate hover:text-blue-600 hover:underline text-left block w-full" title={cari.firma} onClick={() => fetchCariDetail(cari.id)}>{cari.firma || cari.name}</button>
                                {cari.vergi_dairesi && <div className="text-[9px] text-gray-400 truncate">{cari.vergi_dairesi}</div>}
                              </td>
                              <td className="px-3 py-2.5 max-w-[170px]">
                                <span className="text-[11px] text-gray-600 truncate block" title={cari.email}>{cari.email}</span>
                              </td>
                              <td className="px-3 py-2.5">
                                <span className="text-[11px] text-gray-700 whitespace-nowrap">{cari.sehir || '-'}</span>
                              </td>
                              <td className="px-3 py-2.5">
                                <span className="text-[11px] font-mono text-gray-600 whitespace-nowrap">{cari.vergiNo || '-'}</span>
                              </td>
                              <td className="px-3 py-2.5">
                                {cari.cari_tipi === 'Alıcı' && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap">Alıcı</span>}
                                {cari.cari_tipi === 'Satıcı' && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200 whitespace-nowrap">Satıcı</span>}
                                {cari.cari_tipi === 'Alıcı/Satıcı' && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 whitespace-nowrap">A/S</span>}
                              </td>
                              <td className="px-3 py-2.5">
                                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${cari.mail_bildirim ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>
                                  {cari.mail_bildirim ? 'Aktif' : 'Kapalı'}
                                </span>
                              </td>
                              <td className="px-3 py-2.5">
                                <button
                                  onClick={() => handleToggleCariStatus(cari.id)}
                                  className={`text-[9px] font-semibold px-1.5 py-0.5 rounded cursor-pointer transition hover:opacity-80 whitespace-nowrap ${
                                    cari.basvuru_durumu === 'onayli' || cari.basvuru_durumu === 'onaylandi' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                                  }`}
                                  title="Tıkla: Aktif/Pasif değiştir"
                                >
                                  {cari.basvuru_durumu === 'onayli' || cari.basvuru_durumu === 'onaylandi' ? 'Aktif' : cari.basvuru_durumu === 'pasif' ? 'Pasif' : 'Beklemede'}
                                </button>
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="flex gap-1 flex-wrap">
                                  <button onClick={() => { setEditingCari(cari); setCariForm({ firma: cari.firma || '', name: cari.name || '', email: cari.email || '', telefon: cari.telefon || '', adres: cari.adres || '', sehir: cari.sehir || '', vergiNo: cari.vergiNo || '', vergi_dairesi: cari.vergi_dairesi || '', cari_tipi: cari.cari_tipi || '', mail_bildirim: cari.mail_bildirim || false, basvuru_durumu: cari.basvuru_durumu || 'onayli' }); }} className="text-[9px] font-semibold px-1.5 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition whitespace-nowrap">Düzenle</button>
                                  <button onClick={() => fetchCariDetail(cari.id)} className="text-[9px] font-semibold px-1.5 py-1 rounded bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 transition whitespace-nowrap">Detay</button>
                                  {cari.email && (
                                    <button
                                      onClick={async () => {
                                        if (!window.confirm(`${cari.firma || cari.email} için şifre sıfırlanacak ve e-posta gönderilecek. Devam edilsin mi?`)) return;
                                        try {
                                          const token = user?.token || JSON.parse(localStorage.getItem('user') || '{}').token;
                                          const resp = await fetch('/api/admin/reset-password', {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                            body: JSON.stringify({ user_id: cari.id })
                                          });
                                          const data = await resp.json();
                                          if (data.success) {
                                            alert(`Şifre sıfırlandı ve ${cari.email} adresine gönderildi.`);
                                          } else {
                                            alert('Hata: ' + (data.error || 'Şifre sıfırlanamadı.'));
                                          }
                                        } catch { alert('Sunucuya bağlanılamadı.'); }
                                      }}
                                      className="text-[9px] font-semibold px-1.5 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition whitespace-nowrap"
                                      title="Geçici şifre oluştur ve e-posta gönder"
                                    >
                                      Şifre Sıfırla
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Sayfalama */}
                  {carilerTotal > 30 && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-sm text-gray-500">
                        Sayfa {carilerPage} / {Math.ceil(carilerTotal / 30)} ({carilerTotal} kayıt)
                      </div>
                      <div className="flex gap-2">
                        <button
                          disabled={carilerPage <= 1}
                          onClick={() => setCarilerPage(p => Math.max(1, p - 1))}
                          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Önceki
                        </button>
                        <button
                          disabled={carilerPage >= Math.ceil(carilerTotal / 30)}
                          onClick={() => setCarilerPage(p => p + 1)}
                          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Sonraki
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* === CARİ DÜZENLEME MODAL === */}
          {editingCari && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setEditingCari(null)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-blue-900">Cari Düzenle</h2>
                    <button onClick={() => setEditingCari(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">#{editingCari.id} - {editingCari.kart_kodu || 'Kod yok'}</div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Firma Adı</label>
                      <input className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400" value={cariForm.firma || ''} onChange={e => setCariForm(f => ({...f, firma: e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Ad Soyad</label>
                      <input className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400" value={cariForm.name || ''} onChange={e => setCariForm(f => ({...f, name: e.target.value}))} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">E-Posta</label>
                      <input className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400" value={cariForm.email || ''} onChange={e => setCariForm(f => ({...f, email: e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Telefon</label>
                      <input className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400" value={cariForm.telefon || ''} onChange={e => setCariForm(f => ({...f, telefon: e.target.value}))} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Adres</label>
                    <textarea className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400" rows={2} value={cariForm.adres || ''} onChange={e => setCariForm(f => ({...f, adres: e.target.value}))} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Şehir</label>
                      <input className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400" value={cariForm.sehir || ''} onChange={e => setCariForm(f => ({...f, sehir: e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Vergi No</label>
                      <input className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400" value={cariForm.vergiNo || ''} onChange={e => setCariForm(f => ({...f, vergiNo: e.target.value}))} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Vergi Dairesi</label>
                      <input className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400" value={cariForm.vergi_dairesi || ''} onChange={e => setCariForm(f => ({...f, vergi_dairesi: e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Cari Tipi</label>
                      <select className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 bg-white" value={cariForm.cari_tipi || ''} onChange={e => setCariForm(f => ({...f, cari_tipi: e.target.value}))}>
                        <option value="">Seçiniz</option>
                        <option value="Alıcı">Alıcı</option>
                        <option value="Satıcı">Satıcı</option>
                        <option value="Alıcı/Satıcı">Alıcı/Satıcı</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Durum</label>
                      <select className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 bg-white" value={cariForm.basvuru_durumu || ''} onChange={e => setCariForm(f => ({...f, basvuru_durumu: e.target.value}))}>
                        <option value="onayli">Aktif</option>
                        <option value="pasif">Pasif</option>
                        <option value="beklemede">Beklemede</option>
                      </select>
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-400" checked={!!cariForm.mail_bildirim} onChange={e => setCariForm(f => ({...f, mail_bildirim: e.target.checked}))} />
                        <span className="text-sm text-gray-700 font-medium">Mail Bildirim Aktif</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
                  <button onClick={() => setEditingCari(null)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition">İptal</button>
                  <button onClick={handleSaveCari} disabled={cariSaving} className="px-5 py-2 text-sm text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition disabled:opacity-50">
                    {cariSaving ? 'Kaydediliyor...' : 'Kaydet'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* === ÜRÜN DÜZENLEME MODAL === */}
          {editingItemModal && (() => {
            const { orderId, idx, item, formQty, formPrice, formDiscType, formDiscVal } = editingItemModal;
            const measureText = formatMeasureDisplay(item.measure);
            const qty = formQty !== undefined ? formQty : (parseInt(item.qty) || 1);
            const price = formPrice !== undefined ? formPrice : (parseFloat(item.price) || 0);
            const discType = formDiscType !== undefined ? formDiscType : (item.discount_type || 'none');
            const discVal = formDiscVal !== undefined ? formDiscVal : (parseFloat(item.discount_value) || 0);
            const lineTotal = price * qty;
            let discountAmt = 0;
            if (discType === 'percent') discountAmt = lineTotal * discVal / 100;
            else if (discType === 'amount') discountAmt = discVal;
            const lineFinal = Math.max(0, lineTotal - discountAmt);
            const updateForm = (patch) => setEditingItemModal(prev => ({...prev, ...patch}));
            return (
            <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={() => setEditingItemModal(null)}>
              <div className="bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl w-full sm:max-w-md sm:mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} style={{animation: 'slideUp 0.25s ease-out'}}>
                <style>{`@keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
                {/* Başlık */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                  <div>
                    <h3 className="text-base font-bold text-gray-800">{item.name || 'Ürün'}</h3>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {item.barcode && <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{item.barcode}</span>}
                      {measureText && <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded font-medium">{measureText}</span>}
                      {item.sutCode && <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded font-medium">{item.sutCode}</span>}
                    </div>
                  </div>
                  <button onClick={() => setEditingItemModal(null)} className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-xl font-bold">&times;</button>
                </div>
                {/* Form alanları */}
                <div className="p-5 space-y-4">
                  {/* Adet + Fiyat */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1.5">Adet</label>
                      <input type="number" min={1} step={1} value={qty} onChange={e => updateForm({ formQty: Math.max(1, parseInt(e.target.value) || 1) })} className="w-full px-4 py-3 text-base font-bold text-blue-900 text-center border border-gray-200 rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 focus:bg-white transition" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1.5">Birim Fiyat (₺)</label>
                      <input type="text" value={formPrice !== undefined ? (formPrice === 0 && editingItemModal._priceRaw === '' ? '' : editingItemModal._priceRaw !== undefined ? editingItemModal._priceRaw : price.toLocaleString('tr-TR', {minimumFractionDigits:2})) : (item.price > 0 ? parseFloat(item.price).toLocaleString('tr-TR', {minimumFractionDigits:2}) : '')} placeholder="0,00" onChange={e => {
                        const raw = e.target.value;
                        const parsed = parseFloat(raw.replace(/\./g, '').replace(',', '.')) || 0;
                        updateForm({ formPrice: parsed, _priceRaw: raw });
                      }} className="w-full px-4 py-3 text-base text-right text-gray-800 border border-gray-200 rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 focus:bg-white transition" />
                    </div>
                  </div>
                  {/* İndirim */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5">İndirim</label>
                    <div className="flex items-center gap-3">
                      <select value={discType} onChange={e => updateForm({ formDiscType: e.target.value, formDiscVal: discType !== e.target.value ? 0 : discVal })} className="px-4 py-3 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:ring-2 focus:ring-orange-200 focus:bg-white transition">
                        <option value="none">İndirim Yok</option><option value="percent">Yüzde (%)</option><option value="amount">Tutar (₺)</option>
                      </select>
                      {discType !== 'none' && (
                        <input type="number" step="1" min="0" value={discVal || ''} onChange={e => updateForm({ formDiscVal: parseFloat(e.target.value) || 0 })} placeholder="0" className="flex-1 px-4 py-3 text-base text-right border border-gray-200 rounded-xl bg-gray-50 focus:ring-2 focus:ring-orange-200 focus:bg-white transition" />
                      )}
                    </div>
                  </div>
                  {/* Satır toplamı bilgi - anlık güncellenir */}
                  <div className="bg-blue-50/60 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Satır Toplamı</span>
                      <span className="text-lg font-bold text-blue-800">{lineFinal.toLocaleString('tr-TR', {minimumFractionDigits:2})} ₺</span>
                    </div>
                    {discountAmt > 0 && (
                      <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-blue-100">
                        <span className="text-xs text-orange-500">İndirim tutarı</span>
                        <span className="text-sm font-semibold text-orange-600">-{discountAmt.toLocaleString('tr-TR', {minimumFractionDigits:2})} ₺</span>
                      </div>
                    )}
                  </div>
                </div>
                {/* Alt butonlar */}
                <div className="p-5 border-t border-gray-100 space-y-3">
                  <button onClick={async () => {
                    // Update qty & price
                    const resp1 = await authFetch('/api/guest-offers/' + orderId + '/item-update', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ itemIndex: idx, qty: qty, price: price }) });
                    if (resp1.ok) { const data = await resp1.json(); updateAdminOrderLocal(orderId, { items: JSON.stringify(data.items) }); }
                    // Update discount
                    const resp2 = await authFetch('/api/guest-offers/' + orderId + '/item-discount', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ itemIndex: idx, discount_type: discType, discount_value: discVal, discount_note: item.discount_note || '' }) });
                    if (resp2.ok) { const data = await resp2.json(); updateAdminOrderLocal(orderId, { items: JSON.stringify(data.items) }); }
                    setEditingItemModal(null);
                  }} className="w-full py-3 text-sm font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 transition shadow-lg shadow-blue-200">
                    Kaydet
                  </button>
                  <div className="flex gap-2">
                    {item.status !== 'cancelled' ? (
                      <button onClick={async () => {
                        const reason = prompt('Red sebebi (isteğe bağlı):');
                        if (reason === null) return;
                        const resp = await authFetch('/api/guest-offers/' + orderId + '/item-reject', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ itemIndex: idx, reject_reason: reason || 'Çıkartıldı' }) });
                        if (resp.ok) { const data = await resp.json(); updateAdminOrderLocal(orderId, { status: data.orderStatus, items: JSON.stringify(data.items) }); setEditingItemModal(null); }
                      }} className="flex-1 py-2.5 text-sm font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-xl hover:bg-rose-100 transition">
                        Çıkart
                      </button>
                    ) : (
                      <>
                        <button onClick={async () => {
                          const resp = await authFetch('/api/guest-offers/' + orderId + '/item-status', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ itemIndex: idx, status: 'pending' }) });
                          if (resp.ok) { const data = await resp.json(); updateAdminOrderLocal(orderId, { status: data.orderStatus, items: JSON.stringify(data.items) }); setEditingItemModal(null); }
                        }} className="flex-1 py-2.5 text-sm font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 transition">
                          Geri Al
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm('Bu ürünü siparişten kalıcı olarak silmek istediğinize emin misiniz?')) return;
                          const resp = await authFetch('/api/guest-offers/' + orderId + '/item-remove/' + idx, { method: 'DELETE' });
                          if (resp.ok) { const data = await resp.json(); updateAdminOrderLocal(orderId, { status: data.orderStatus, items: JSON.stringify(data.items) }); setEditingItemModal(null); }
                        }} className="flex-1 py-2.5 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition">
                          Kalıcı Sil
                        </button>
                      </>
                    )}
                    <button onClick={() => setEditingItemModal(null)} className="flex-1 py-2.5 text-sm font-semibold text-gray-500 bg-gray-100 rounded-xl hover:bg-gray-200 transition">
                      İptal
                    </button>
                  </div>
                </div>
              </div>
            </div>
            );
          })()}

          {/* === CARİ DETAY MODAL === */}
          {cariDetail && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { setCariDetail(null); setCariDetailOrders([]); setCariDetailFirmalar([]); setFirmaSearchQuery(''); setFirmaSearchResults([]); }}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                {cariDetailLoading ? (
                  <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div></div>
                ) : (
                  <>
                    <div className="p-6 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-lg font-bold text-blue-900">{cariDetail.firma || cariDetail.name}</h2>
                          <div className="text-xs text-gray-500 mt-1">{cariDetail.kart_kodu || '-'} | {cariDetail.email} | {cariDetail.sehir || '-'}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleToggleCariStatus(cariDetail.id)} className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition ${cariDetail.basvuru_durumu === 'onayli' || cariDetail.basvuru_durumu === 'onaylandi' ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'}`}>
                            {cariDetail.basvuru_durumu === 'onayli' || cariDetail.basvuru_durumu === 'onaylandi' ? 'Aktif' : cariDetail.basvuru_durumu === 'pasif' ? 'Pasif' : 'Beklemede'}
                          </button>
                          <button onClick={() => { setCariDetail(null); setCariDetailOrders([]); setCariDetailFirmalar([]); setFirmaSearchQuery(''); setFirmaSearchResults([]); }} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      {/* Cari Bilgileri */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                        <div className="bg-gray-50 rounded-lg p-3"><div className="text-[10px] text-gray-500 font-semibold">Telefon</div><div className="text-sm font-medium text-gray-800">{cariDetail.telefon || '-'}</div></div>
                        <div className="bg-gray-50 rounded-lg p-3"><div className="text-[10px] text-gray-500 font-semibold">Vergi No</div><div className="text-sm font-medium text-gray-800">{cariDetail.vergiNo || '-'}</div></div>
                        <div className="bg-gray-50 rounded-lg p-3"><div className="text-[10px] text-gray-500 font-semibold">Vergi Dairesi</div><div className="text-sm font-medium text-gray-800">{cariDetail.vergi_dairesi || '-'}</div></div>
                        <div className="bg-gray-50 rounded-lg p-3"><div className="text-[10px] text-gray-500 font-semibold">Cari Tipi</div><div className="text-sm font-medium text-gray-800">{cariDetail.cari_tipi || '-'}</div></div>
                      </div>
                      {cariDetail.adres && <div className="bg-gray-50 rounded-lg p-3 mb-5"><div className="text-[10px] text-gray-500 font-semibold">Adres</div><div className="text-sm text-gray-800">{cariDetail.adres}</div></div>}

                      {/* Sekmeler */}
                      <div className="flex gap-1 mb-4 border-b border-gray-200">
                        <button 
                          onClick={() => setCariDetailTab('siparisler')}
                          className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${cariDetailTab === 'siparisler' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                          Siparişler ({cariDetailOrders.length})
                        </button>
                        <button 
                          onClick={() => setCariDetailTab('firmalar')}
                          className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${cariDetailTab === 'firmalar' ? 'border-amber-600 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                          Yetkili Firmalar ({cariDetailFirmalar.length})
                        </button>
                      </div>

                      {/* SİPARİŞLER SEKMESİ */}
                      {cariDetailTab === 'siparisler' && (
                        <>
                      {cariDetailOrders.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 text-sm">Bu cariye ait sipariş bulunmuyor.</div>
                      ) : (
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                          {cariDetailOrders.map(order => {
                            const payStatusColors = { paid: 'bg-green-100 text-green-700', partial: 'bg-yellow-100 text-yellow-700', unpaid: 'bg-red-100 text-red-700' };
                            const payStatusLabels = { paid: 'Ödendi', partial: 'Kısmi', unpaid: 'Ödenmedi' };
                            const statusColors = { pending: 'bg-yellow-100 text-yellow-700', approved: 'bg-blue-100 text-blue-700', shipping: 'bg-purple-100 text-purple-700', delivered: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700' };
                            const statusLabels = { pending: 'Onay Bekleniyor', approved: 'Onaylandı', shipping: 'Teslimat', delivered: 'Teslim', cancelled: 'İptal' };
                            let items = [];
                            try { items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items || []; } catch(e) {}
                            const orderQty = items.reduce((s,i) => s + (parseInt(i.qty) || 1), 0);
                            const isExpanded = cariOrderExpanded === order.id;
                            // Hesaplamalar
                            const araToplam = items.reduce((s, i) => s + (parseFloat(i.price) || 0) * (parseInt(i.qty) || 1), 0);
                            const hasOrderDiscount = order.discount_type && order.discount_type !== 'none' && parseFloat(order.discount_value) > 0;
                            let orderDiscountAmount = 0;
                            if (hasOrderDiscount) {
                              orderDiscountAmount = order.discount_type === 'percent' ? araToplam * (parseFloat(order.discount_value) / 100) : parseFloat(order.discount_value);
                            }
                            // Ürün bazlı indirimler
                            const itemDiscountTotal = items.reduce((s, i) => {
                              if (i.discount_type && i.discount_type !== 'none' && parseFloat(i.discount_value) > 0) {
                                const lineTotal = (parseFloat(i.price) || 0) * (parseInt(i.qty) || 1);
                                return s + (i.discount_type === 'percent' ? lineTotal * (parseFloat(i.discount_value) / 100) : parseFloat(i.discount_value));
                              }
                              return s;
                            }, 0);
                            const genelToplam = Math.max(0, araToplam - orderDiscountAmount - itemDiscountTotal);
                            return (
                              <div key={order.id} className={`bg-white border rounded-xl transition ${isExpanded ? 'border-blue-300 shadow-md' : 'border-gray-200 hover:border-gray-300'}`}>
                                <button className="w-full text-left p-4" onClick={() => setCariOrderExpanded(isExpanded ? null : order.id)}>
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-blue-900 text-sm">#{order.id}</span>
                                      <span className="text-xs text-gray-500">{new Date(order.created_at).toLocaleDateString('tr-TR')}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {order.siparis_tipi === 'devir' && <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">Devir</span>}
                                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${statusColors[order.status] || 'bg-gray-100 text-gray-700'}`}>{statusLabels[order.status] || order.status}</span>
                                      <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-600">{items.length} kalem, {orderQty} adet</span>
                                    {araToplam > 0 && <span className="text-xs font-bold text-blue-900">{genelToplam.toLocaleString('tr-TR', {minimumFractionDigits:2})} TL</span>}
                                  </div>
                                </button>
                                {/* Genişletilmiş Detay */}
                                {isExpanded && (
                                  <div className="px-4 pb-4 border-t border-gray-100">
                                    {/* Firma bilgisi */}
                                    {order.firma && <div className="text-[11px] text-gray-500 mt-3 mb-2">Firma: <span className="font-semibold text-gray-700">{order.firma}</span></div>}
                                    {/* Sipariş notu */}
                                    {order.note && <div className="text-[11px] text-gray-500 mb-3 bg-yellow-50 border border-yellow-100 rounded-lg p-2"><span className="font-semibold">Not:</span> {order.note}</div>}
                                    {/* Ürün Tablosu */}
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-[11px]">
                                        <thead>
                                          <tr className="text-left text-[9px] font-semibold text-gray-400 uppercase border-b border-gray-200">
                                            <th className="py-1.5 pr-2">#</th>
                                            <th className="py-1.5 pr-2">Ürün</th>
                                            <th className="py-1.5 pr-2 text-center">Adet</th>
                                            <th className="py-1.5 pr-2 text-right">Birim Fiyat</th>
                                            <th className="py-1.5 pr-2 text-center">İndirim</th>
                                            <th className="py-1.5 text-right">Toplam</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                          {items.map((item, idx) => {
                                            const lineTotal = (parseFloat(item.price) || 0) * (parseInt(item.qty) || 1);
                                            const hasItemDisc = item.discount_type && item.discount_type !== 'none' && parseFloat(item.discount_value) > 0;
                                            let itemDiscAmt = 0;
                                            if (hasItemDisc) {
                                              itemDiscAmt = item.discount_type === 'percent' ? lineTotal * (parseFloat(item.discount_value) / 100) : parseFloat(item.discount_value);
                                            }
                                            return (
                                              <tr key={idx} className="hover:bg-blue-50/30">
                                                <td className="py-1.5 pr-2 text-gray-400 font-bold">{idx + 1}</td>
                                                <td className="py-1.5 pr-2">
                                                  <div className="font-semibold text-gray-800 truncate max-w-[180px]" title={item.name}>{item.name || 'Ürün'}</div>
                                                  <div className="text-[9px] text-gray-400 flex gap-1">
                                                    {item.marka && <span>{item.marka}</span>}
                                                    {item.barcode && <span className="font-mono">{item.barcode}</span>}
                                                    {item.sutCode && <span className="text-emerald-600">{item.sutCode}</span>}
                                                  </div>
                                                </td>
                                                <td className="py-1.5 pr-2 text-center font-bold text-blue-900">{parseInt(item.qty) || 1}</td>
                                                <td className="py-1.5 pr-2 text-right text-gray-600">{parseFloat(item.price) > 0 ? parseFloat(item.price).toLocaleString('tr-TR', {minimumFractionDigits:2}) + ' TL' : '-'}</td>
                                                <td className="py-1.5 pr-2 text-center">
                                                  {hasItemDisc ? (
                                                    <span className="text-orange-600 font-semibold">
                                                      {item.discount_type === 'percent' ? `%${item.discount_value}` : `${parseFloat(item.discount_value).toLocaleString('tr-TR', {minimumFractionDigits:2})} TL`}
                                                    </span>
                                                  ) : <span className="text-gray-300">-</span>}
                                                </td>
                                                <td className="py-1.5 text-right font-semibold text-gray-800">{lineTotal > 0 ? (lineTotal - itemDiscAmt).toLocaleString('tr-TR', {minimumFractionDigits:2}) + ' TL' : '-'}</td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                    {/* Toplam Bilgileri */}
                                    <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
                                      <div className="flex justify-between text-[11px]">
                                        <span className="text-gray-500">Ara Toplam</span>
                                        <span className="font-semibold text-gray-700">{araToplam.toLocaleString('tr-TR', {minimumFractionDigits:2})} TL</span>
                                      </div>
                                      {itemDiscountTotal > 0 && (
                                        <div className="flex justify-between text-[11px]">
                                          <span className="text-orange-500">Ürün İndirimleri <span className="bg-orange-50 px-1 py-0.5 rounded text-[9px] font-medium">%{(araToplam > 0 ? (itemDiscountTotal / araToplam * 100) : 0).toFixed(1)}</span></span>
                                          <span className="font-semibold text-orange-600">-{itemDiscountTotal.toLocaleString('tr-TR', {minimumFractionDigits:2})} TL</span>
                                        </div>
                                      )}
                                      <div className="flex justify-between text-sm pt-1 border-t border-gray-200">
                                        <span className="font-bold text-blue-900">Genel Toplam</span>
                                        <span className="font-bold text-blue-900">{genelToplam.toLocaleString('tr-TR', {minimumFractionDigits:2})} TL</span>
                                      </div>
                                    </div>
                                    {/* PDF İndir */}
                                    <div className="mt-3 flex justify-end">
                                      <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); generateProformaPDF(order, items, cariDetail); }} className="flex items-center gap-1.5 text-[10px] font-semibold px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 active:bg-red-200 transition touch-manipulation">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m.75 12 3 3m0 0 3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" /></svg>
                                        Proforma PDF İndir
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                        </>
                      )}

                      {/* YETKİLİ FİRMALAR SEKMESİ */}
                      {cariDetailTab === 'firmalar' && (
                        <div>
                          {/* Firma Arama ve Ekleme */}
                          <div className="mb-4">
                            <div className="relative">
                              <input
                                type="text"
                                placeholder="Firma ara (isim, email veya vergi no)..."
                                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                                value={firmaSearchQuery}
                                onChange={(e) => {
                                  setFirmaSearchQuery(e.target.value);
                                  searchFirmaForCari(e.target.value);
                                }}
                              />
                              {firmaSearchLoading && (
                                <div className="absolute right-3 top-3">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-600"></div>
                                </div>
                              )}
                            </div>
                            {/* Arama Sonuçları */}
                            {firmaSearchResults.length > 0 && (
                              <div className="mt-2 border border-gray-200 rounded-lg bg-white shadow-lg max-h-48 overflow-y-auto">
                                {firmaSearchResults.map(f => {
                                  const isAlreadyAssigned = cariDetailFirmalar.some(cf => cf.id === f.id);
                                  return (
                                    <div key={f.id} className="flex items-center justify-between px-3 py-2 hover:bg-amber-50 border-b border-gray-100 last:border-b-0">
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">{f.firma}</div>
                                        <div className="text-[10px] text-gray-500">{f.email} {f.sehir ? `| ${f.sehir}` : ''} {f.vergiNo ? `| VKN: ${f.vergiNo}` : ''}</div>
                                      </div>
                                      {isAlreadyAssigned ? (
                                        <span className="text-[10px] font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">Zaten Atanmış</span>
                                      ) : (
                                        <button
                                          disabled={firmaAtamaLoading}
                                          onClick={() => handleAtaFirma(cariDetail.id, f.id, cariDetailFirmalar.length === 0)}
                                          className="text-[10px] font-semibold px-2.5 py-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-300 transition disabled:opacity-50"
                                        >
                                          {firmaAtamaLoading ? '...' : 'Ata'}
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Mevcut Yetkili Firmalar Listesi */}
                          {cariDetailFirmalar.length === 0 ? (
                            <div className="text-center py-8 text-gray-400 text-sm">Bu cariye henüz firma atanmamış.</div>
                          ) : (
                            <div className="space-y-2">
                              {cariDetailFirmalar.map(f => (
                                <div key={f.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl hover:border-amber-200 transition">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${f.is_default ? 'bg-amber-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                                      {(f.firma || '?')[0].toUpperCase()}
                                    </div>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-gray-900">{f.firma}</span>
                                        {f.is_default && (
                                          <span className="text-[9px] font-bold px-1.5 py-0.5 bg-green-100 text-green-700 rounded border border-green-200">Varsayılan</span>
                                        )}
                                      </div>
                                      <div className="text-[10px] text-gray-500">{f.email || ''} {f.sehir ? `| ${f.sehir}` : ''} {f.vergiNo ? `| VKN: ${f.vergiNo}` : ''}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {!f.is_default && (
                                      <button
                                        onClick={() => handleSetDefaultFirma(cariDetail.id, f.id)}
                                        className="text-[9px] font-semibold px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition"
                                        title="Varsayılan Yap"
                                      >
                                        Varsayılan Yap
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleRemoveFirma(cariDetail.id, f.id)}
                                      className="text-[9px] font-semibold px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition"
                                      title="Kaldır"
                                    >
                                      Kaldır
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* === TUR YÖNETİMİ GÖRÜNÜMÜ (admin) === */}
          {mainView === 'tourManagement' && user?.role === 'admin' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <button onClick={() => setMainView('products')} className="flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium text-sm transition">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
                    Geri
                  </button>
                  <h1 className="text-2xl font-bold text-blue-900">Site Tur Yönetimi</h1>
                </div>
              </div>

              {/* Bilgi Kartı */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-blue-900 text-sm mb-2">Nasıl Kullanılır?</h3>
                <ul className="text-xs text-blue-800 space-y-1 list-disc pl-4">
                  <li><strong>Sayfa:</strong> Turun hangi sayfada gorunecegini secin (dashboard, teklif_olustur, login vb.)</li>
                  <li><strong>Hedef Selector:</strong> Vurgulanacak elemanin CSS selectoru. Ornek: <code className="bg-white/50 px-1 rounded">[data-tour="welcome-banner"]</code></li>
                  <li><strong>Pozisyon:</strong> Aciklama kutusunun konumu (top, bottom, left, right)</li>
                  <li><strong>Rol:</strong> Bos birakilirsa herkese gosterilir. 'admin' veya 'member' secebilirsiniz.</li>
                </ul>
                <div className="mt-3 flex flex-wrap gap-1">
                  <span className="text-[10px] text-blue-600 font-medium">Mevcut data-tour hedefleri:</span>
                  {['welcome-banner', 'stat-cards', 'quick-access', 'recent-orders', 'user-menu', 'navbar',
                    'product-search', 'category-filter', 'product-grid', 'cart-sidebar',
                    'login-form', 'login-header', 'register-tab'].map(t => (
                    <code key={t} className="text-[9px] bg-white border border-blue-200 px-1.5 py-0.5 rounded text-blue-700 cursor-pointer hover:bg-blue-100"
                      onClick={() => {
                        if (tourEditingStep !== null || tourNewStep) {
                          setTourForm(f => ({ ...f, target_selector: `[data-tour="${t}"]` }));
                        }
                      }}
                    >{t}</code>
                  ))}
                </div>
              </div>

              {/* Sayfa Secici + Yeni Ekle */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <select
                  className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-400 flex-1 sm:max-w-xs"
                  value={tourSelectedPage}
                  onChange={e => { setTourSelectedPage(e.target.value); setTourEditingStep(null); setTourNewStep(false); }}
                >
                  <option value="dashboard">Dashboard</option>
                  <option value="teklif_olustur">Siparis Olustur</option>
                  <option value="login">Giris Sayfasi</option>
                  <option value="cariler">Cariler</option>
                  <option value="siparislerim">Siparislerim</option>
                  <option value="odeme">Odeme</option>
                  <option value="hesabim">Hesabim</option>
                  {tourPages.filter(p => !['dashboard', 'teklif_olustur', 'login', 'cariler', 'siparislerim', 'odeme', 'hesabim'].includes(p)).map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    setTourNewStep(true);
                    setTourEditingStep(null);
                    const maxOrder = tourSteps.filter(s => s.page === tourSelectedPage).reduce((mx, s) => Math.max(mx, s.step_order), 0);
                    setTourForm({ page: tourSelectedPage, step_order: maxOrder + 1, target_selector: '', title: '', description: '', position: 'bottom', is_active: 1, show_for_role: '' });
                  }}
                  className="px-4 py-2.5 bg-blue-700 text-white rounded-lg text-sm font-semibold hover:bg-blue-800 transition flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                  Yeni Adim Ekle
                </button>
              </div>

              {/* Yeni Adim / Duzenleme Formu */}
              {(tourNewStep || tourEditingStep !== null) && (
                <div className="bg-white rounded-xl border border-blue-200 p-5 mb-4 shadow-sm">
                  <h3 className="font-semibold text-blue-900 text-sm mb-4">{tourNewStep ? 'Yeni Tur Adimi' : 'Adimi Duzenle'}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Baslik *</label>
                      <input type="text" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400" placeholder="Hosgeldiniz!" value={tourForm.title} onChange={e => setTourForm(f => ({ ...f, title: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Hedef Selector</label>
                      <input type="text" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-400" placeholder='[data-tour="welcome-banner"]' value={tourForm.target_selector} onChange={e => setTourForm(f => ({ ...f, target_selector: e.target.value }))} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Aciklama *</label>
                      <textarea className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400" rows={3} placeholder="Bu alanda siparislerinizi takip edebilirsiniz..." value={tourForm.description} onChange={e => setTourForm(f => ({ ...f, description: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Pozisyon</label>
                      <select className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" value={tourForm.position} onChange={e => setTourForm(f => ({ ...f, position: e.target.value }))}>
                        <option value="bottom">Alt (bottom)</option>
                        <option value="top">Ust (top)</option>
                        <option value="left">Sol (left)</option>
                        <option value="right">Sag (right)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Gosterilecek Rol</label>
                      <select className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" value={tourForm.show_for_role} onChange={e => setTourForm(f => ({ ...f, show_for_role: e.target.value }))}>
                        <option value="">Herkes</option>
                        <option value="admin">Sadece Admin</option>
                        <option value="member">Sadece Bayi</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Sira No</label>
                      <input type="number" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" value={tourForm.step_order} onChange={e => setTourForm(f => ({ ...f, step_order: parseInt(e.target.value) || 1 }))} min={1} />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={tourForm.is_active === 1} onChange={e => setTourForm(f => ({ ...f, is_active: e.target.checked ? 1 : 0 }))} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        <span className="text-sm text-gray-700">Aktif</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={async () => {
                        if (!tourForm.title || !tourForm.description) { alert('Baslik ve aciklama zorunlu'); return; }
                        const success = await saveTourStep(tourForm, tourNewStep);
                        if (success) setTourSaving(false);
                      }}
                      disabled={tourSaving}
                      className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-semibold hover:bg-blue-800 transition disabled:opacity-50"
                    >
                      {tourSaving ? 'Kaydediliyor...' : (tourNewStep ? 'Ekle' : 'Guncelle')}
                    </button>
                    <button
                      onClick={() => { setTourEditingStep(null); setTourNewStep(false); }}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
                    >
                      Iptal
                    </button>
                  </div>
                </div>
              )}

              {/* Mevcut Adimlar Listesi */}
              {tourStepsLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-700 mb-4"></div>
                  <div className="text-blue-600 text-sm">Tur adimlari yukleniyor...</div>
                </div>
              ) : (
                <div className="space-y-2">
                  {tourSteps.filter(s => s.page === tourSelectedPage).length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z" /></svg>
                      <p className="text-sm">Bu sayfa icin henuz tur adimi yok.</p>
                      <p className="text-xs text-gray-400 mt-1">"Yeni Adim Ekle" butonu ile baslayin.</p>
                    </div>
                  ) : (
                    tourSteps.filter(s => s.page === tourSelectedPage).map((step, idx, arr) => (
                      <div key={step.id} className={`bg-white rounded-xl border ${step.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'} p-4 flex items-start gap-3 transition hover:shadow-sm`}>
                        {/* Sira ve Yukari/Asagi */}
                        <div className="flex flex-col items-center gap-1 flex-shrink-0">
                          <button onClick={() => moveTourStep(idx, 'up')} disabled={idx === 0} className="text-gray-400 hover:text-blue-600 disabled:opacity-20 transition">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" /></svg>
                          </button>
                          <span className="text-lg font-bold text-blue-700 w-8 h-8 flex items-center justify-center bg-blue-50 rounded-full">{step.step_order}</span>
                          <button onClick={() => moveTourStep(idx, 'down')} disabled={idx === arr.length - 1} className="text-gray-400 hover:text-blue-600 disabled:opacity-20 transition">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>
                          </button>
                        </div>
                        {/* Icerik */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-blue-900 text-sm">{step.title}</span>
                            {!step.is_active && <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">Pasif</span>}
                            {step.show_for_role && <span className="text-[9px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-medium">{step.show_for_role === 'admin' ? 'Admin' : 'Bayi'}</span>}
                            <span className="text-[9px] bg-gray-50 text-gray-400 px-1.5 py-0.5 rounded font-mono">{step.position}</span>
                          </div>
                          <p className="text-xs text-gray-600 mb-1.5 line-clamp-2">{step.description}</p>
                          {step.target_selector && (
                            <code className="text-[10px] bg-gray-50 text-gray-500 px-1.5 py-0.5 rounded font-mono">{step.target_selector}</code>
                          )}
                        </div>
                        {/* Aksiyonlar */}
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            onClick={() => {
                              setTourEditingStep(step.id);
                              setTourNewStep(false);
                              setTourForm({
                                id: step.id, page: step.page, step_order: step.step_order, target_selector: step.target_selector || '',
                                title: step.title, description: step.description, position: step.position || 'bottom',
                                is_active: step.is_active, show_for_role: step.show_for_role || ''
                              });
                            }}
                            className="text-[9px] font-semibold px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition"
                          >
                            Duzenle
                          </button>
                          <button
                            onClick={() => deleteTourStep(step.id)}
                            className="text-[9px] font-semibold px-2 py-1 rounded bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition"
                          >
                            Sil
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* === AKTİVİTE LOGLARI GÖRÜNÜMÜ (admin) === */}
          {mainView === 'activityLogs' && user?.role === 'admin' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <button onClick={() => setMainView('products')} className="flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium text-sm transition">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
                    Geri
                  </button>
                  <h1 className="text-2xl font-bold text-blue-900">Aktivite Logları</h1>
                  <span className="text-sm text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full font-medium">{activityLogsTotal} kayıt</span>
                </div>
              </div>

              {/* İstatistik Kartları */}
              {activityLogsStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <div className="text-2xl font-bold text-blue-700">{activityLogsStats.total_logs || 0}</div>
                    <div className="text-xs text-gray-500 mt-1">Toplam İşlem</div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-700">{activityLogsStats.unique_users || 0}</div>
                    <div className="text-xs text-gray-500 mt-1">Benzersiz Kullanıcı</div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <div className="text-2xl font-bold text-violet-700">{activityLogsStats.unique_sessions || 0}</div>
                    <div className="text-xs text-gray-500 mt-1">Oturum Sayısı</div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <div className="text-2xl font-bold text-amber-700">{activityLogsStats.actionCounts?.find(a => a.action_type === 'login')?.cnt || 0}</div>
                    <div className="text-xs text-gray-500 mt-1">Giriş Sayısı</div>
                  </div>
                </div>
              )}

              {/* En Aktif Kullanıcılar */}
              {activityLogsStats?.activeUsers?.length > 0 && !activityLogsSessionDetail && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
                  <h3 className="font-semibold text-blue-900 text-sm mb-3">En Aktif Kullanıcılar</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-gray-500 border-b"><th className="text-left py-1.5 px-2">Kullanıcı</th><th className="text-left py-1.5 px-2">Firma</th><th className="text-left py-1.5 px-2">E-posta</th><th className="text-right py-1.5 px-2">İşlem</th><th className="text-right py-1.5 px-2">Son Aktivite</th></tr></thead>
                      <tbody>
                        {activityLogsStats.activeUsers.map((u, i) => (
                          <tr key={i} className="border-b border-gray-50 hover:bg-blue-50/30">
                            <td className="py-1.5 px-2 font-medium">{u.user_name || '-'}</td>
                            <td className="py-1.5 px-2 text-gray-600">{u.firma || '-'}</td>
                            <td className="py-1.5 px-2 text-gray-500">{u.user_email || '-'}</td>
                            <td className="py-1.5 px-2 text-right"><span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-semibold">{u.action_count}</span></td>
                            <td className="py-1.5 px-2 text-right text-gray-400">{u.last_activity ? new Date(u.last_activity).toLocaleString('tr-TR') : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Filtreler */}
              <div className="flex flex-col md:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg>
                  <input
                    type="text"
                    placeholder="Kullanıcı adı, e-posta veya firma ara..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400"
                    value={activityLogsSearch}
                    onChange={e => { setActivityLogsSearch(e.target.value); setActivityLogsPage(1); }}
                  />
                </div>
                <select
                  className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white"
                  value={activityLogsActionType}
                  onChange={e => { setActivityLogsActionType(e.target.value); setActivityLogsPage(1); }}
                >
                  <option value="">Tüm İşlemler</option>
                  <option value="login">Giriş</option>
                  <option value="logout">Çıkış</option>
                  <option value="page_view">Sayfa Görüntüleme</option>
                  <option value="product_detail_open">Ürün Detay Açma</option>
                  <option value="product_detail_close">Ürün Detay Kapama</option>
                  <option value="cart_add">Sepete Ekleme</option>
                  <option value="cart_remove">Sepetten Çıkarma</option>
                  <option value="order_create">Sipariş Oluşturma</option>
                  <option value="search">Arama</option>
                  <option value="filter_change">Filtre Değişikliği</option>
                </select>
                <input type="date" className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm" value={activityLogsDateFrom} onChange={e => { setActivityLogsDateFrom(e.target.value); setActivityLogsPage(1); }} />
                <input type="date" className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm" value={activityLogsDateTo} onChange={e => { setActivityLogsDateTo(e.target.value); setActivityLogsPage(1); }} />
              </div>

              {/* Oturum Detay Görünümü */}
              {activityLogsSessionDetail && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-blue-900 text-sm">Oturum Detayı ({activityLogsSessionDetail.length} işlem)</h3>
                    <button onClick={() => setActivityLogsSessionDetail(null)} className="text-xs text-red-600 hover:text-red-800 font-medium px-2 py-1 rounded border border-red-200 hover:bg-red-50">Kapat</button>
                  </div>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {activityLogsSessionDetail.map((log, i) => (
                      <div key={i} className="flex items-start gap-3 p-2 rounded-lg bg-gray-50 border border-gray-100">
                        <div className="flex-shrink-0 w-16 text-[10px] text-gray-400 pt-0.5">{new Date(log.created_at).toLocaleTimeString('tr-TR')}</div>
                        <div className="flex-1">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                            log.action_type === 'login' ? 'bg-emerald-50 text-emerald-700' :
                            log.action_type === 'logout' ? 'bg-red-50 text-red-700' :
                            log.action_type === 'cart_add' ? 'bg-blue-50 text-blue-700' :
                            log.action_type === 'cart_remove' ? 'bg-orange-50 text-orange-700' :
                            log.action_type === 'product_detail_open' ? 'bg-violet-50 text-violet-700' :
                            log.action_type === 'product_detail_close' ? 'bg-gray-100 text-gray-700' :
                            log.action_type === 'order_create' ? 'bg-emerald-50 text-emerald-800 font-bold' :
                            log.action_type === 'search' ? 'bg-amber-50 text-amber-700' :
                            'bg-gray-50 text-gray-600'
                          }`}>{
                            log.action_type === 'login' ? 'Giriş' :
                            log.action_type === 'logout' ? 'Çıkış' :
                            log.action_type === 'page_view' ? 'Sayfa' :
                            log.action_type === 'product_detail_open' ? 'Ürün Açma' :
                            log.action_type === 'product_detail_close' ? 'Ürün Kapama' :
                            log.action_type === 'cart_add' ? 'Sepete Ekleme' :
                            log.action_type === 'cart_remove' ? 'Sepetten Çıkarma' :
                            log.action_type === 'order_create' ? 'Sipariş' :
                            log.action_type === 'search' ? 'Arama' :
                            log.action_type === 'filter_change' ? 'Filtre' :
                            log.action_type
                          }</span>
                          {log.action_detail && (
                            <span className="ml-2 text-[10px] text-gray-500">
                              {log.action_detail.product_name && `Ürün: ${log.action_detail.product_name}`}
                              {log.action_detail.term && `"${log.action_detail.term}"`}
                              {log.action_detail.page && `Sayfa: ${log.action_detail.page}`}
                              {log.action_detail.duration_seconds > 0 && ` (${log.action_detail.duration_seconds}sn)`}
                              {log.action_detail.quantity && ` x${log.action_detail.quantity}`}
                              {log.action_detail.item_count && ` ${log.action_detail.item_count} ürün`}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Log Tablosu */}
              {activityLogsLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-700 mb-4"></div>
                  <div className="text-blue-600 text-sm">Loglar yükleniyor...</div>
                </div>
              ) : activityLogs.length === 0 ? (
                <div className="text-center py-16 text-gray-500">Henüz aktivite kaydı yok.</div>
              ) : (
                <>
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" style={{minWidth: 900}}>
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                            <th className="text-left px-3 py-2.5 w-[140px]">Tarih</th>
                            <th className="text-left px-3 py-2.5">Kullanıcı</th>
                            <th className="text-left px-3 py-2.5">Firma</th>
                            <th className="text-left px-3 py-2.5 w-[80px]">Rol</th>
                            <th className="text-left px-3 py-2.5 w-[130px]">İşlem</th>
                            <th className="text-left px-3 py-2.5">Detay</th>
                            <th className="text-left px-3 py-2.5 w-[60px]">Süre</th>
                            <th className="text-left px-3 py-2.5 w-[80px]">Oturum</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {activityLogs.map((log) => (
                            <tr key={log.id} className="hover:bg-blue-50/30 transition">
                              <td className="px-3 py-2 text-[11px] text-gray-500 whitespace-nowrap">{new Date(log.created_at).toLocaleString('tr-TR')}</td>
                              <td className="px-3 py-2">
                                <div className="text-xs font-medium text-blue-900">{log.user_name || '-'}</div>
                                <div className="text-[10px] text-gray-400">{log.user_email || '-'}</div>
                              </td>
                              <td className="px-3 py-2 text-[11px] text-gray-600 max-w-[150px] truncate">{log.firma || '-'}</td>
                              <td className="px-3 py-2">
                                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${log.user_role === 'admin' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                                  {log.user_role === 'admin' ? 'Admin' : 'Bayi'}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${
                                  log.action_type === 'login' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                  log.action_type === 'logout' ? 'bg-red-50 text-red-600 border border-red-200' :
                                  log.action_type === 'cart_add' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                  log.action_type === 'cart_remove' ? 'bg-orange-50 text-orange-700 border border-orange-200' :
                                  log.action_type === 'product_detail_open' ? 'bg-violet-50 text-violet-700 border border-violet-200' :
                                  log.action_type === 'product_detail_close' ? 'bg-gray-100 text-gray-600 border border-gray-200' :
                                  log.action_type === 'order_create' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold' :
                                  log.action_type === 'search' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                                  log.action_type === 'page_view' ? 'bg-cyan-50 text-cyan-700 border border-cyan-200' :
                                  'bg-gray-50 text-gray-600 border border-gray-200'
                                }`}>{
                                  log.action_type === 'login' ? 'Giriş' :
                                  log.action_type === 'logout' ? 'Çıkış' :
                                  log.action_type === 'page_view' ? 'Sayfa' :
                                  log.action_type === 'product_detail_open' ? 'Ürün Detay' :
                                  log.action_type === 'product_detail_close' ? 'Detay Kapat' :
                                  log.action_type === 'cart_add' ? 'Sepete Ekle' :
                                  log.action_type === 'cart_remove' ? 'Sepetten Çıkar' :
                                  log.action_type === 'order_create' ? 'Sipariş' :
                                  log.action_type === 'search' ? 'Arama' :
                                  log.action_type === 'filter_change' ? 'Filtre' :
                                  log.action_type
                                }</span>
                              </td>
                              <td className="px-3 py-2 text-[10px] text-gray-500 max-w-[200px] truncate" title={JSON.stringify(log.action_detail)}>
                                {log.action_detail?.product_name && <span className="font-medium text-blue-800">{log.action_detail.product_name}</span>}
                                {log.action_detail?.term && <span>"{log.action_detail.term}" <span className="text-gray-400">({log.action_detail.result_count} sonuç)</span></span>}
                                {log.action_detail?.page && <span>Sayfa: {log.action_detail.page}</span>}
                                {log.action_detail?.item_count && <span>{log.action_detail.item_count} ürün, {log.action_detail.total_qty} adet</span>}
                                {log.action_detail?.source && <span className="text-gray-400 ml-1">({log.action_detail.source})</span>}
                                {log.action_detail?.variant && <span className="text-gray-400 ml-1">{log.action_detail.variant}</span>}
                                {log.action_detail?.quantity && <span className="text-gray-400 ml-1">x{log.action_detail.quantity}</span>}
                                {log.action_detail?.filter_type && <span>{log.action_detail.filter_type}: {log.action_detail.filter_value}</span>}
                              </td>
                              <td className="px-3 py-2 text-[10px] text-gray-500">
                                {log.action_detail?.duration_seconds > 0 ? `${log.action_detail.duration_seconds}sn` : '-'}
                              </td>
                              <td className="px-3 py-2">
                                {log.session_id && (
                                  <button
                                    onClick={() => fetchSessionDetail(log.session_id)}
                                    className="text-[9px] font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                    title={log.session_id}
                                  >
                                    {log.session_id.substring(0, 12)}...
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Sayfalama */}
                  {activityLogsTotalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-sm text-gray-500">
                        Sayfa {activityLogsPage} / {activityLogsTotalPages} ({activityLogsTotal} kayıt)
                      </div>
                      <div className="flex gap-2">
                        <button
                          disabled={activityLogsPage <= 1}
                          onClick={() => setActivityLogsPage(p => Math.max(1, p - 1))}
                          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                        >
                          Önceki
                        </button>
                        <button
                          disabled={activityLogsPage >= activityLogsTotalPages}
                          onClick={() => setActivityLogsPage(p => p + 1)}
                          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                        >
                          Sonraki
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* İşlem Tipi Dağılımı */}
              {activityLogsStats?.actionCounts?.length > 0 && !activityLogsSessionDetail && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 mt-6">
                  <h3 className="font-semibold text-blue-900 text-sm mb-3">İşlem Tipi Dağılımı</h3>
                  <div className="flex flex-wrap gap-2">
                    {activityLogsStats.actionCounts.map((ac, i) => (
                      <button key={i}
                        onClick={() => { setActivityLogsActionType(ac.action_type === activityLogsActionType ? '' : ac.action_type); setActivityLogsPage(1); }}
                        className={`text-xs px-3 py-1.5 rounded-full font-medium transition ${ac.action_type === activityLogsActionType ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        {ac.action_type === 'login' ? 'Giriş' :
                         ac.action_type === 'logout' ? 'Çıkış' :
                         ac.action_type === 'page_view' ? 'Sayfa' :
                         ac.action_type === 'product_detail_open' ? 'Ürün Detay' :
                         ac.action_type === 'product_detail_close' ? 'Detay Kapat' :
                         ac.action_type === 'cart_add' ? 'Sepete Ekle' :
                         ac.action_type === 'cart_remove' ? 'Sepetten Çıkar' :
                         ac.action_type === 'order_create' ? 'Sipariş' :
                         ac.action_type === 'search' ? 'Arama' :
                         ac.action_type === 'filter_change' ? 'Filtre' :
                         ac.action_type}
                        <span className="ml-1 bg-white/30 px-1.5 py-0.5 rounded-full text-[10px]">{ac.cnt}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* === ADMİN SİPARİŞ YÖNETİMİ GÖRÜNÜMÜ === */}
          {mainView === 'adminOrders' && user?.role === 'admin' && (
            <div>
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <button onClick={() => setMainView('products')} className="flex items-center gap-0.5 sm:gap-1 text-blue-700 hover:text-blue-900 font-medium text-xs sm:text-sm transition shrink-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
                    <span className="hidden sm:inline">Geri</span>
                  </button>
                  <h1 className="text-lg sm:text-2xl font-bold text-blue-900 truncate">Siparişler</h1>
                  <span className="text-[10px] sm:text-sm text-blue-500 bg-blue-50 px-1.5 sm:px-2 py-0.5 rounded-full font-medium shrink-0">{adminOrdersTotal}</span>
                </div>
                <button onClick={() => { fetchAdminOrders(adminOrdersSearch, adminOrdersStatus, adminOrdersEmail, adminOrdersPage, adminOrdersPaymentStatus); fetchAdminOrdersStats(); }} className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" /></svg>
                  <span className="hidden sm:inline">Yenile</span>
                </button>
              </div>

              {/* İstatistik Kartları */}
              {adminOrdersStats && (
                <div className="flex gap-1.5 sm:gap-2 mb-4 sm:mb-5 overflow-x-auto">
                  <div onClick={() => { setAdminOrdersStatus(''); setAdminOrdersPaymentStatus(''); setAdminOrdersPage(1); }} className={`cursor-pointer border rounded-lg px-3 py-2 text-center shadow-sm transition-all active:scale-95 shrink-0 min-w-0 flex-1 ${!adminOrdersStatus && !adminOrdersPaymentStatus ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-200' : 'bg-white border-blue-100 hover:border-blue-300'}`}>
                    <div className="text-base sm:text-xl font-bold text-blue-900">{adminOrdersStats.total}</div>
                    <div className="text-[9px] sm:text-[10px] text-blue-500 font-medium">Toplam</div>
                  </div>
                  {adminOrdersStats.statuses?.map(s => {
                    const colors = { pending: 'text-yellow-600 border-yellow-100', approved: 'text-blue-600 border-blue-100', shipping: 'text-purple-600 border-purple-100', delivered: 'text-green-600 border-green-100', cancelled: 'text-red-600 border-red-100' };
                    const activeColors = { pending: 'bg-yellow-50 border-yellow-400 ring-2 ring-yellow-200', approved: 'bg-blue-50 border-blue-400 ring-2 ring-blue-200', shipping: 'bg-purple-50 border-purple-400 ring-2 ring-purple-200', delivered: 'bg-green-50 border-green-400 ring-2 ring-green-200', cancelled: 'bg-red-50 border-red-400 ring-2 ring-red-200' };
                    const labels = { pending: 'Bekliyor', approved: 'Onaylı', shipping: 'Teslimat', delivered: 'Teslim', cancelled: 'İptal' };
                    const isActive = adminOrdersStatus === s.status && !adminOrdersPaymentStatus;
                    return (
                      <div key={s.status} onClick={() => { setAdminOrdersStatus(isActive ? '' : s.status); setAdminOrdersPaymentStatus(''); setAdminOrdersPage(1); }} className={`cursor-pointer border rounded-lg px-3 py-2 text-center shadow-sm transition-all active:scale-95 shrink-0 min-w-0 flex-1 ${isActive ? activeColors[s.status] : `bg-white ${colors[s.status] || 'border-gray-100'} hover:shadow-md`}`}>
                        <div className={`text-base sm:text-xl font-bold ${colors[s.status]?.split(' ')[0] || 'text-gray-600'}`}>{s.cnt}</div>
                        <div className="text-[9px] sm:text-[10px] font-medium opacity-70">{labels[s.status] || s.status}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Filtreler */}
              <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <div className="flex-1 relative">
                  <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg>
                  <input type="text" placeholder="Sipariş no, firma adı veya e-posta ile ara..." className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400" value={adminOrdersSearch} onChange={e => { setAdminOrdersSearch(e.target.value); setAdminOrdersPage(1); }} />
                </div>
                <select className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-400" value={adminOrdersStatus} onChange={e => { setAdminOrdersStatus(e.target.value); setAdminOrdersPaymentStatus(''); setAdminOrdersPage(1); }}>
                  <option value="">Tüm Durumlar</option>
                  <option value="pending">Onay Bekleniyor</option>
                  <option value="approved">Onaylandı</option>
                  <option value="shipping">Teslimat Sürecinde</option>
                  <option value="delivered">Teslim Edildi</option>
                  <option value="cancelled">İptal</option>
                </select>
                {/* Ödeme durumu filtre butonu kaldırıldı */}
                {adminOrdersEmail && (
                  <button onClick={() => { setAdminOrdersEmail(''); setAdminOrdersPage(1); }} className="px-3 py-2.5 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 flex items-center gap-1 transition">
                    <span className="truncate max-w-[120px]">{adminOrdersEmail}</span> &times;
                  </button>
                )}
              </div>

              {/* Sipariş Listesi */}
              {adminOrdersLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-700 mb-4"></div>
                  <div className="text-blue-600 text-sm">Siparişler yükleniyor...</div>
                </div>
              ) : adminOrders.length === 0 ? (
                <div className="text-center py-16 text-gray-500">Sipariş bulunamadı.</div>
              ) : (
                <>
                  {/* Odak modu: Sipariş açıkken arka plan karanlık overlay */}
                  {adminExpandedOrder && (
                    <div className="fixed inset-0 bg-black/40 z-40 transition-opacity" onClick={() => setAdminExpandedOrder(null)} />
                  )}
                  <div className={`space-y-3 ${adminExpandedOrder ? 'relative z-40' : ''}`}>
                    {adminOrders.map(order => {
                      const statusColors = { pending: 'bg-amber-50 text-amber-700 border-amber-200', approved: 'bg-sky-50 text-sky-700 border-sky-200', shipping: 'bg-violet-50 text-violet-700 border-violet-200', delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200', cancelled: 'bg-rose-50 text-rose-700 border-rose-200' };
                      const statusLabels = { pending: 'Onay Bekleniyor', approved: 'Sipariş Onaylandı', shipping: 'Teslimat Sürecinde', delivered: 'Teslim Edildi', cancelled: 'İptal Edildi' };
                      const payStatusColors = { paid: 'bg-emerald-50 text-emerald-700', partial: 'bg-amber-50 text-amber-700', unpaid: 'bg-rose-50 text-rose-600' };
                      const payStatusLabels = { paid: 'Ödendi', partial: 'Kısmi Ödeme', unpaid: 'Ödenmedi' };
                      let items = [];
                      try { items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items || []; } catch(e) {}
                      const orderQty = items.reduce((s,i) => s + (parseInt(i.qty) || 1), 0);
                      const isExpanded = adminExpandedOrder === order.id;

                      return (
                        <div key={order.id} className={`rounded-xl border shadow-sm overflow-hidden transition-all ${isExpanded ? 'relative z-50 bg-white border-blue-300 shadow-2xl ring-2 ring-blue-200' : adminExpandedOrder && !isExpanded ? 'opacity-30 pointer-events-none bg-white border-gray-200' : 'bg-white border-gray-200'}`}>
                          {/* Sipariş Başlık */}
                          <div className="p-3 sm:p-4 cursor-pointer hover:bg-gray-50/50 transition" onClick={() => setAdminExpandedOrder(isExpanded ? null : order.id)}>
                            {/* Mobil: Dikey düzen / Desktop: Yatay düzen */}
                            <div className="flex items-start sm:items-center justify-between gap-2">
                              <div className="flex items-start sm:items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                <div className="text-base sm:text-lg font-bold text-blue-900 shrink-0">#{order.id}</div>
                                <div className="min-w-0">
                                  <div className="text-xs sm:text-sm font-semibold text-blue-900 truncate max-w-[180px] sm:max-w-none" title={order.firma || order.email}>{order.firma || order.email}</div>
                                  <div className="text-[9px] sm:text-[10px] text-gray-500 truncate">{order.siparis_tipi === 'devir' ? 'Devir' : new Date(order.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                                  {(() => {
                                    const itemDescs = items.filter(i => i.desc && i.desc.trim()).map(i => i.desc.trim());
                                    const uniqueDescs = [...new Set(itemDescs)];
                                    const noteText = order.payment_notes || (uniqueDescs.length === 1 ? uniqueDescs[0] : uniqueDescs.length > 1 ? uniqueDescs[0] + ` (+${uniqueDescs.length - 1} not)` : '');
                                    return noteText ? <div className="text-[9px] sm:text-[10px] text-amber-700 truncate max-w-[200px] sm:max-w-[400px]" title={noteText}>
                                      <span className="inline-flex items-center gap-0.5"><svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 0 1 1.037-.443 48.282 48.282 0 0 0 5.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" /></svg> {noteText}</span>
                                    </div> : null;
                                  })()}
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1 sm:gap-2 shrink-0">
                                <div className="flex items-center gap-1">
                                  {order.siparis_tipi === 'devir' && <span className="text-[9px] sm:text-[10px] font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border bg-indigo-100 text-indigo-700 border-indigo-200">Devir</span>}
                                  <span className={`text-[9px] sm:text-[10px] font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border ${statusColors[order.status] || 'bg-gray-100 text-gray-700'}`}>{statusLabels[order.status] || order.status}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <div className="text-[10px] sm:text-xs text-gray-500 whitespace-nowrap">{items.length} kalem / {orderQty} adet</div>
                                  <svg className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Sipariş Detay */}
                          {isExpanded && (() => {
                            // Ürün bazlı indirim hesapla
                            const itemsWithDiscount = items.map(i => {
                              const lineTotal = (parseFloat(i.price) || 0) * (parseInt(i.qty) || 1);
                              let itemDiscount = 0;
                              if (i.discount_type === 'percent') itemDiscount = lineTotal * (parseFloat(i.discount_value) || 0) / 100;
                              else if (i.discount_type === 'amount') itemDiscount = parseFloat(i.discount_value) || 0;
                              return { ...i, lineTotal, itemDiscount, lineFinal: lineTotal - itemDiscount };
                            });
                            const subtotal = itemsWithDiscount.reduce((s, i) => s + i.lineTotal, 0);
                            const itemDiscountsTotal = itemsWithDiscount.reduce((s, i) => s + i.itemDiscount, 0);
                            // Sipariş geneli indirim
                            let orderDiscount = 0;
                            if (order.discount_type === 'percent') orderDiscount = (subtotal - itemDiscountsTotal) * (parseFloat(order.discount_value) || 0) / 100;
                            else if (order.discount_type === 'amount') orderDiscount = parseFloat(order.discount_value) || 0;
                            const totalDiscount = itemDiscountsTotal + orderDiscount;
                            const orderTotal = subtotal - totalDiscount;
                            const payments = order._payments || [];
                            const totalPaid = order._totalPaid || parseFloat(order.payment_amount) || 0;
                            const methodLabels = { havale: 'Havale / EFT', kredi_karti: 'Kredi Kartı', diger: 'Diğer' };
                            // İlk açılışta ödemeleri yükle
                            if (!order._payments && !order._paymentsLoading) {
                              order._paymentsLoading = true;
                              loadOrderPayments(order.id);
                            }
                            return (
                            <div className="border-t border-gray-100">

                              {/* ── Üst Bar: Durum + Tutar + Aksiyonlar ── */}
                              <div className="px-3 sm:px-5 py-3 bg-gray-50/80 border-b border-gray-100">
                                <div className="flex items-center justify-between gap-2">
                                  {/* Sol: Durum + Dropdown */}
                                  <div className="flex items-center gap-2">
                                    {order.status === 'pending' ? (
                                      <button onClick={async () => {
                                        const resp = await authFetch('/api/guest-offers/' + order.id + '/status', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'approved' }) });
                                        if (resp.ok) {
                                          const data = await resp.json();
                                          const updatedItems = data.items || items.map(i => ({...i, status: 'approved'}));
                                          updateAdminOrderLocal(order.id, { status: 'approved', items: JSON.stringify(updatedItems) });
                                        }
                                      }} className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 shadow-sm transition-all">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>
                                        Onayla
                                      </button>
                                    ) : null}
                                    <select className="text-xs font-medium pl-2 pr-6 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 focus:ring-2 focus:ring-blue-200 cursor-pointer appearance-none" style={{backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: 'right 0.3rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1em 1em'}} value={order.status || 'pending'} onChange={async (e) => {
                                      const newStatus = e.target.value;
                                      if (newStatus === order.status) return;
                                      const resp = await authFetch('/api/guest-offers/' + order.id + '/status', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus }) });
                                      if (resp.ok) {
                                        const data = await resp.json();
                                        const updatedItems = data.items || items.map(i => ({...i, status: newStatus}));
                                        updateAdminOrderLocal(order.id, { status: newStatus, items: JSON.stringify(updatedItems) });
                                      }
                                    }}>
                                      <option value="pending">Bekliyor</option>
                                      <option value="approved">Onaylı</option>
                                      <option value="shipping">Teslimat</option>
                                      <option value="delivered">Teslim</option>
                                      <option value="cancelled">İptal</option>
                                    </select>
                                  </div>
                                  {/* Orta: Tutar */}
                                  <div className="flex items-center gap-2 flex-1 justify-center">
                                    {itemDiscountsTotal > 0 && (
                                      <span className="hidden sm:inline text-xs text-gray-400 line-through">{subtotal.toLocaleString('tr-TR', {minimumFractionDigits:2})}</span>
                                    )}
                                    {itemDiscountsTotal > 0 && (
                                      <span className="hidden sm:inline text-xs text-orange-500 font-medium">-{itemDiscountsTotal.toLocaleString('tr-TR', {minimumFractionDigits:2})} <span className="bg-orange-50 px-1 py-0.5 rounded text-[10px]">%{(subtotal > 0 ? (itemDiscountsTotal / subtotal * 100) : 0).toFixed(1)}</span></span>
                                    )}
                                    <span className="text-base sm:text-lg font-bold text-blue-800">{orderTotal.toLocaleString('tr-TR', {minimumFractionDigits:2})} ₺</span>
                                  </div>
                                  {/* Sağ: PDF */}
                                  <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); generateProformaPDF(order, items); }} className="flex items-center gap-1 text-[10px] sm:text-xs font-semibold px-2 sm:px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 active:bg-red-200 transition-all touch-manipulation shrink-0">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m.75 12 3 3m0 0 3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" /></svg>
                                    PDF
                                  </button>
                                </div>
                              </div>

                              {/* ── Sekme Çubuğu ── */}
                              <div className="flex items-center gap-0.5 sm:gap-1 border-b border-gray-200 px-3 sm:px-5 overflow-x-auto">
                                {[
                                  { key: 'kalemler', label: `Ürünler (${items.length})`, labelLg: `Ürün Kalemleri (${items.length})` },
                                  { key: 'sevkiyat', label: 'Sevkiyat', labelLg: 'Sevkiyat' },
                                  { key: 'iade', label: 'İadeler', labelLg: 'İadeler' }
                                ].map(tab => (
                                  <button key={tab.key} onClick={() => {
                                    setAdminOrderTab(tab.key);
                                    if (tab.key === 'sevkiyat' && !sevkiyatlar[order.id]) fetchSevkiyatlar(order.id);
                                    if (tab.key === 'iade' && !iadeler[order.id]) fetchIadeler(order.id);
                                  }} className={`px-3 sm:px-4 py-2 text-sm sm:text-[13px] font-semibold border-b-2 transition-colors whitespace-nowrap ${adminOrderTab === tab.key ? 'border-blue-600 text-blue-700 bg-blue-50/50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
                                    <span className="hidden sm:inline">{tab.labelLg}</span><span className="sm:hidden">{tab.label}</span>
                                  </button>
                                ))}
                              </div>

                              {/* ── Ürün Kalemleri ── */}
                              {adminOrderTab === 'kalemler' && <div className="px-1 sm:px-3 py-2 sm:py-3">
                                {/* Başlık */}
                                <div className="hidden sm:grid grid-cols-[30px_1fr_100px_60px_90px_80px_90px] gap-2 px-3 pb-1.5 border-b border-gray-200 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                  <span>#</span><span>Ürün</span><span>Ölçü</span><span className="text-center">Adet</span><span className="text-right">Fiyat</span><span className="text-center">İndirim</span><span className="text-right">Toplam</span>
                                </div>
                                <div className="flex flex-col divide-y divide-gray-100">
                                  {itemsWithDiscount.map((item, idx) => {
                                    const measureText = formatMeasureDisplay(item.measure);
                                    const lineTotal = (parseFloat(item.price) || 0) * (parseInt(item.qty) || 1);
                                    const discountDisplay = item.discount_type === 'percent' && parseFloat(item.discount_value) > 0
                                      ? '%' + item.discount_value
                                      : item.discount_type === 'amount' && parseFloat(item.discount_value) > 0
                                        ? parseFloat(item.discount_value).toLocaleString('tr-TR', {minimumFractionDigits:2}) + ' ₺'
                                        : '-';
                                    return (
                                      <div key={idx} className={`${item.status === 'cancelled' ? 'opacity-40' : ''}`}>
                                        {/* Desktop satır - tıklanabilir */}
                                        <div className={`hidden sm:grid grid-cols-[30px_1fr_100px_60px_90px_80px_90px] gap-2 items-center px-3 py-2.5 hover:bg-blue-50/40 cursor-pointer transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                                          onClick={() => setEditingItemModal({ orderId: order.id, idx, item })}>
                                          <span className="text-xs font-bold text-gray-400">{idx + 1}</span>
                                          <div className="min-w-0">
                                            <div className="text-[13px] font-semibold text-gray-800 truncate" title={item.name}>{item.name || 'Ürün'}</div>
                                            <div className="flex items-center gap-1.5 text-[11px] text-gray-400 truncate">
                                              {item.barcode && <span className="font-mono">{item.barcode}</span>}
                                              {item.sutCode && <span className="text-emerald-600 font-semibold">{item.sutCode}</span>}
                                            </div>
                                            {item.desc && <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5 mt-0.5 truncate max-w-[320px]" title={item.desc}><span className="font-semibold">Not:</span> {item.desc}</div>}
                                          </div>
                                          <div className="text-[11px] text-blue-700 font-medium truncate" title={measureText}>{measureText || '-'}</div>
                                          <div className="text-center text-[13px] font-bold text-blue-900">{item.qty || 1}</div>
                                          <div className="text-right text-[12px] text-gray-700">{item.price > 0 ? parseFloat(item.price).toLocaleString('tr-TR', {minimumFractionDigits:2}) + ' ₺' : '-'}</div>
                                          <div className="text-center text-[12px] text-orange-600 font-medium">{discountDisplay}</div>
                                          <div className="text-right text-[13px] font-bold text-blue-800">{item.lineFinal > 0 ? item.lineFinal.toLocaleString('tr-TR', {minimumFractionDigits:2}) + ' ₺' : '-'}</div>
                                        </div>
                                        {/* Mobil satır - tıklanabilir */}
                                        <div className={`sm:hidden px-4 py-3 active:bg-blue-50 cursor-pointer transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} ${item.status === 'cancelled' ? 'opacity-50' : ''}`}
                                          onClick={() => setEditingItemModal({ orderId: order.id, idx, item })}>
                                          <div className="flex items-start gap-2.5">
                                            <span className="text-sm font-bold text-gray-400 shrink-0 mt-0.5">{idx + 1}.</span>
                                            <div className="flex-1 min-w-0">
                                              <div className="text-[15px] font-semibold text-gray-800 leading-tight">{item.name || 'Ürün'}</div>
                                              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                                {item.barcode && <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{item.barcode}</span>}
                                                {measureText && <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded font-medium">{measureText}</span>}
                                              </div>
                                              {item.desc && <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5 mt-1"><span className="font-semibold">Not:</span> {item.desc}</div>}
                                              {/* Adet / Fiyat / İndirim - her biri label üstte, değer altta */}
                                              <div className="flex items-start gap-4 mt-2">
                                                <div className="flex flex-col">
                                                  <span className="text-[10px] text-gray-400 uppercase tracking-wide">Adet</span>
                                                  <span className="text-sm font-bold text-blue-900">{item.qty || 1}</span>
                                                </div>
                                                {item.price > 0 && (
                                                  <div className="flex flex-col">
                                                    <span className="text-[10px] text-gray-400 uppercase tracking-wide">Fiyat</span>
                                                    <span className="text-sm font-bold text-gray-800 whitespace-nowrap">{parseFloat(item.price).toLocaleString('tr-TR', {minimumFractionDigits:2})} ₺</span>
                                                  </div>
                                                )}
                                                {item.itemDiscount > 0 && (
                                                  <div className="flex flex-col">
                                                    <span className="text-[10px] text-orange-500 uppercase tracking-wide whitespace-nowrap">İndirim {discountDisplay ? `(${discountDisplay})` : ''}</span>
                                                    <span className="text-sm font-bold text-orange-600 whitespace-nowrap">-{item.itemDiscount.toLocaleString('tr-TR', {minimumFractionDigits:2})} ₺</span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                            <svg className="w-5 h-5 text-gray-300 shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" /></svg>
                                          </div>
                                        </div>
                                        {/* Red sebebi */}
                                        {item.reject_reason && item.status === 'cancelled' && (
                                          <div className="flex items-center gap-2 px-3 py-1.5 bg-rose-50/60 border-t border-rose-100/50 text-xs">
                                            <span className="text-rose-600">Red: <strong>{item.reject_reason}</strong></span>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                {/* İlave Ürün Ekle Butonu */}
                                <div className="mt-3 flex items-center gap-2">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); setAddItemOrderId(order.id); setAddItemModal(true); setAddItemSearch(''); setAddItemSelected(null); setAddItemQty(1); }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-semibold hover:bg-emerald-100 active:bg-emerald-200 transition touch-manipulation"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                                    İlave Ürün Ekle
                                  </button>
                                </div>
                              </div>}

                              {/* ── SEVKİYAT SEKMESİ ── */}
                              {adminOrderTab === 'sevkiyat' && (() => {
                                const orderSevkiyatlar = sevkiyatlar[order.id] || [];
                                const statusLabels = { hazirlaniyor: 'Hazırlanıyor', sevk_edildi: 'Sevk Edildi', teslim_edildi: 'Teslim Edildi', iptal: 'İptal', durduruldu: 'Durduruldu' };
                                const statusCls2 = { hazirlaniyor: 'bg-amber-100 text-amber-700 border-amber-200', sevk_edildi: 'bg-violet-100 text-violet-700 border-violet-200', teslim_edildi: 'bg-emerald-100 text-emerald-700 border-emerald-200', iptal: 'bg-rose-100 text-rose-700 border-rose-200', durduruldu: 'bg-gray-200 text-gray-600 border-gray-300' };
                                return (
                                <div>
                                  {/* Yeni Sevkiyat Oluştur Butonu */}
                                  {!sevkForm && (
                                    <button onClick={() => {
                                      const sevkItems = items.map((it, i) => ({
                                        item_index: i,
                                        name: it.name,
                                        measure: it.measure,
                                        barcode: it.barcode || '',
                                        qty: 0,
                                        max: Math.max(0, (parseInt(it.qty) || 1) - (parseInt(it.shipped_qty) || 0))
                                      })).filter(i => i.max > 0);
                                      if (sevkItems.length === 0) { alert('Sevk edilecek kalem kalmadı'); return; }
                                      setSevkForm({ orderId: order.id, items: sevkItems });
                                      setSevkNotes(''); setSevkTrackingNo(''); setSevkKargoFirmasi('');
                                    }} className="mb-3 px-4 py-2 bg-violet-600 text-white text-[12px] font-semibold rounded-lg hover:bg-violet-700 transition-colors shadow-sm flex items-center gap-2">
                                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                                      Ürün Sevk Et
                                    </button>
                                  )}

                                  {/* Sevkiyat Formu */}
                                  {sevkForm && sevkForm.orderId === order.id && (
                                    <div className="mb-4 p-3 bg-violet-50 border border-violet-200 rounded-lg">
                                      <div className="flex items-center justify-between mb-2">
                                        <h4 className="text-[12px] font-bold text-violet-800">Yeni Sevkiyat</h4>
                                        <button onClick={() => setSevkForm(null)} className="text-gray-400 hover:text-red-500 text-[10px]">Kapat</button>
                                      </div>
                                      <div className="space-y-1.5 mb-3">
                                        <div className="grid grid-cols-[1fr_50px_50px_90px_90px_90px_28px] gap-1 text-[9px] font-semibold text-gray-500 uppercase px-1">
                                          <span>Ürün</span><span className="text-center">Kalan</span><span className="text-center">Miktar</span><span>Lot No</span><span>Ürt. Tarihi</span><span>SKT</span><span title="Seri No" className="text-center">S/N</span>
                                        </div>
                                        {sevkForm.items.map((si, sIdx) => (
                                          <div key={sIdx} className="grid grid-cols-[1fr_50px_50px_90px_90px_90px_28px] gap-1 items-center px-1 py-1 bg-white rounded border border-violet-100">
                                            <div className="min-w-0">
                                              <span className="text-[11px] text-gray-700 truncate block" title={si.name}>{si.name}</span>
                                              {formatMeasureDisplay(si.measure) && <span className="text-[9px] text-blue-600 font-medium truncate block">{formatMeasureDisplay(si.measure)}</span>}
                                            </div>
                                            <span className="text-[11px] font-semibold text-center text-violet-600">{si.max}</span>
                                            <input type="number" min="0" max={si.max} value={si.qty} onChange={(e) => {
                                              const val = Math.min(Math.max(0, parseInt(e.target.value) || 0), si.max);
                                              setSevkForm(prev => ({ ...prev, items: prev.items.map((x, xi) => xi === sIdx ? { ...x, qty: val } : x) }));
                                            }} className="w-full text-center text-[11px] font-bold border border-violet-200 rounded px-1 py-0.5 focus:ring-1 focus:ring-violet-400 focus:border-violet-400" />
                                            <input type="text" placeholder="Lot No *" value={si.lot_no || ''} onChange={(e) => {
                                              setSevkForm(prev => ({ ...prev, items: prev.items.map((x, xi) => xi === sIdx ? { ...x, lot_no: e.target.value } : x) }));
                                            }} className={`w-full text-[10px] border rounded px-1 py-0.5 focus:ring-1 focus:ring-violet-400 focus:border-violet-400 placeholder:text-gray-300 ${si.qty > 0 && !si.lot_no ? 'border-red-300 bg-red-50/30' : 'border-violet-200'}`} />
                                            <input type="date" value={si.uretim_tarihi || ''} onChange={(e) => {
                                              setSevkForm(prev => ({ ...prev, items: prev.items.map((x, xi) => xi === sIdx ? { ...x, uretim_tarihi: e.target.value } : x) }));
                                            }} className={`w-full text-[10px] border rounded px-1 py-0.5 focus:ring-1 focus:ring-violet-400 focus:border-violet-400 text-gray-600 ${si.qty > 0 && !si.uretim_tarihi ? 'border-red-300 bg-red-50/30' : 'border-violet-200'}`} title="Üretim Tarihi" />
                                            <input type="date" value={si.skt || ''} onChange={(e) => {
                                              setSevkForm(prev => ({ ...prev, items: prev.items.map((x, xi) => xi === sIdx ? { ...x, skt: e.target.value } : x) }));
                                            }} className={`w-full text-[10px] border rounded px-1 py-0.5 focus:ring-1 focus:ring-violet-400 focus:border-violet-400 text-gray-600 ${si.qty > 0 && !si.skt ? 'border-red-300 bg-red-50/30' : 'border-violet-200'}`} title="Son Kullanma Tarihi" />
                                            <button onClick={() => {
                                              setSeriNoModal({ sIdx, name: si.name, qty: si.qty || si.max });
                                              setSeriNoInput(''); setSeriNoAutoStart(''); setSeriNoAutoCount('');
                                            }} className={`w-7 h-7 flex items-center justify-center rounded border transition-colors ${(si.seri_nolar && si.seri_nolar.length > 0) ? 'bg-emerald-100 border-emerald-400 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-50 border-violet-200 text-violet-500 hover:bg-violet-100'}`} title={si.seri_nolar && si.seri_nolar.length > 0 ? `${si.seri_nolar.length} seri no girildi` : 'Seri No Gir'}>
                                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" /></svg>
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                      <div className="grid grid-cols-3 gap-2 mb-2">
                                        <select value={sevkKargoFirmasi} onChange={(e) => setSevkKargoFirmasi(e.target.value)} className="text-[11px] border border-violet-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-violet-400 bg-white">
                                          <option value="">Kargo Firması Seçin</option>
                                          <option value="UPS">UPS</option>
                                          <option value="DHL">DHL</option>
                                          <option value="Yurtiçi Kargo">Yurtiçi Kargo</option>
                                          <option value="Aras Kargo">Aras Kargo</option>
                                          <option value="PTT Kargo">PTT Kargo</option>
                                          <option value="MNG Kargo">MNG Kargo</option>
                                          <option value="Sürat Kargo">Sürat Kargo</option>
                                          <option value="TNT">TNT</option>
                                          <option value="FedEx">FedEx</option>
                                          <option value="Diğer">Diğer</option>
                                        </select>
                                        <input type="text" placeholder="Kargo Takip No" value={sevkTrackingNo} onChange={(e) => setSevkTrackingNo(e.target.value)} className="text-[11px] border border-violet-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-violet-400" />
                                        <input type="text" placeholder="Not (opsiyonel)" value={sevkNotes} onChange={(e) => setSevkNotes(e.target.value)} className="text-[11px] border border-violet-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-violet-400" />
                                      </div>
                                      <div className="flex gap-2">
                                        <button onClick={() => handleCreateSevkiyat(order.id)} disabled={sevkLoading || sevkForm.items.every(i => i.qty === 0)} className="px-4 py-1.5 bg-violet-600 text-white text-[11px] font-semibold rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                          {sevkLoading ? 'Oluşturuluyor...' : 'Sevkiyatı Oluştur'}
                                        </button>
                                        <button onClick={() => {
                                          setSevkForm(prev => ({ ...prev, items: prev.items.map(i => ({ ...i, qty: i.max })) }));
                                        }} className="px-3 py-1.5 bg-violet-100 text-violet-700 text-[11px] font-semibold rounded-lg hover:bg-violet-200 transition-colors">Tümünü Seç</button>
                                      </div>
                                    </div>
                                  )}

                                  {/* Seri No Modal */}
                                  {seriNoModal && sevkForm && sevkForm.orderId === order.id && (() => {
                                    const si = sevkForm.items[seriNoModal.sIdx];
                                    if (!si) return null;
                                    const currentSeriNolar = si.seri_nolar || [];
                                    return (
                                      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setSeriNoModal(null)}>
                                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                                          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                                            <div>
                                              <h3 className="text-sm font-bold text-gray-800">Seri No Girişi</h3>
                                              <p className="text-[10px] text-gray-500 mt-0.5 truncate max-w-[280px]" title={seriNoModal.name}>{seriNoModal.name}</p>
                                              {si.barcode && <p className="text-[9px] text-blue-600 font-mono mt-0.5">Barkod: {si.barcode}</p>}
                                              {si.lot_no && <p className="text-[9px] text-violet-600 font-mono">Lot: {si.lot_no}</p>}
                                            </div>
                                            <button onClick={() => setSeriNoModal(null)} className="text-gray-400 hover:text-red-500 transition-colors">
                                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                            </button>
                                          </div>
                                          <div className="px-4 py-3 space-y-3 overflow-y-auto flex-1">
                                            {/* Tek tek seri no ekleme */}
                                            <div>
                                              <label className="text-[10px] font-semibold text-gray-600 uppercase mb-1 block">Tek Seri No Ekle</label>
                                              <div className="flex gap-1.5">
                                                <input type="text" placeholder="Seri numarası girin" value={seriNoInput} onChange={(e) => setSeriNoInput(e.target.value)} onKeyDown={(e) => {
                                                  if (e.key === 'Enter' && seriNoInput.trim()) {
                                                    addSingleSeriNo(seriNoModal.sIdx, si.barcode, si.lot_no, seriNoInput);
                                                  }
                                                }} disabled={seriNoChecking} className="flex-1 text-[11px] border border-gray-300 rounded-lg px-2.5 py-1.5 focus:ring-2 focus:ring-violet-400 focus:border-violet-400 disabled:opacity-50" />
                                                <button onClick={() => addSingleSeriNo(seriNoModal.sIdx, si.barcode, si.lot_no, seriNoInput)} disabled={seriNoChecking} className="px-3 py-1.5 bg-violet-600 text-white text-[11px] font-semibold rounded-lg hover:bg-violet-700 transition-colors whitespace-nowrap disabled:opacity-50">
                                                  {seriNoChecking ? 'Kontrol...' : 'Ekle'}
                                                </button>
                                              </div>
                                            </div>

                                            {/* Otomatik seri no oluşturma */}
                                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                                              <label className="text-[10px] font-semibold text-blue-700 uppercase mb-1.5 block">Otomatik Seri No Oluştur</label>
                                              <div className="grid grid-cols-[1fr_70px_auto] gap-1.5">
                                                <input type="text" placeholder="Başlangıç (örn: SN-001)" value={seriNoAutoStart} onChange={(e) => setSeriNoAutoStart(e.target.value)} disabled={seriNoChecking} className="text-[11px] border border-blue-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 disabled:opacity-50" />
                                                <input type="number" min="1" max="999" placeholder="Adet" value={seriNoAutoCount} onChange={(e) => setSeriNoAutoCount(e.target.value)} disabled={seriNoChecking} className="text-[11px] border border-blue-300 rounded-lg px-2 py-1.5 text-center focus:ring-2 focus:ring-blue-400 focus:border-blue-400 disabled:opacity-50" />
                                                <button disabled={seriNoChecking} onClick={() => {
                                                  if (!seriNoAutoStart.trim() || !seriNoAutoCount) return;
                                                  const count = Math.min(Math.max(1, parseInt(seriNoAutoCount) || 1), 999);
                                                  const start = seriNoAutoStart.trim();
                                                  const match = start.match(/^(.*?)(\d+)$/);
                                                  let generated = [];
                                                  if (match) {
                                                    const prefix = match[1];
                                                    const numStr = match[2];
                                                    const numLen = numStr.length;
                                                    let startNum = parseInt(numStr, 10);
                                                    for (let i = 0; i < count; i++) {
                                                      generated.push(prefix + String(startNum + i).padStart(numLen, '0'));
                                                    }
                                                  } else {
                                                    for (let i = 0; i < count; i++) {
                                                      generated.push(start + '-' + (i + 1));
                                                    }
                                                  }
                                                  addBulkSeriNo(seriNoModal.sIdx, si.barcode, si.lot_no, generated);
                                                }} className="px-3 py-1.5 bg-blue-600 text-white text-[11px] font-semibold rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap disabled:opacity-50">
                                                  {seriNoChecking ? 'Kontrol...' : 'Oluştur'}
                                                </button>
                                              </div>
                                              <p className="text-[9px] text-blue-500 mt-1">Örn: "SN-001" başlangıç, 20 adet → SN-001, SN-002, ..., SN-020</p>
                                            </div>

                                            {/* Mevcut seri no listesi */}
                                            <div>
                                              <div className="flex items-center justify-between mb-1">
                                                <label className="text-[10px] font-semibold text-gray-600 uppercase">Seri No Listesi ({currentSeriNolar.length})</label>
                                                {currentSeriNolar.length > 0 && (
                                                  <button onClick={() => {
                                                    if (window.confirm('Tüm seri numaralarını silmek istediğinize emin misiniz?')) {
                                                      setSevkForm(prev => ({ ...prev, items: prev.items.map((x, xi) => xi === seriNoModal.sIdx ? { ...x, seri_nolar: [] } : x) }));
                                                    }
                                                  }} className="text-[9px] text-rose-500 hover:text-rose-700 font-semibold">Tümünü Sil</button>
                                                )}
                                              </div>
                                              {currentSeriNolar.length === 0 ? (
                                                <p className="text-[10px] text-gray-400 italic py-3 text-center">Henüz seri no eklenmedi</p>
                                              ) : (
                                                <div className="max-h-[200px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                                                  {currentSeriNolar.map((sn, snIdx) => (
                                                    <div key={snIdx} className="flex items-center justify-between px-2.5 py-1.5 hover:bg-gray-50 group">
                                                      <div className="flex items-center gap-2">
                                                        <span className="text-[9px] text-gray-400 font-mono w-5 text-right">{snIdx + 1}.</span>
                                                        <span className="text-[11px] text-gray-700 font-mono">{sn}</span>
                                                      </div>
                                                      <button onClick={() => {
                                                        setSevkForm(prev => ({ ...prev, items: prev.items.map((x, xi) => xi === seriNoModal.sIdx ? { ...x, seri_nolar: (x.seri_nolar || []).filter((_, fi) => fi !== snIdx) } : x) }));
                                                      }} className="opacity-0 group-hover:opacity-100 text-rose-400 hover:text-rose-600 transition-opacity">
                                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                      </button>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                                            <span className="text-[10px] text-gray-500">{currentSeriNolar.length} seri no kayıtlı</span>
                                            <button onClick={() => setSeriNoModal(null)} className="px-4 py-1.5 bg-violet-600 text-white text-[11px] font-semibold rounded-lg hover:bg-violet-700 transition-colors">Tamam</button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  {/* Sevkiyat Geçmişi */}
                                  {orderSevkiyatlar.length === 0 ? (
                                    <p className="text-[11px] text-gray-400 italic">Henüz sevkiyat kaydı bulunmuyor.</p>
                                  ) : (
                                    <div className="space-y-2">
                                      {orderSevkiyatlar.map(s => (
                                        <div key={s.id} className="border border-gray-200 rounded-lg overflow-hidden">
                                          <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                                            <div className="flex items-center gap-2">
                                              <span className={`text-[9px] font-bold px-2 py-0.5 rounded border ${statusCls2[s.status] || 'bg-gray-100 text-gray-500'}`}>{statusLabels[s.status] || s.status}</span>
                                              <span className="text-[10px] text-gray-500">#{s.id}</span>
                                              {s.kargo_firmasi && <span className="text-[9px] font-semibold px-1.5 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded">{s.kargo_firmasi}</span>}
                                              {s.tracking_no && (
                                                <span className="text-[10px] text-blue-600 font-mono flex items-center gap-1">
                                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                                                  {s.tracking_no}
                                                </span>
                                              )}
                                              <span className="text-[10px] text-gray-400">{new Date(s.created_at).toLocaleDateString('tr-TR')} {new Date(s.created_at).toLocaleTimeString('tr-TR', {hour:'2-digit',minute:'2-digit'})}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                              {s.status === 'hazirlaniyor' && (
                                                <>
                                                  <button onClick={() => handleSevkiyatStatusChange(s.id, order.id, 'sevk_edildi')} className="text-[9px] px-2 py-0.5 bg-violet-100 text-violet-700 rounded hover:bg-violet-200 font-semibold border border-violet-200">Sevk Et</button>
                                                  <button onClick={() => handleSevkiyatStatusChange(s.id, order.id, 'iptal')} className="text-[9px] px-2 py-0.5 bg-rose-100 text-rose-600 rounded hover:bg-rose-200 font-semibold border border-rose-200">İptal</button>
                                                </>
                                              )}
                                              {s.status === 'sevk_edildi' && (
                                                <>
                                                  <button onClick={() => handleSevkiyatStatusChange(s.id, order.id, 'teslim_edildi')} className="text-[9px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 font-semibold border border-emerald-200">Teslim Edildi</button>
                                                  <button onClick={() => handleSevkiyatStatusChange(s.id, order.id, 'durduruldu')} className="text-[9px] px-2 py-0.5 bg-gray-200 text-gray-600 rounded hover:bg-gray-300 font-semibold border border-gray-300">Durdur</button>
                                                  <button onClick={() => handleSevkiyatStatusChange(s.id, order.id, 'iptal')} className="text-[9px] px-2 py-0.5 bg-rose-100 text-rose-600 rounded hover:bg-rose-200 font-semibold border border-rose-200">İptal</button>
                                                </>
                                              )}
                                              {s.status === 'durduruldu' && (
                                                <button onClick={() => handleSevkiyatStatusChange(s.id, order.id, 'sevk_edildi')} className="text-[9px] px-2 py-0.5 bg-violet-100 text-violet-700 rounded hover:bg-violet-200 font-semibold border border-violet-200">Devam Et</button>
                                              )}
                                            </div>
                                          </div>
                                          {s.notes && <div className="px-3 py-1 text-[10px] text-gray-500 bg-gray-50/50 border-t border-gray-100 italic">{s.notes}</div>}
                                          <div className="px-3 py-1">
                                            {(s.kalemler || []).map((k, ki) => {
                                              const origItem = items[k.item_index];
                                              const seriNolar = k.seri_nolar ? (typeof k.seri_nolar === 'string' ? JSON.parse(k.seri_nolar) : k.seri_nolar) : [];
                                              return (
                                                <div key={ki} className="py-1 text-[10px] border-b border-gray-50 last:border-0">
                                                  <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
                                                      <span className="text-gray-700 truncate">{origItem ? origItem.name : `Kalem #${k.item_index + 1}`}</span>
                                                      {k.lot_no && <span className="text-[9px] font-mono px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded flex-shrink-0" title="Lot No">LOT: {k.lot_no}</span>}
                                                      {k.uretim_tarihi && <span className="text-[9px] font-mono px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded flex-shrink-0" title="Üretim Tarihi">ÜRT: {new Date(k.uretim_tarihi).toLocaleDateString('tr-TR')}</span>}
                                                      {k.skt && <span className="text-[9px] font-mono px-1.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded flex-shrink-0" title="Son Kullanma Tarihi">SKT: {new Date(k.skt).toLocaleDateString('tr-TR')}</span>}
                                                    </div>
                                                    <span className="font-bold text-violet-700 flex-shrink-0 ml-2">{k.qty} adet</span>
                                                  </div>
                                                  {seriNolar.length > 0 && (
                                                    <details className="mt-1">
                                                      <summary className="text-[9px] text-violet-600 cursor-pointer hover:text-violet-800 font-semibold">{seriNolar.length} Seri No</summary>
                                                      <div className="mt-1 flex flex-wrap gap-1 pl-2">
                                                        {seriNolar.map((sn, snI) => (
                                                          <span key={snI} className="text-[8px] font-mono px-1.5 py-0.5 bg-violet-50 text-violet-700 border border-violet-200 rounded">{sn}</span>
                                                        ))}
                                                      </div>
                                                    </details>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>);
                              })()}

                              {/* ── İADE SEKMESİ ── */}
                              {adminOrderTab === 'iade' && (() => {
                                const orderIadeler = iadeler[order.id] || [];
                                const iadeStatusLabels = { beklemede: 'Beklemede', onaylandi: 'Onaylandı', reddedildi: 'Reddedildi' };
                                const iadeStatusCls = { beklemede: 'bg-amber-100 text-amber-700 border-amber-200', onaylandi: 'bg-emerald-100 text-emerald-700 border-emerald-200', reddedildi: 'bg-rose-100 text-rose-700 border-rose-200' };
                                // Sadece sevk edilen kalemlerden iade yapılabilir
                                const returnableItems = items.map((it, i) => ({
                                  index: i,
                                  name: it.name,
                                  maxReturnable: Math.max(0, (parseInt(it.shipped_qty) || 0) - (parseInt(it.returned_qty) || 0))
                                })).filter(ri => ri.maxReturnable > 0);
                                return (
                                <div>
                                  {/* İade Talebi Oluştur */}
                                  {!iadeForm && returnableItems.length > 0 && (
                                    <button onClick={() => setIadeForm({ orderId: order.id, item_index: returnableItems[0].index, qty: 1, reason: '' })} className="mb-3 px-4 py-2 bg-orange-500 text-white text-[12px] font-semibold rounded-lg hover:bg-orange-600 transition-colors shadow-sm flex items-center gap-2">
                                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                                      İade Talebi Oluştur
                                    </button>
                                  )}
                                  {returnableItems.length === 0 && !iadeForm && <p className="text-[11px] text-gray-400 mb-3 italic">İade edilebilir kalem yok (henüz sevk yapılmamış).</p>}

                                  {/* İade Formu */}
                                  {iadeForm && iadeForm.orderId === order.id && (
                                    <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                      <div className="flex items-center justify-between mb-2">
                                        <h4 className="text-[12px] font-bold text-orange-800">Yeni İade Talebi</h4>
                                        <button onClick={() => setIadeForm(null)} className="text-gray-400 hover:text-red-500 text-[10px]">Kapat</button>
                                      </div>
                                      <div className="grid grid-cols-[1fr_80px] gap-2 mb-2">
                                        <select value={iadeForm.item_index} onChange={(e) => setIadeForm(prev => ({ ...prev, item_index: parseInt(e.target.value), qty: 1 }))} className="text-[11px] border border-orange-200 rounded px-2 py-1.5 bg-white">
                                          {returnableItems.map(ri => <option key={ri.index} value={ri.index}>{ri.name} (maks: {ri.maxReturnable})</option>)}
                                        </select>
                                        <input type="number" min="1" max={returnableItems.find(ri => ri.index === iadeForm.item_index)?.maxReturnable || 1} value={iadeForm.qty} onChange={(e) => {
                                          const mx = returnableItems.find(ri => ri.index === iadeForm.item_index)?.maxReturnable || 1;
                                          setIadeForm(prev => ({ ...prev, qty: Math.min(Math.max(1, parseInt(e.target.value) || 1), mx) }));
                                        }} className="text-[11px] text-center border border-orange-200 rounded px-2 py-1.5" />
                                      </div>
                                      <input type="text" placeholder="İade sebebi..." value={iadeForm.reason} onChange={(e) => setIadeForm(prev => ({ ...prev, reason: e.target.value }))} className="w-full text-[11px] border border-orange-200 rounded px-2 py-1.5 mb-2" />
                                      <button onClick={() => handleCreateIade(order.id)} disabled={iadeLoading} className="px-4 py-1.5 bg-orange-500 text-white text-[11px] font-semibold rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors">
                                        {iadeLoading ? 'Oluşturuluyor...' : 'İade Talebini Gönder'}
                                      </button>
                                    </div>
                                  )}

                                  {/* İade Geçmişi */}
                                  {orderIadeler.length === 0 ? (
                                    <p className="text-[11px] text-gray-400 italic">Henüz iade talebi bulunmuyor.</p>
                                  ) : (
                                    <div className="space-y-1.5">
                                      {orderIadeler.map(iade => {
                                        const origItem = items[iade.item_index];
                                        return (
                                          <div key={iade.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-200 hover:border-orange-200 transition-colors">
                                            <div className="flex items-center gap-2">
                                              <span className={`text-[9px] font-bold px-2 py-0.5 rounded border ${iadeStatusCls[iade.status] || 'bg-gray-100'}`}>{iadeStatusLabels[iade.status] || iade.status}</span>
                                              <span className="text-[11px] text-gray-700">{origItem ? origItem.name : `Kalem #${iade.item_index + 1}`}</span>
                                              <span className="text-[11px] font-bold text-orange-600">{iade.qty} adet</span>
                                              {iade.reason && <span className="text-[10px] text-gray-400 italic truncate max-w-[200px]">{iade.reason}</span>}
                                              <span className="text-[9px] text-gray-400">{new Date(iade.created_at).toLocaleDateString('tr-TR')}</span>
                                              <span className="text-[8px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{iade.requested_by === 'admin' ? 'Admin' : 'Firma'}</span>
                                            </div>
                                            {iade.status === 'beklemede' && user?.role === 'admin' && (
                                              <div className="flex gap-1">
                                                <button onClick={() => handleIadeStatusChange(iade.id, order.id, 'onaylandi')} className="text-[9px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 font-semibold border border-emerald-200">Onayla</button>
                                                <button onClick={() => handleIadeStatusChange(iade.id, order.id, 'reddedildi')} className="text-[9px] px-2 py-0.5 bg-rose-100 text-rose-600 rounded hover:bg-rose-200 font-semibold border border-rose-200">Reddet</button>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>);
                              })()}

                            </div>);
                          })()}
                        </div>
                      );
                    })}
                  </div>

                  {/* Sayfalama */}
                  {adminOrdersTotal > 30 && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-sm text-gray-500">Sayfa {adminOrdersPage} / {Math.ceil(adminOrdersTotal / 30)} ({adminOrdersTotal} sipariş)</div>
                      <div className="flex gap-2">
                        <button disabled={adminOrdersPage <= 1} onClick={() => setAdminOrdersPage(p => Math.max(1, p - 1))} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Önceki</button>
                        <button disabled={adminOrdersPage >= Math.ceil(adminOrdersTotal / 30)} onClick={() => setAdminOrdersPage(p => p + 1)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Sonraki</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* === BAYİ STOK YÖNETİMİ GÖRÜNÜMÜ === */}
          {mainView === 'stokYonetimi' && user && user?.role !== 'admin' && selectedFirma && (
            <div data-tour="stok-yonetimi-page">
              <div className="flex items-center justify-between mb-6" data-tour="stok-baslik">
                <h1 className="text-2xl font-bold text-blue-900">Stok Yönetimi</h1>
                <button onClick={() => fetchBayiStokData(selectedFirma.id)} className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" /></svg>
                  Yenile
                </button>
              </div>

              {bayiStokLoading ? (
                <div className="text-center py-12 text-gray-400">Yükleniyor...</div>
              ) : (
                <>
                  {/* Teslim Bekleyen Sevkiyatlar */}
                  {bayiTeslimBekleyen.length > 0 && (
                    <div className="mb-6" data-tour="stok-teslim-bekleyen">
                      <h2 className="text-sm font-bold text-amber-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                        Teslim Bekleyen Gönderiler ({bayiTeslimBekleyen.length})
                      </h2>
                      <div className="space-y-3">
                        {bayiTeslimBekleyen.map(s => (
                          <div key={s.id} className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded">Sevkiyat #{s.id}</span>
                                {s.kargo_firmasi && <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200">{s.kargo_firmasi}</span>}
                                {s.tracking_no && <span className="text-xs text-blue-600 font-mono">{s.tracking_no}</span>}
                                <span className="text-xs text-gray-500">{new Date(s.created_at).toLocaleDateString('tr-TR')}</span>
                              </div>
                              <button onClick={() => handleTeslimAl(s.id, null)} className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                Teslim Aldım
                              </button>
                            </div>
                            <div className="space-y-1">
                              {(s.kalemler || []).map((k, ki) => (
                                <div key={ki} className="flex items-center justify-between text-sm bg-white rounded px-3 py-1.5 border border-amber-100">
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <span className="text-gray-800 truncate">{k.product_name}</span>
                                    {k.lot_no && <span className="text-[9px] font-mono px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded">LOT: {k.lot_no}</span>}
                                  </div>
                                  <span className="font-bold text-amber-700 ml-2">{k.qty} adet</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Stok Durumu Tablosu */}
                  <div className="mb-6" data-tour="stok-durumu">
                    <h2 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                      Stok Durumu
                    </h2>
                    {bayiStok.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-xl border border-gray-200">
                        <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                        Henüz stoğunuzda ürün bulunmuyor. Sevk edilen ürünleri teslim alarak stoğunuza ekleyebilirsiniz.
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="hidden sm:grid grid-cols-[1fr_100px_80px_80px_80px_80px_70px] gap-2 px-4 py-2 bg-gray-50 border-b text-[10px] font-semibold text-gray-500 uppercase">
                          <span>Ürün</span><span>Barkod</span><span className="text-center">Lot No</span><span className="text-center">Teslim</span><span className="text-center">Dağıtılan</span><span className="text-center">Kalan</span><span></span>
                        </div>
                        {bayiStok.map((s, sIdx) => (
                          <div key={sIdx} className={`border-b border-gray-100 last:border-0 ${s.kalan === 0 ? 'bg-emerald-50/30' : ''}`}>
                            <div className="grid grid-cols-[1fr_100px_80px_80px_80px_80px_70px] gap-2 items-center px-4 py-2.5">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-800 truncate">{s.product_name}</div>
                                {s.measure && <div className="text-[10px] text-blue-600">{formatMeasureDisplay(s.measure)}</div>}
                                <div className="flex gap-1 mt-0.5">
                                  {s.uretim_tarihi && <span className="text-[8px] font-mono px-1 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 rounded">ÜRT: {new Date(s.uretim_tarihi).toLocaleDateString('tr-TR')}</span>}
                                  {s.skt && <span className="text-[8px] font-mono px-1 py-0.5 bg-rose-50 text-rose-600 border border-rose-200 rounded">SKT: {new Date(s.skt).toLocaleDateString('tr-TR')}</span>}
                                </div>
                              </div>
                              <span className="text-[10px] font-mono text-gray-500 truncate">{s.barcode}</span>
                              <span className="text-xs text-center text-gray-600">{s.lot_no || '-'}</span>
                              <span className="text-xs text-center font-semibold text-blue-700">{s.toplam_teslim}</span>
                              <span className="text-xs text-center font-semibold text-violet-700">{s.toplam_dagitim}</span>
                              <span className={`text-xs text-center font-bold ${s.kalan === 0 ? 'text-emerald-600' : s.kalan > 0 ? 'text-red-600' : 'text-gray-400'}`}>{s.kalan}</span>
                              <div className="flex justify-end">
                                {s.kalan > 0 && (
                                  <button onClick={() => {
                                    setDagitimModal(s);
                                    setDagitimForm({ il: '', hastane_tipi: '', hastane_kurum: '', operasyon_tarihi: '', seri_nolar: [], operasyon_notu: '' });
                                    setHastaneListesi([]);
                                  }} className="px-2 py-1 bg-violet-600 text-white text-[10px] font-semibold rounded-lg hover:bg-violet-700 transition-colors">
                                    Dağıt
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Dağıtım Geçmişi */}
                  {bayiDagitimlar.length > 0 && (
                    <div data-tour="stok-dagitim-gecmisi">
                      <h2 className="text-sm font-bold text-violet-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        Dağıtım Geçmişi ({bayiDagitimlar.length})
                      </h2>
                      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="hidden sm:grid grid-cols-[1fr_180px_80px_80px] gap-2 px-4 py-2 bg-gray-50 border-b text-[10px] font-semibold text-gray-500 uppercase">
                          <span>Ürün</span><span>Hastane / İl</span><span className="text-center">Tarih</span><span className="text-center">Adet</span>
                        </div>
                        {bayiDagitimlar.slice(0, 50).map((d, di) => (
                          <div key={di} className="grid grid-cols-[1fr_180px_80px_80px] gap-2 items-center px-4 py-2 border-b border-gray-50 last:border-0 hover:bg-gray-50/50 text-xs">
                            <div className="min-w-0">
                              <span className="text-gray-800 truncate block">{d.product_name}</span>
                              {d.seri_no && <span className="text-[9px] font-mono text-violet-600">S/N: {d.seri_no}</span>}
                              {d.lot_no && <span className="text-[9px] font-mono text-blue-600 ml-2">LOT: {d.lot_no}</span>}
                            </div>
                            <div className="min-w-0">
                              <span className="text-gray-700 truncate block">{d.hastane_kurum}</span>
                              <span className="text-[9px] text-gray-400">{[d.il, d.hastane_tipi].filter(Boolean).join(' - ') || ''}</span>
                            </div>
                            <span className="text-center text-gray-500">{d.operasyon_tarihi ? new Date(d.operasyon_tarihi).toLocaleDateString('tr-TR') : '-'}</span>
                            <span className="text-center font-bold text-violet-700">{d.qty}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Dağıtım Modal */}
              {dagitimModal && (() => {
                const availableSeriNolar = (dagitimModal.seri_nolar || []).filter(sn => !(dagitimModal.dagitilan_seri_nolar || []).includes(sn));
                const selectedCount = (dagitimForm.seri_nolar || []).length;
                const hastaneTipleri = ['Devlet', 'Özel', 'Üniversite', 'Şehir'];
                const filteredHastaneler = hastaneListesi.filter(h => {
                  if (dagitimForm.hastane_tipi && h.hastane_tipi !== dagitimForm.hastane_tipi) return false;
                  return true;
                });
                // Hastane tipine göre grupla
                const grouped = {};
                filteredHastaneler.forEach(h => { if (!grouped[h.hastane_tipi]) grouped[h.hastane_tipi] = []; grouped[h.hastane_tipi].push(h); });
                const tipColors = { 'Devlet': 'text-blue-700 bg-blue-50', 'Özel': 'text-emerald-700 bg-emerald-50', 'Üniversite': 'text-violet-700 bg-violet-50', 'Şehir': 'text-amber-700 bg-amber-50' };
                return (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setDagitimModal(null)}>
                  <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                    <div className="px-5 py-4 border-b border-gray-200">
                      <h3 className="text-base font-bold text-gray-800">Ürün Dağıtımı</h3>
                      <p className="text-xs text-gray-500 mt-1 truncate">{dagitimModal.product_name}</p>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        {dagitimModal.barcode && <span className="text-[9px] font-mono text-blue-600">Barkod: {dagitimModal.barcode}</span>}
                        {dagitimModal.lot_no && <span className="text-[9px] font-mono text-violet-600">Lot: {dagitimModal.lot_no}</span>}
                        <span className="text-[9px] font-bold text-emerald-600">Kalan: {dagitimModal.kalan}</span>
                        {selectedCount > 0 && <span className="text-[9px] font-bold text-violet-600">Seçili: {selectedCount} adet</span>}
                      </div>
                    </div>
                    <div className="px-5 py-4 space-y-3">
                      {/* İl Seçimi */}
                      <div>
                        <label className="text-[10px] font-semibold text-gray-600 uppercase block mb-1">İl *</label>
                        <select value={dagitimForm.il} onChange={(e) => {
                          const il = e.target.value;
                          setDagitimForm(p => ({ ...p, il, hastane_tipi: '', hastane_kurum: '' }));
                          setHastaneListesi([]);
                          if (il) fetchHastaneler(il);
                        }} className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-violet-400 focus:border-violet-400 bg-white">
                          <option value="">İl seçin...</option>
                          {hastaneIller.map(il => <option key={il} value={il}>{il}</option>)}
                        </select>
                      </div>
                      {/* Hastane Tipi Filtreleme */}
                      {dagitimForm.il && (
                        <div>
                          <label className="text-[10px] font-semibold text-gray-600 uppercase block mb-1">Hastane Tipi</label>
                          <div className="flex gap-1.5 flex-wrap">
                            <button onClick={() => setDagitimForm(p => ({ ...p, hastane_tipi: '', hastane_kurum: '' }))} className={`px-2.5 py-1 text-[10px] font-semibold rounded-lg border transition-colors ${!dagitimForm.hastane_tipi ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>Tümü</button>
                            {hastaneTipleri.map(t => (
                              <button key={t} onClick={() => setDagitimForm(p => ({ ...p, hastane_tipi: t, hastane_kurum: '' }))} className={`px-2.5 py-1 text-[10px] font-semibold rounded-lg border transition-colors ${dagitimForm.hastane_tipi === t ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>{t}</button>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Hastane Seçimi */}
                      {dagitimForm.il && (
                        <div>
                          <label className="text-[10px] font-semibold text-gray-600 uppercase block mb-1">Hastane / Kurum *</label>
                          {filteredHastaneler.length > 0 ? (
                            <select value={dagitimForm.hastane_kurum} onChange={(e) => setDagitimForm(p => ({ ...p, hastane_kurum: e.target.value }))} className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-violet-400 focus:border-violet-400 bg-white">
                              <option value="">Hastane seçin...</option>
                              {Object.entries(grouped).map(([tipi, hastaneler]) => (
                                <optgroup key={tipi} label={`── ${tipi} Hastaneleri ──`}>
                                  {hastaneler.map(h => <option key={h.id} value={h.hastane_adi}>{h.hastane_adi}</option>)}
                                </optgroup>
                              ))}
                            </select>
                          ) : (
                            <input type="text" value={dagitimForm.hastane_kurum} onChange={(e) => setDagitimForm(p => ({ ...p, hastane_kurum: e.target.value }))} placeholder="Hastane veya kurum adı yazın..." className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-violet-400 focus:border-violet-400" />
                          )}
                          {dagitimForm.hastane_kurum && (() => {
                            const selH = filteredHastaneler.find(h => h.hastane_adi === dagitimForm.hastane_kurum);
                            return selH ? <span className={`inline-block mt-1 text-[9px] font-semibold px-2 py-0.5 rounded ${tipColors[selH.hastane_tipi] || 'text-gray-600 bg-gray-50'}`}>{selH.hastane_tipi}</span> : null;
                          })()}
                        </div>
                      )}
                      {/* Operasyon Tarihi */}
                      <div>
                        <label className="text-[10px] font-semibold text-gray-600 uppercase block mb-1">Operasyon Tarihi *</label>
                        <input type="date" value={dagitimForm.operasyon_tarihi} onChange={(e) => setDagitimForm(p => ({ ...p, operasyon_tarihi: e.target.value }))} className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-violet-400 focus:border-violet-400" />
                      </div>
                      {/* Seri No Seçimi */}
                      {availableSeriNolar.length > 0 && (
                        <div>
                          <label className="text-[10px] font-semibold text-gray-600 uppercase block mb-1">
                            Dağıtılacak Seri Numaraları * <span className="text-violet-600">({selectedCount} / {availableSeriNolar.length} seçili)</span>
                          </label>
                          <div className="flex gap-1.5 mb-1.5">
                            <button onClick={() => setDagitimForm(p => ({ ...p, seri_nolar: [...availableSeriNolar] }))} className="text-[9px] text-blue-600 hover:text-blue-800 font-medium">Tümünü Seç</button>
                            <span className="text-gray-300">|</span>
                            <button onClick={() => setDagitimForm(p => ({ ...p, seri_nolar: [] }))} className="text-[9px] text-red-600 hover:text-red-800 font-medium">Temizle</button>
                          </div>
                          <div className="max-h-[140px] overflow-y-auto border border-gray-200 rounded-lg p-1.5 grid grid-cols-2 gap-0.5">
                            {availableSeriNolar.map((sn, snI) => {
                              const isChecked = (dagitimForm.seri_nolar || []).includes(sn);
                              return (
                                <label key={snI} className={`flex items-center gap-1.5 text-[11px] cursor-pointer px-2 py-1 rounded transition-colors ${isChecked ? 'bg-violet-50 border border-violet-200' : 'hover:bg-gray-50 border border-transparent'}`}>
                                  <input type="checkbox" checked={isChecked} onChange={(e) => {
                                    setDagitimForm(p => {
                                      const current = [...(p.seri_nolar || [])];
                                      if (e.target.checked) { current.push(sn); } else { const idx = current.indexOf(sn); if (idx >= 0) current.splice(idx, 1); }
                                      return { ...p, seri_nolar: current };
                                    });
                                  }} className="rounded border-gray-300 text-violet-600 focus:ring-violet-400 w-3.5 h-3.5" />
                                  <span className={`font-mono ${isChecked ? 'text-violet-700 font-semibold' : 'text-gray-600'}`}>{sn}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {/* Lot No (readonly) */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[10px] font-semibold text-gray-600 uppercase block mb-1">Lot No</label>
                          <input type="text" value={dagitimModal.lot_no || ''} readOnly className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-gray-600 uppercase block mb-1">Miktar</label>
                          <input type="text" value={selectedCount > 0 ? `${selectedCount} adet (seri no bazlı)` : `${dagitimModal.kalan} kalan`} readOnly className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
                        </div>
                      </div>
                      {/* Operasyon Notu */}
                      <div>
                        <label className="text-[10px] font-semibold text-gray-600 uppercase block mb-1">Operasyon Notu</label>
                        <textarea value={dagitimForm.operasyon_notu} onChange={(e) => setDagitimForm(p => ({ ...p, operasyon_notu: e.target.value }))} placeholder="Ek notlar..." rows={2} className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-violet-400 focus:border-violet-400 resize-none" />
                      </div>
                    </div>
                    <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between">
                      <div className="text-[10px] text-gray-400">
                        {selectedCount > 0 && <span className="text-violet-600 font-semibold">{selectedCount} seri no seçildi</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => { setDagitimModal(null); setHastaneListesi([]); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium">İptal</button>
                        <button onClick={handleDagitimKaydet} disabled={dagitimLoading || !dagitimForm.hastane_kurum || !dagitimForm.operasyon_tarihi} className="px-5 py-2 bg-violet-600 text-white text-sm font-semibold rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-colors">
                          {dagitimLoading ? 'Kaydediliyor...' : `Dağıtımı Kaydet${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })()}

              {/* ── UTS İşlemleri Bölümü ── */}
              {utsDurum?.kayitli && (
                <div className="mt-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <svg className="w-4 h-4 text-emerald-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m9.068-9.068a4.5 4.5 0 0 1 6.364 6.364l-4.5 4.5a4.5 4.5 0 0 1-7.244-1.242" /></svg>
                    </div>
                    <h2 className="text-lg font-bold text-gray-800">UTS İşlemleri</h2>
                    <span className="ml-auto text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-600 font-semibold">{utsDurum.is_test ? 'TEST' : 'GERÇEK'}</span>
                  </div>

                  {/* UTS Sekmeleri */}
                  <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 overflow-x-auto">
                    {[
                      { key: 'bekleyen', label: 'Alma Bekleyenler', icon: '📥' },
                      { key: 'bildirim', label: 'Bildirim Gönder', icon: '📤' },
                      { key: 'sorgulama', label: 'Ürün Sorgula', icon: '🔍' },
                      { key: 'gecmis', label: 'İşlem Geçmişi', icon: '📋' },
                    ].map(tab => (
                      <button key={tab.key} onClick={() => {
                        setUtsTab(tab.key);
                        if (tab.key === 'bekleyen') fetchUtsBekleyenAlmalar(selectedFirma.id);
                        if (tab.key === 'gecmis') fetchUtsBildirimGecmisi(selectedFirma.id);
                      }} className={`flex-1 min-w-[100px] px-3 py-2 rounded-lg text-xs font-semibold transition whitespace-nowrap ${utsTab === tab.key ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                        <span className="mr-1">{tab.icon}</span>{tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Alma Bekleyenler */}
                  {utsTab === 'bekleyen' && (
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-700 text-sm">UTS Alma Bekleyen Ürünler</h3>
                        <button onClick={() => fetchUtsBekleyenAlmalar(selectedFirma.id)} className="text-xs text-blue-600 hover:text-blue-800">Yenile</button>
                      </div>
                      {utsBekleyenAlmalar === null ? (
                        <div className="text-center py-8 text-gray-400 text-sm">Yükleniyor...</div>
                      ) : (
                        <div>
                          {Array.isArray(utsBekleyenAlmalar?.sonucListesi) && utsBekleyenAlmalar.sonucListesi.length > 0 ? (
                            <div className="space-y-2">
                              {utsBekleyenAlmalar.sonucListesi.map((item, i) => (
                                <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-800 truncate">{item.urunAdi || item.UNO || 'Ürün'}</p>
                                    <p className="text-[10px] text-gray-400">SNO: {item.SNO || '-'} | LNO: {item.LNO || '-'} | Lot: {item.LOT || '-'}</p>
                                    <p className="text-[10px] text-gray-400">Gönderen: {item.gonderenKurumAdi || item.GKK || '-'}</p>
                                  </div>
                                  <button onClick={async () => {
                                    if (!window.confirm('Bu ürünü UTS üzerinden teslim almak istiyor musunuz?')) return;
                                    try {
                                      const resp = await authFetch('/api/uts/bildirim', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ firma_id: selectedFirma.id, bildirim_tipi: 'alma', data: { VBI: item.VBI || item.bildirimId, ADT: item.ADT || 1, GKK: item.GKK, UNO: item.UNO, LNO: item.LNO, SNO: item.SNO } }) });
                                      const r = await resp.json();
                                      if (r.success) { alert('Alma bildirimi başarılı!'); fetchUtsBekleyenAlmalar(selectedFirma.id); } else { alert('Hata: ' + (r.error || JSON.stringify(r.data))); }
                                    } catch (e) { alert('Hata: ' + e.message); }
                                  }} className="px-3 py-1.5 bg-emerald-600 text-white text-[10px] font-semibold rounded-lg hover:bg-emerald-700 transition whitespace-nowrap">
                                    Teslim Al
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-400 text-sm">
                              {typeof utsBekleyenAlmalar === 'object' && utsBekleyenAlmalar?.mesaj ? utsBekleyenAlmalar.mesaj : 'Bekleyen alma bildirimi yok'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bildirim Gönder */}
                  {utsTab === 'bildirim' && (
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                      <h3 className="font-semibold text-gray-700 text-sm mb-3">UTS Bildirim Gönder</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Bildirim Tipi</label>
                          <select value={utsBildirimTipi} onChange={e => { setUtsBildirimTipi(e.target.value); setUtsBildirimForm({}); }} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                            <option value="">Seçiniz...</option>
                            {utsBildirimTipleri.filter(t => t.bayi).map(t => (
                              <option key={t.key} value={t.key}>{t.label}</option>
                            ))}
                          </select>
                        </div>

                        {utsBildirimTipi && (
                          <>
                            {['verme', 'alma', 'kullanim', 'tuketiciyeVerme', 'tanimsizYereVerme', 'geciciKullanimaVerme'].includes(utsBildirimTipi) && (
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-[10px] font-semibold text-gray-500 mb-1">UNO (Ürün Numarası)</label>
                                  <input type="text" value={utsBildirimForm.UNO || ''} onChange={e => setUtsBildirimForm(f => ({ ...f, UNO: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-semibold text-gray-500 mb-1">LNO (Lot Numarası)</label>
                                  <input type="text" value={utsBildirimForm.LNO || ''} onChange={e => setUtsBildirimForm(f => ({ ...f, LNO: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-semibold text-gray-500 mb-1">SNO (Seri Numarası)</label>
                                  <input type="text" value={utsBildirimForm.SNO || ''} onChange={e => setUtsBildirimForm(f => ({ ...f, SNO: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-semibold text-gray-500 mb-1">ADT (Adet)</label>
                                  <input type="number" value={utsBildirimForm.ADT || ''} onChange={e => setUtsBildirimForm(f => ({ ...f, ADT: parseInt(e.target.value) || '' }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                              </div>
                            )}

                            {['verme', 'tanimsizYereVerme'].includes(utsBildirimTipi) && (
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-[10px] font-semibold text-gray-500 mb-1">KUN (Karşı Kurum No)</label>
                                  <input type="text" value={utsBildirimForm.KUN || ''} onChange={e => setUtsBildirimForm(f => ({ ...f, KUN: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-semibold text-gray-500 mb-1">BNO (Belge No)</label>
                                  <input type="text" value={utsBildirimForm.BNO || ''} onChange={e => setUtsBildirimForm(f => ({ ...f, BNO: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                              </div>
                            )}

                            {utsBildirimTipi === 'kullanim' && (
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-[10px] font-semibold text-gray-500 mb-1">HAA (Hastane Adı)</label>
                                  <input type="text" value={utsBildirimForm.HAA || ''} onChange={e => setUtsBildirimForm(f => ({ ...f, HAA: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-semibold text-gray-500 mb-1">HAS (Hasta TC/Pasaport)</label>
                                  <input type="text" value={utsBildirimForm.HAS || ''} onChange={e => setUtsBildirimForm(f => ({ ...f, HAS: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-semibold text-gray-500 mb-1">TKN (T.C. Kimlik No)</label>
                                  <input type="text" value={utsBildirimForm.TKN || ''} onChange={e => setUtsBildirimForm(f => ({ ...f, TKN: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-semibold text-gray-500 mb-1">YKN (Yabancı Kimlik No)</label>
                                  <input type="text" value={utsBildirimForm.YKN || ''} onChange={e => setUtsBildirimForm(f => ({ ...f, YKN: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-semibold text-gray-500 mb-1">DTA (Doktor Adı)</label>
                                  <input type="text" value={utsBildirimForm.DTA || ''} onChange={e => setUtsBildirimForm(f => ({ ...f, DTA: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-semibold text-gray-500 mb-1">TUR (İşlem Türü)</label>
                                  <input type="text" value={utsBildirimForm.TUR || ''} onChange={e => setUtsBildirimForm(f => ({ ...f, TUR: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                              </div>
                            )}

                            {['hekZayiat', 'imhaBertaraf'].includes(utsBildirimTipi) && (
                              <div>
                                <label className="block text-[10px] font-semibold text-gray-500 mb-1">GIT (Gerekçe/Açıklama)</label>
                                <textarea value={utsBildirimForm.GIT || ''} onChange={e => setUtsBildirimForm(f => ({ ...f, GIT: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                              </div>
                            )}

                            <button onClick={handleUtsBildirimGonder} disabled={utsLoading} className="w-full px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition disabled:opacity-50">
                              {utsLoading ? 'Gönderiliyor...' : 'Bildirimi Gönder'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Ürün Sorgulama */}
                  {utsTab === 'sorgulama' && (
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                      <h3 className="font-semibold text-gray-700 text-sm mb-3">UTS Ürün Sorgulama</h3>
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-500 mb-1">UNO</label>
                          <input type="text" value={utsSorguForm.uno} onChange={e => setUtsSorguForm(f => ({ ...f, uno: e.target.value }))} placeholder="Ürün No" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-500 mb-1">LNO</label>
                          <input type="text" value={utsSorguForm.lno} onChange={e => setUtsSorguForm(f => ({ ...f, lno: e.target.value }))} placeholder="Lot No" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-500 mb-1">SNO</label>
                          <input type="text" value={utsSorguForm.sno} onChange={e => setUtsSorguForm(f => ({ ...f, sno: e.target.value }))} placeholder="Seri No" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                        </div>
                      </div>
                      <button onClick={handleUtsSorgula} disabled={utsLoading} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50 mb-3">
                        {utsLoading ? 'Sorgulanıyor...' : 'Sorgula'}
                      </button>
                      {utsSorguSonuc && (
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <pre className="text-[10px] text-gray-700 whitespace-pre-wrap overflow-x-auto max-h-60">{JSON.stringify(utsSorguSonuc, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}

                  {/* İşlem Geçmişi */}
                  {utsTab === 'gecmis' && (
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-700 text-sm">UTS İşlem Geçmişi</h3>
                        <button onClick={() => fetchUtsBildirimGecmisi(selectedFirma.id)} className="text-xs text-blue-600 hover:text-blue-800">Yenile</button>
                      </div>
                      {utsBildirimGecmisi.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 text-sm">Henüz UTS işlemi yok</div>
                      ) : (
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {utsBildirimGecmisi.map((log, i) => (
                            <div key={log.id || i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${log.durum === 'basarili' ? 'bg-green-500' : log.durum === 'hatali' ? 'bg-red-500' : 'bg-amber-500'}`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-700">{utsBildirimTipleri.find(t => t.key === log.bildirim_tipi)?.label || log.bildirim_tipi}</p>
                                <p className="text-[10px] text-gray-400">{new Date(log.created_at).toLocaleString('tr-TR')}</p>
                                {log.hata_mesaji && <p className="text-[10px] text-red-500 mt-0.5 truncate">{log.hata_mesaji}</p>}
                              </div>
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${log.durum === 'basarili' ? 'bg-green-100 text-green-700' : log.durum === 'hatali' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                {log.durum === 'basarili' ? 'Başarılı' : log.durum === 'hatali' ? 'Hatalı' : 'Bekliyor'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* UTS bağlantı yoksa bilgi mesajı */}
              {!utsDurum?.kayitli && (
                <div className="mt-8 bg-gray-50 border border-gray-200 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m9.068-9.068a4.5 4.5 0 0 1 6.364 6.364l-4.5 4.5a4.5 4.5 0 0 1-7.244-1.242" /></svg>
                  </div>
                  <h3 className="font-semibold text-gray-700 mb-1">UTS Bağlantısı</h3>
                  <p className="text-xs text-gray-500 mb-3">
                    UTS ürün takip sistemi bağlantınızı Hesabım sayfasından yapılandırabilirsiniz.
                  </p>
                  <button onClick={() => { setMainView('hesabim'); navigate('/dash/hesabim'); }} className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-semibold hover:bg-emerald-100 transition border border-emerald-200">
                    Hesabım Sayfasına Git
                  </button>
                </div>
              )}

            </div>
          )}

          {/* === ADMİN DAĞITIM AĞI GÖRÜNÜMÜ (GİZLİ) === */}
          {mainView === 'dagitimAgi' && user?.role === 'admin' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-blue-900">Dağıtım Ağı</h1>
                <button onClick={fetchDagitimAgi} className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" /></svg>
                  Yenile
                </button>
              </div>

              {/* Özet Kartları */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700">{dagitimAgiStats.toplamBayi || 0}</div>
                  <div className="text-xs text-blue-600 font-medium mt-1">Aktif Bayi</div>
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-indigo-700">{dagitimAgiStats.toplamTeslim || 0}</div>
                  <div className="text-xs text-indigo-600 font-medium mt-1">Toplam Teslim</div>
                </div>
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-violet-700">{dagitimAgiStats.toplamDagitim || 0}</div>
                  <div className="text-xs text-violet-600 font-medium mt-1">Toplam Dağıtım</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-700">{dagitimAgiStats.toplamKalan || 0}</div>
                  <div className="text-xs text-emerald-600 font-medium mt-1">Toplam Kalan</div>
                </div>
              </div>

              {/* Arama */}
              <div className="mb-4">
                <input type="text" placeholder="Bayi adı, cari adı veya kart kodu ile ara..." value={dagitimAgiSearch} onChange={(e) => setDagitimAgiSearch(e.target.value)} className="w-full max-w-md text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400" />
              </div>

              {dagitimAgiLoading ? (
                <div className="text-center py-12 text-gray-400">Yükleniyor...</div>
              ) : dagitimAgiBayiler.length === 0 ? (
                <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-xl border border-gray-200">
                  Henüz teslim alan bayi bulunmuyor.
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="hidden sm:grid grid-cols-[1fr_90px_80px_80px_80px_80px_90px] gap-2 px-4 py-2 bg-gray-50 border-b text-[10px] font-semibold text-gray-500 uppercase">
                    <span>Bayi / Cari</span><span>Kart Kodu</span><span>Şehir</span><span className="text-center">Teslim</span><span className="text-center">Dağıtım</span><span className="text-center">Kalan</span><span className="text-center">Son Dağıtım</span>
                  </div>
                  {dagitimAgiBayiler.filter(b => {
                    if (!dagitimAgiSearch) return true;
                    const q = dagitimAgiSearch.toLowerCase();
                    return b.firma_adi?.toLowerCase().includes(q) || b.cari_adi?.toLowerCase().includes(q) || b.kart_kodu?.toLowerCase().includes(q);
                  }).map(b => (
                    <div key={b.firma_id} className="border-b border-gray-100 last:border-0">
                      <div className="grid grid-cols-[1fr_90px_80px_80px_80px_80px_90px] gap-2 items-center px-4 py-3 hover:bg-gray-50/50 cursor-pointer" onClick={() => {
                        if (dagitimAgiExpandedFirma === b.firma_id) { setDagitimAgiExpandedFirma(null); setDagitimAgiDetay(null); }
                        else { setDagitimAgiExpandedFirma(b.firma_id); fetchDagitimAgiDetay(b.firma_id); }
                      }}>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-800">{b.firma_adi}</div>
                          {b.cari_adi && <div className="text-[10px] text-violet-600 font-medium">{b.cari_adi}</div>}
                          <div className="text-[10px] text-gray-400">{b.email}{b.telefon ? ` • ${b.telefon}` : ''}</div>
                        </div>
                        <span className="text-[10px] font-mono text-blue-600">{b.kart_kodu || '-'}</span>
                        <span className="text-xs text-gray-600">{b.sehir || '-'}</span>
                        <span className="text-xs text-center font-semibold text-blue-700">{b.toplam_teslim}</span>
                        <span className="text-xs text-center font-semibold text-violet-700">{b.toplam_dagitim}</span>
                        <span className={`text-xs text-center font-bold ${b.kalan_stok === 0 ? 'text-emerald-600' : 'text-red-600'}`}>{b.kalan_stok}</span>
                        <span className="text-[10px] text-center text-gray-500">{b.son_dagitim ? new Date(b.son_dagitim).toLocaleDateString('tr-TR') : '-'}</span>
                      </div>

                      {/* Genişletilmiş Detay */}
                      {dagitimAgiExpandedFirma === b.firma_id && dagitimAgiDetay && (
                        <div className="px-4 pb-4 bg-gray-50/50 border-t border-gray-100">
                          {/* Cari bilgi kartı */}
                          {dagitimAgiDetay.firma && (
                            <div className="mt-3 mb-3 p-3 bg-white rounded-lg border border-blue-100 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
                              <div><span className="text-gray-400 uppercase font-semibold">Firma:</span> <span className="text-gray-800 font-medium">{dagitimAgiDetay.firma.firma}</span></div>
                              {dagitimAgiDetay.firma.cari_adi && <div><span className="text-gray-400 uppercase font-semibold">Cari:</span> <span className="text-violet-700 font-medium">{dagitimAgiDetay.firma.cari_adi}</span></div>}
                              {dagitimAgiDetay.firma.kart_kodu && <div><span className="text-gray-400 uppercase font-semibold">Kart Kodu:</span> <span className="text-blue-600 font-mono">{dagitimAgiDetay.firma.kart_kodu}</span></div>}
                              {dagitimAgiDetay.firma.cari_tipi && <div><span className="text-gray-400 uppercase font-semibold">Cari Tipi:</span> <span className="text-gray-700">{dagitimAgiDetay.firma.cari_tipi}</span></div>}
                              {dagitimAgiDetay.firma.sehir && <div><span className="text-gray-400 uppercase font-semibold">Şehir:</span> <span className="text-gray-700">{dagitimAgiDetay.firma.sehir}</span></div>}
                              {dagitimAgiDetay.firma.yetkili && <div><span className="text-gray-400 uppercase font-semibold">Yetkili:</span> <span className="text-gray-700">{dagitimAgiDetay.firma.yetkili}</span></div>}
                              {(dagitimAgiDetay.firma.telefon || dagitimAgiDetay.firma.cari_telefon) && <div><span className="text-gray-400 uppercase font-semibold">Telefon:</span> <span className="text-gray-700">{dagitimAgiDetay.firma.cari_telefon || dagitimAgiDetay.firma.telefon}</span></div>}
                              {dagitimAgiDetay.firma.email && <div><span className="text-gray-400 uppercase font-semibold">Email:</span> <span className="text-gray-700">{dagitimAgiDetay.firma.email}</span></div>}
                            </div>
                          )}
                          {/* Ürün bazlı stok */}
                          <h4 className="text-[10px] font-bold text-blue-700 uppercase tracking-wide mt-3 mb-2">Ürün Bazlı Stok</h4>
                          <div className="space-y-1 mb-4">
                            {(dagitimAgiDetay.stok || []).map((s, si) => (
                              <div key={si} className="grid grid-cols-[1fr_80px_60px_60px_60px] gap-2 items-center px-3 py-1.5 bg-white rounded border border-gray-100 text-xs">
                                <div className="min-w-0">
                                  <span className="text-gray-800 truncate block">{s.product_name}</span>
                                  {s.lot_no && <span className="text-[8px] font-mono text-blue-600">LOT: {s.lot_no}</span>}
                                </div>
                                <span className="font-mono text-[9px] text-gray-400 truncate">{s.barcode}</span>
                                <span className="text-center font-semibold text-blue-700">{s.toplam_teslim}</span>
                                <span className="text-center font-semibold text-violet-700">{s.toplam_dagitim}</span>
                                <span className={`text-center font-bold ${s.kalan === 0 ? 'text-emerald-600' : 'text-red-600'}`}>{s.kalan}</span>
                              </div>
                            ))}
                          </div>
                          {/* Dağıtım geçmişi */}
                          {(dagitimAgiDetay.dagitimlar || []).length > 0 && (
                            <>
                              <h4 className="text-[10px] font-bold text-violet-700 uppercase tracking-wide mb-2">Dağıtım Geçmişi</h4>
                              <div className="space-y-1">
                                {(dagitimAgiDetay.dagitimlar || []).slice(0, 30).map((d, di) => (
                                  <div key={di} className="grid grid-cols-[1fr_100px_80px_80px_60px_60px] gap-1 items-center px-3 py-1.5 bg-white rounded border border-gray-100 text-[10px]">
                                    <span className="text-gray-800 truncate">{d.product_name}</span>
                                    <span className="text-gray-700 truncate">{d.hastane_kurum}</span>
                                    <span className="text-gray-600 truncate">{d.hasta_adi || '-'}</span>
                                    <span className="text-gray-600 truncate">{d.doktor_adi || '-'}</span>
                                    <span className="text-center text-gray-500">{d.operasyon_tarihi ? new Date(d.operasyon_tarihi).toLocaleDateString('tr-TR') : '-'}</span>
                                    <span className="text-center font-bold text-violet-700">{d.qty}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === ADMİN UTS YÖNETİMİ GÖRÜNÜMÜ === */}
          {mainView === 'utsYonetimi' && user?.role === 'admin' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-blue-900">UTS Yönetimi</h1>
                <button onClick={() => { fetchUtsAdminBayiler(); fetchUtsAdminLoglar(); }} className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" /></svg>
                  Yenile
                </button>
              </div>

              {/* Özet Kartlar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-[10px] text-gray-400 uppercase font-semibold">Toplam Bayi</p>
                  <p className="text-2xl font-bold text-blue-900">{utsAdminBayiler.length}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-[10px] text-gray-400 uppercase font-semibold">Aktif</p>
                  <p className="text-2xl font-bold text-green-700">{utsAdminBayiler.filter(b => b.aktif).length}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-[10px] text-gray-400 uppercase font-semibold">Pasif</p>
                  <p className="text-2xl font-bold text-amber-600">{utsAdminBayiler.filter(b => !b.aktif).length}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-[10px] text-gray-400 uppercase font-semibold">Toplam İşlem</p>
                  <p className="text-2xl font-bold text-violet-700">{utsAdminBayiler.reduce((s, b) => s + (b.toplam_islem || 0), 0)}</p>
                </div>
              </div>

              {/* Admin UTS Sekmeleri */}
              <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1">
                {[
                  { key: 'bayiler', label: 'Bayi Durumları' },
                  { key: 'izinler', label: 'İzin Yönetimi' },
                  { key: 'loglar', label: 'Bildirim Logları' },
                ].map(tab => (
                  <button key={tab.key} onClick={() => setUtsTab(tab.key)} className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition ${utsTab === tab.key ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Bayiler Tablosu */}
              {utsTab === 'bayiler' && (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  {utsAdminBayiler.length === 0 ? (
                    <div className="text-center py-12 text-gray-400 text-sm">Henüz UTS kaydı bulunan bayi yok</div>
                  ) : (
                    <div>
                      <div className="hidden sm:grid grid-cols-[1fr_120px_80px_80px_80px_120px] gap-2 px-4 py-2 bg-gray-50 border-b text-[10px] font-semibold text-gray-500 uppercase">
                        <span>Firma</span><span>Kurum No</span><span>Durum</span><span className="text-center">İşlem</span><span className="text-center">Ortam</span><span className="text-center">Aksiyonlar</span>
                      </div>
                      {utsAdminBayiler.map(bayi => (
                        <div key={bayi.firma_id} className="grid grid-cols-1 sm:grid-cols-[1fr_120px_80px_80px_80px_120px] gap-2 items-center px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50/50 text-xs">
                          <div className="min-w-0">
                            <span className="font-semibold text-gray-800 block truncate">{bayi.firma}</span>
                            <span className="text-[10px] text-gray-400">{bayi.email}</span>
                          </div>
                          <span className="text-gray-600 font-mono text-[11px]">{bayi.uts_kurum_no || '-'}</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full w-fit ${bayi.aktif ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                            {bayi.aktif ? 'Aktif' : 'Pasif'}
                          </span>
                          <span className="text-center text-gray-500">{bayi.toplam_islem || 0}</span>
                          <span className={`text-center text-[10px] font-semibold ${bayi.is_test ? 'text-amber-600' : 'text-green-600'}`}>{bayi.is_test ? 'TEST' : 'GERÇEK'}</span>
                          <div className="flex gap-1 justify-center">
                            <button onClick={() => handleUtsAdminAktifPasif(bayi.firma_id, !bayi.aktif)} className={`px-2 py-1 text-[10px] rounded font-semibold transition ${bayi.aktif ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                              {bayi.aktif ? 'Pasif Et' : 'Aktif Et'}
                            </button>
                            <button onClick={() => { setUtsAdminSelectedBayi(bayi); fetchUtsAdminBayiIzinler(bayi.firma_id); setUtsTab('izinler'); }} className="px-2 py-1 bg-violet-100 text-violet-700 text-[10px] rounded font-semibold hover:bg-violet-200 transition">İzinler</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* İzin Yönetimi */}
              {utsTab === 'izinler' && (
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  {utsAdminSelectedBayi ? (
                    <>
                      <div className="flex items-center gap-3 mb-4">
                        <button onClick={() => { setUtsAdminSelectedBayi(null); setUtsTab('bayiler'); }} className="text-xs text-blue-600 hover:text-blue-800">← Geri</button>
                        <h3 className="font-semibold text-gray-700 text-sm">{utsAdminSelectedBayi.firma} - UTS İzinleri</h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {utsBildirimTipleri.filter(t => t.bayi).map(tip => {
                          const izin = utsAdminBayiIzinler.find(iz => iz.bildirim_tipi === tip.key);
                          const izinDurumu = izin ? izin.izin_durumu : 1;
                          return (
                            <div key={tip.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                              <div>
                                <span className="text-xs font-medium text-gray-700">{tip.label}</span>
                                <span className="text-[10px] text-gray-400 block">{tip.key}</span>
                              </div>
                              <button onClick={() => handleUtsAdminIzinGuncelle(utsAdminSelectedBayi.firma_id, tip.key, izinDurumu ? 0 : 1)} className={`px-3 py-1 rounded-full text-[10px] font-bold transition ${izinDurumu ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}>
                                {izinDurumu ? 'Açık' : 'Kapalı'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      Bir bayi seçerek izinlerini yönetin.
                      <button onClick={() => setUtsTab('bayiler')} className="block mx-auto mt-2 text-blue-600 hover:text-blue-800 text-xs font-semibold">Bayileri Görüntüle</button>
                    </div>
                  )}
                </div>
              )}

              {/* Loglar */}
              {utsTab === 'loglar' && (
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-700 text-sm">UTS Bildirim Logları</h3>
                    <button onClick={fetchUtsAdminLoglar} className="text-xs text-blue-600 hover:text-blue-800">Yenile</button>
                  </div>
                  {utsAdminLoglar.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-sm">Henüz UTS log kaydı yok</div>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {utsAdminLoglar.map((log, i) => (
                        <div key={log.id || i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${log.durum === 'basarili' ? 'bg-green-500' : log.durum === 'hatali' ? 'bg-red-500' : 'bg-amber-500'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-800">{log.firma || 'Firma ' + log.firma_id}</span>
                              <span className="text-[10px] text-gray-400">|</span>
                              <span className="text-[10px] text-violet-600 font-medium">{utsBildirimTipleri.find(t => t.key === log.bildirim_tipi)?.label || log.bildirim_tipi}</span>
                            </div>
                            <p className="text-[10px] text-gray-400">{new Date(log.created_at).toLocaleString('tr-TR')}</p>
                            {log.hata_mesaji && <p className="text-[10px] text-red-500 mt-0.5 truncate">{log.hata_mesaji}</p>}
                          </div>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0 ${log.durum === 'basarili' ? 'bg-green-100 text-green-700' : log.durum === 'hatali' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {log.durum === 'basarili' ? 'OK' : log.durum === 'hatali' ? 'HATA' : '...'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* === ADMİN ÜRÜN GRUPLARI & RESİMLER GÖRÜNÜMÜ === */}
          {mainView === 'urunGruplari' && user?.role === 'admin' && (() => {
            const kategoriler = [...new Set(urunGruplari.map(g => g.kategori).filter(Boolean))].sort();
            const filteredGroups = urunGruplari.filter(g => {
              const matchSearch = !urunGruplariSearch || 
                g.name.toLowerCase().includes(urunGruplariSearch.toLowerCase()) ||
                (g.marka || '').toLowerCase().includes(urunGruplariSearch.toLowerCase()) ||
                (g.sut_codes || '').toLowerCase().includes(urunGruplariSearch.toLowerCase());
              const matchKategori = !urunGruplariKategori || g.kategori === urunGruplariKategori;
              return matchSearch && matchKategori;
            });
            const hasImage = g => g.image_url && g.image_url !== '/urunler/300x300.png';
            const withImage = filteredGroups.filter(hasImage).length;
            const withoutImage = filteredGroups.filter(g => !hasImage(g)).length;

            const handleImageUpload = async (productId, file) => {
              setUrunGruplariUploading(productId);
              const formData = new FormData();
              formData.append('image', file);
              try {
                const res = await authFetch(`/api/admin/product-groups/${productId}/image`, {
                  method: 'POST',
                  body: formData,
                });
                const data = await res.json();
                if (data.success) {
                  setUrunGruplari(prev => prev.map(g => g.id === productId ? { ...g, image_url: data.image_url } : g));
                } else {
                  alert('Resim yüklenemedi: ' + (data.error || 'Bilinmeyen hata'));
                }
              } catch (err) {
                alert('Resim yükleme hatası: ' + err.message);
              }
              setUrunGruplariUploading(null);
            };

            const handleRemoveImage = async (productId) => {
              if (!window.confirm('Resmi kaldırmak istediğinize emin misiniz?')) return;
              try {
                const res = await authFetch(`/api/admin/product-groups/${productId}/image`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ image_url: '/urunler/300x300.png' }),
                });
                const data = await res.json();
                if (data.success) {
                  setUrunGruplari(prev => prev.map(g => g.id === productId ? { ...g, image_url: '/urunler/300x300.png' } : g));
                }
              } catch (err) {
                alert('Hata: ' + err.message);
              }
            };

            const openVariantPriceModal = async (productId) => {
              setVariantPriceModal({ productId, parent: null, variants: [], sutPrices: {}, editedPrices: {}, loading: true, saving: false });
              setVariantPriceSearch('');
              try {
                const res = await authFetch(`/api/admin/product-groups/${productId}/variant-prices`);
                const data = await res.json();
                const edited = {};
                (data.variants || []).forEach(v => {
                  if (v.custom_price != null) edited[v.id] = String(v.custom_price);
                });
                setVariantPriceModal({
                  productId,
                  parent: data.parent,
                  variants: data.variants || [],
                  sutPrices: data.sut_prices || {},
                  editedPrices: edited,
                  loading: false,
                  saving: false,
                });
              } catch (err) {
                alert('Varyantlar yüklenemedi: ' + err.message);
                setVariantPriceModal(null);
              }
            };

            const saveVariantPrices = async () => {
              if (!variantPriceModal) return;
              const { productId, editedPrices, variants } = variantPriceModal;
              setVariantPriceModal(prev => ({ ...prev, saving: true }));
              try {
                const prices = variants.map(v => ({
                  variant_id: v.id,
                  custom_price: editedPrices[v.id] !== undefined ? (editedPrices[v.id] || null) : (v.custom_price != null ? String(v.custom_price) : null),
                }));
                const res = await authFetch(`/api/admin/product-groups/${productId}/variant-prices`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ prices }),
                });
                const data = await res.json();
                if (data.success) {
                  // Parent min/max'ı yeniden hesapla
                  const customPrices = prices.map(p => p.custom_price).filter(p => p != null && p !== '').map(Number);
                  const newMin = customPrices.length > 0 ? Math.min(...customPrices) : null;
                  const newMax = customPrices.length > 0 ? Math.max(...customPrices) : null;
                  setUrunGruplari(prev => prev.map(g => g.id === productId ? { ...g, custom_price_min: newMin, custom_price_max: newMax } : g));
                  setVariantPriceModal(null);
                } else {
                  alert('Kayıt hatası: ' + (data.error || ''));
                  setVariantPriceModal(prev => ({ ...prev, saving: false }));
                }
              } catch (err) {
                alert('Kayıt hatası: ' + err.message);
                setVariantPriceModal(prev => ({ ...prev, saving: false }));
              }
            };

            const openMediaLibrary = async (productId) => {
              setMediaLibraryOpen(productId);
              setMediaLibrarySearch('');
              if (mediaLibraryItems.length === 0) {
                setMediaLibraryLoading(true);
                try {
                  const res = await fetch('/api/media-library');
                  const data = await res.json();
                  setMediaLibraryItems(data);
                } catch (err) {
                  console.error('Medya kütüphanesi yüklenemedi:', err);
                }
                setMediaLibraryLoading(false);
              }
            };

            const selectFromLibrary = async (productId, imageUrl) => {
              try {
                const res = await authFetch(`/api/admin/product-groups/${productId}/image`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ image_url: imageUrl }),
                });
                const data = await res.json();
                if (data.success) {
                  setUrunGruplari(prev => prev.map(g => g.id === productId ? { ...g, image_url: imageUrl } : g));
                  setMediaLibraryOpen(null);
                }
              } catch (err) {
                alert('Hata: ' + err.message);
              }
            };

            const handleToggleGroupActive = async (productId, currentActive) => {
              const newActive = currentActive ? false : true;
              try {
                const res = await authFetch(`/api/admin/product-groups/${productId}/toggle-active`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ is_active: newActive }),
                });
                const data = await res.json();
                if (data.success) {
                  setUrunGruplari(prev => prev.map(g => g.id === productId ? { ...g, is_active: newActive ? 1 : 0, inactive_variant_count: newActive ? 0 : g.variant_count } : g));
                }
              } catch (err) {
                alert('Hata: ' + err.message);
              }
            };

            const handleToggleVariantActive = async (variantId, newActive) => {
              try {
                const res = await authFetch(`/api/admin/variants/${variantId}/toggle-active`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ is_active: newActive }),
                });
                return await res.json();
              } catch (err) {
                alert('Hata: ' + err.message);
                return { success: false };
              }
            };

            const openVariantActiveModal = async (productId) => {
              setVariantPriceModal({ productId, parent: null, variants: [], sutPrices: {}, editedPrices: {}, loading: true, saving: false, mode: 'active' });
              setVariantPriceSearch('');
              try {
                const res = await authFetch(`/api/admin/product-groups/${productId}/variant-prices`);
                const data = await res.json();
                setVariantPriceModal({
                  productId,
                  parent: data.parent,
                  variants: data.variants || [],
                  sutPrices: data.sut_prices || {},
                  editedPrices: {},
                  loading: false,
                  saving: false,
                  mode: 'active',
                });
              } catch (err) {
                alert('Varyantlar yüklenemedi: ' + err.message);
                setVariantPriceModal(null);
              }
            };

            const filteredMedia = mediaLibrarySearch
              ? mediaLibraryItems.filter(m => m.name.toLowerCase().includes(mediaLibrarySearch.toLowerCase()))
              : mediaLibraryItems;

            return (
            <div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
                <div>
                  <h1 className="text-2xl font-bold text-blue-900">Ürün Grupları & Resimler</h1>
                  <p className="text-sm text-gray-500 mt-1">Her ürün grubuna resim ekleyerek ön sayfada görünmesini sağlayın</p>
                </div>
                <button onClick={() => setMainView('products')} className="flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium text-sm transition">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" /></svg>
                  Ürünlere Dön
                </button>
              </div>

              {/* Özet Kartları */}
              {(() => {
                const activeGroups = urunGruplari.filter(g => g.is_active !== 0).length;
                const passiveGroups = urunGruplari.filter(g => g.is_active === 0).length;
                return (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-blue-700">{urunGruplari.length}</div>
                    <div className="text-xs text-blue-600 font-medium">Toplam Grup</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-emerald-700">{activeGroups}</div>
                    <div className="text-xs text-emerald-600 font-medium">Aktif</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-red-600">{passiveGroups}</div>
                    <div className="text-xs text-red-500 font-medium">Pasif</div>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-orange-700">{withImage}</div>
                    <div className="text-xs text-orange-600 font-medium">Resimli</div>
                  </div>
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-indigo-700">{kategoriler.length}</div>
                    <div className="text-xs text-indigo-600 font-medium">Kategori</div>
                  </div>
                </div>
                );
              })()}

              {/* Filtreler */}
              <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Ürün adı, marka veya SUT kodu ile ara..."
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                    value={urunGruplariSearch}
                    onChange={e => setUrunGruplariSearch(e.target.value)}
                  />
                </div>
                <select
                  className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 bg-white"
                  value={urunGruplariKategori}
                  onChange={e => setUrunGruplariKategori(e.target.value)}
                >
                  <option value="">Tüm Kategoriler</option>
                  {kategoriler.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>

              {urunGruplariLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-700"></div>
                  <span className="ml-3 text-blue-700 font-medium">Yükleniyor...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredGroups.map(group => {
                    const isActive = group.is_active !== 0;
                    const inactiveVars = parseInt(group.inactive_variant_count) || 0;
                    return (
                    <div key={group.id} className={`bg-white border rounded-xl shadow-sm overflow-hidden transition hover:shadow-md ${!isActive ? 'border-red-200 opacity-70' : hasImage(group) ? 'border-emerald-200' : 'border-orange-200'}`}>
                      {/* Resim Alanı */}
                      <div className="relative bg-gray-50 h-40 flex items-center justify-center overflow-hidden group">
                        <img
                          src={group.image_url || '/urunler/300x300.png'}
                          alt={group.name}
                          className={`object-contain w-full h-full p-2 ${!isActive ? 'grayscale' : ''}`}
                        />
                        {/* Overlay - Resim Değiştir */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                          <div className="flex items-center gap-2">
                            <label className="px-3 py-2 bg-white text-blue-700 rounded-lg text-xs font-bold cursor-pointer hover:bg-blue-50 transition shadow">
                              {urunGruplariUploading === group.id ? (
                                <span className="flex items-center gap-1">
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-700"></div>
                                  Yükleniyor...
                                </span>
                              ) : (
                                <>Dosyadan Yükle</>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                disabled={urunGruplariUploading === group.id}
                                onChange={e => {
                                  if (e.target.files?.[0]) handleImageUpload(group.id, e.target.files[0]);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                            <button
                              className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition shadow"
                              onClick={() => openMediaLibrary(group.id)}
                            >
                              Kütüphaneden Seç
                            </button>
                          </div>
                          {hasImage(group) && (
                            <button
                              className="px-3 py-2 bg-red-500 text-white rounded-lg text-xs font-bold hover:bg-red-600 transition shadow"
                              onClick={() => handleRemoveImage(group.id)}
                            >
                              Kaldır
                            </button>
                          )}
                        </div>
                        {/* Badges */}
                        <div className="absolute top-2 right-2 flex flex-col gap-1">
                          {hasImage(group) ? (
                            <span className="bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">Resimli</span>
                          ) : (
                            <span className="bg-orange-400 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">Resimsiz</span>
                          )}
                          {!isActive && (
                            <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">Pasif</span>
                          )}
                        </div>
                      </div>
                      {/* Bilgiler */}
                      <div className="p-3">
                        <div className="font-bold text-blue-900 text-sm leading-tight line-clamp-2 mb-1.5" title={group.name}>{group.name}</div>
                        <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                          {group.marka && <span className="bg-indigo-100 text-indigo-700 text-[10px] px-1.5 py-0.5 rounded font-semibold">{group.marka}</span>}
                          {group.kategori && <span className="text-[10px] text-blue-500">{group.kategori}</span>}
                          <span className="text-[10px] text-gray-400">{group.variant_count} varyant</span>
                          {inactiveVars > 0 && isActive && (
                            <span className="text-[10px] text-red-500 font-semibold">{inactiveVars} pasif</span>
                          )}
                        </div>

                        {/* Aktif/Pasif Toggle */}
                        <div className="flex items-center justify-between mb-2 py-1.5 px-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-semibold ${isActive ? 'text-emerald-700' : 'text-red-600'}`}>
                              {isActive ? 'Aktif' : 'Pasif'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              className="text-[9px] text-blue-600 hover:text-blue-800 font-semibold hover:underline"
                              onClick={() => openVariantActiveModal(group.id)}
                            >
                              Barkod Bazlı
                            </button>
                            <button
                              onClick={() => handleToggleGroupActive(group.id, isActive)}
                              className={`relative w-9 h-5 rounded-full transition-colors ${isActive ? 'bg-emerald-500' : 'bg-gray-300'}`}
                            >
                              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isActive ? 'left-[18px]' : 'left-0.5'}`} />
                            </button>
                          </div>
                        </div>

                        {/* Fiyat Bilgisi */}
                        {(() => {
                          const hasCustom = group.custom_price_min != null;
                          return (
                            <div className="space-y-1">
                              {/* SUT Fiyatı */}
                              {group.sut_price_min && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] text-gray-400 shrink-0">SUT:</span>
                                  <span className={`text-[11px] font-mono ${hasCustom ? 'text-gray-400 line-through' : 'text-blue-700 font-bold'}`}>
                                    {Number(group.sut_price_min) === Number(group.sut_price_max)
                                      ? `${Number(group.sut_price_min).toFixed(2)} TL`
                                      : `${Number(group.sut_price_min).toFixed(2)} - ${Number(group.sut_price_max).toFixed(2)} TL`}
                                  </span>
                                </div>
                              )}
                              {/* Özel Fiyat aralığı (varsa) */}
                              {hasCustom && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] text-emerald-600 shrink-0 font-semibold">Özel:</span>
                                  <span className="text-[11px] font-mono text-emerald-700 font-bold">
                                    {Number(group.custom_price_min) === Number(group.custom_price_max || group.custom_price_min)
                                      ? `${Number(group.custom_price_min).toFixed(2)} TL`
                                      : `${Number(group.custom_price_min).toFixed(2)} - ${Number(group.custom_price_max).toFixed(2)} TL`}
                                  </span>
                                </div>
                              )}
                              {/* Fiyat Düzenle Butonu */}
                              <button
                                className="w-full mt-0.5 px-2 py-1.5 bg-blue-50 text-blue-600 text-[10px] font-semibold rounded border border-blue-200 hover:bg-blue-100 transition flex items-center justify-center gap-1"
                                onClick={() => openVariantPriceModal(group.id)}
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" /></svg>
                                {hasCustom ? 'Fiyatları Düzenle' : 'Özel Fiyat Belirle'}
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}

              {!urunGruplariLoading && filteredGroups.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 0 3Z" /></svg>
                  <p className="text-lg font-semibold">Ürün grubu bulunamadı</p>
                  <p className="text-sm mt-1">Arama kriterlerinizi değiştirmeyi deneyin</p>
                </div>
              )}

              {/* Medya Kütüphanesi Modal */}
              {mediaLibraryOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => setMediaLibraryOpen(null)}>
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                    {/* Modal Header */}
                    <div className="p-5 pb-3 border-b border-gray-100 flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-bold text-blue-900">Medya Kütüphanesi</h2>
                        <p className="text-xs text-gray-500 mt-0.5">Mevcut resimlerden birini seçin veya yeni resim yükleyin ({mediaLibraryItems.length} resim)</p>
                      </div>
                      <button className="text-gray-400 hover:text-red-500 text-2xl font-bold transition" onClick={() => setMediaLibraryOpen(null)}>&times;</button>
                    </div>

                    {/* Yeni Resim Yükle + Arama */}
                    <div className="px-5 py-3 border-b border-gray-50 flex flex-col sm:flex-row gap-3">
                      <label className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold cursor-pointer hover:bg-blue-700 transition shadow inline-flex items-center gap-2 shrink-0">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" /></svg>
                        Yeni Resim Yükle
                        <input type="file" accept="image/*" className="hidden" onChange={async e => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('image', file);
                          try {
                            const res = await fetch('/api/upload-image', { method: 'POST', body: formData });
                            const data = await res.json();
                            if (data.url) {
                              const newItem = { name: data.url.split('/').pop(), url: data.url, size: file.size, modified: new Date().toISOString() };
                              setMediaLibraryItems(prev => [newItem, ...prev]);
                              selectFromLibrary(mediaLibraryOpen, data.url);
                            }
                          } catch (err) {
                            alert('Yükleme hatası: ' + err.message);
                          }
                          e.target.value = '';
                        }} />
                      </label>
                      <input
                        type="text"
                        placeholder="Dosya adı ile ara..."
                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                        value={mediaLibrarySearch}
                        onChange={e => setMediaLibrarySearch(e.target.value)}
                      />
                    </div>

                    {/* Resim Grid */}
                    <div className="flex-1 overflow-y-auto p-5">
                      {mediaLibraryLoading ? (
                        <div className="flex items-center justify-center py-16">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
                          <span className="ml-3 text-blue-700">Yükleniyor...</span>
                        </div>
                      ) : filteredMedia.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                          <p className="text-sm">Resim bulunamadı</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                          {filteredMedia.map(item => {
                            const isCurrentImage = urunGruplari.find(g => g.id === mediaLibraryOpen)?.image_url === item.url;
                            return (
                              <button
                                key={item.url}
                                className={`relative bg-gray-50 border-2 rounded-xl overflow-hidden aspect-square flex items-center justify-center transition-all hover:shadow-lg hover:scale-[1.03] ${
                                  isCurrentImage ? 'border-emerald-500 ring-2 ring-emerald-300' : 'border-gray-200 hover:border-blue-400'
                                }`}
                                onClick={() => selectFromLibrary(mediaLibraryOpen, item.url)}
                                title={item.name}
                              >
                                <img src={item.url} alt={item.name} className="object-contain w-full h-full p-1" />
                                {isCurrentImage && (
                                  <span className="absolute top-1 right-1 bg-emerald-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">Aktif</span>
                                )}
                                <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate text-center">{item.name}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Varyant Bazlı Fiyat Düzenleme / Aktif-Pasif Modalı */}
              {variantPriceModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => !variantPriceModal.saving && setVariantPriceModal(null)}>
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                    {/* Header */}
                    <div className="p-5 pb-3 border-b border-gray-100 flex items-start justify-between">
                      <div>
                        <h2 className="text-lg font-bold text-blue-900">
                          {variantPriceModal.mode === 'active' ? 'Barkod Bazlı Aktif/Pasif Yönetimi' : 'Barkod Bazlı Fiyat Düzenleme'}
                        </h2>
                        {variantPriceModal.parent && (
                          <p className="text-sm text-gray-500 mt-0.5">
                            {variantPriceModal.parent.name} — {variantPriceModal.variants.length} varyant
                            {variantPriceModal.mode === 'active' && (() => {
                              const activeVars = variantPriceModal.variants.filter(v => v.is_active !== 0).length;
                              const passiveVars = variantPriceModal.variants.length - activeVars;
                              return <span className="ml-2 text-xs">(<span className="text-emerald-600 font-semibold">{activeVars} aktif</span>, <span className="text-red-500 font-semibold">{passiveVars} pasif</span>)</span>;
                            })()}
                          </p>
                        )}
                      </div>
                      <button className="text-gray-400 hover:text-red-500 text-2xl font-bold transition" onClick={() => !variantPriceModal.saving && setVariantPriceModal(null)}>&times;</button>
                    </div>

                    {/* Loading */}
                    {variantPriceModal.loading ? (
                      <div className="flex items-center justify-center py-16">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
                        <span className="ml-3 text-blue-700">Varyantlar yükleniyor...</span>
                      </div>
                    ) : variantPriceModal.mode === 'active' ? (
                      /* ── Aktif/Pasif Modu ── */
                      <>
                        <div className="px-5 py-3 border-b border-gray-50 flex flex-col sm:flex-row gap-3 items-center">
                          <input
                            type="text"
                            placeholder="Barkod, ölçü veya isim ile ara..."
                            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                            value={variantPriceSearch}
                            onChange={e => setVariantPriceSearch(e.target.value)}
                          />
                          <div className="flex gap-2 shrink-0">
                            <button
                              className="px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-semibold hover:bg-emerald-200 transition border border-emerald-200"
                              onClick={async () => {
                                for (const v of variantPriceModal.variants) {
                                  if (v.is_active === 0) await handleToggleVariantActive(v.id, true);
                                }
                                setVariantPriceModal(prev => ({ ...prev, variants: prev.variants.map(v => ({ ...v, is_active: 1 })) }));
                                setUrunGruplari(prev => prev.map(g => g.id === variantPriceModal.productId ? { ...g, is_active: 1, inactive_variant_count: 0 } : g));
                              }}
                            >Tümünü Aktif Yap</button>
                            <button
                              className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-200 transition border border-red-200"
                              onClick={async () => {
                                if (!window.confirm('Tüm varyantları pasife almak istediğinize emin misiniz?')) return;
                                for (const v of variantPriceModal.variants) {
                                  if (v.is_active !== 0) await handleToggleVariantActive(v.id, false);
                                }
                                setVariantPriceModal(prev => ({ ...prev, variants: prev.variants.map(v => ({ ...v, is_active: 0 })) }));
                                setUrunGruplari(prev => prev.map(g => g.id === variantPriceModal.productId ? { ...g, is_active: 0, inactive_variant_count: g.variant_count } : g));
                              }}
                            >Tümünü Pasif Yap</button>
                          </div>
                        </div>

                        <div className="flex-1 overflow-auto px-5 py-2">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                              <tr>
                                <th className="text-left px-3 py-2.5 text-gray-600 font-semibold w-[180px]">Barkod</th>
                                <th className="text-left px-3 py-2.5 text-gray-600 font-semibold">Varyant / Ölçü</th>
                                <th className="text-left px-3 py-2.5 text-gray-600 font-semibold w-[90px]">SUT Kodu</th>
                                <th className="text-center px-3 py-2.5 text-gray-600 font-semibold w-[100px]">Durum</th>
                              </tr>
                            </thead>
                            <tbody>
                              {variantPriceModal.variants
                                .filter(v => {
                                  if (!variantPriceSearch) return true;
                                  const s = variantPriceSearch.toLowerCase();
                                  return (v.barcode || '').includes(s) ||
                                    (v.variant_name || '').toLowerCase().includes(s) ||
                                    (v.olcu_clean || '').toLowerCase().includes(s) ||
                                    (v.size_label || '').toLowerCase().includes(s) ||
                                    (v.sut_code_used || '').toLowerCase().includes(s);
                                })
                                .map(v => {
                                  const vActive = v.is_active !== 0;
                                  return (
                                    <tr key={v.id} className={`border-t border-gray-100 transition ${!vActive ? 'bg-red-50/50 opacity-70' : 'hover:bg-gray-50'}`}>
                                      <td className="px-3 py-2.5 font-mono text-gray-600 text-[11px]">{v.barcode}</td>
                                      <td className="px-3 py-2.5">
                                        <div className={`font-medium text-[11px] leading-tight ${!vActive ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{v.olcu_clean || v.size_label || v.variant_name}</div>
                                        {v.parent_group && <div className="text-[9px] text-gray-400">{v.parent_group}</div>}
                                      </td>
                                      <td className="px-3 py-2.5 font-mono text-[10px] text-gray-500">{v.sut_code_used || '-'}</td>
                                      <td className="px-3 py-2.5">
                                        <div className="flex items-center justify-center gap-2">
                                          <span className={`text-[10px] font-semibold ${vActive ? 'text-emerald-700' : 'text-red-600'}`}>
                                            {vActive ? 'Aktif' : 'Pasif'}
                                          </span>
                                          <button
                                            onClick={async () => {
                                              const newActive = !vActive;
                                              const result = await handleToggleVariantActive(v.id, newActive);
                                              if (result.success) {
                                                setVariantPriceModal(prev => ({
                                                  ...prev,
                                                  variants: prev.variants.map(pv => pv.id === v.id ? { ...pv, is_active: newActive ? 1 : 0 } : pv)
                                                }));
                                                // Parent durumunu güncelle
                                                setUrunGruplari(prev => prev.map(g => {
                                                  if (g.id !== variantPriceModal.productId) return g;
                                                  const updatedVars = variantPriceModal.variants.map(pv => pv.id === v.id ? { ...pv, is_active: newActive ? 1 : 0 } : pv);
                                                  const inactiveCount = updatedVars.filter(pv => pv.is_active === 0).length;
                                                  const anyActive = updatedVars.some(pv => pv.is_active !== 0);
                                                  return { ...g, is_active: anyActive ? 1 : 0, inactive_variant_count: inactiveCount };
                                                }));
                                              }
                                            }}
                                            className={`relative w-9 h-5 rounded-full transition-colors ${vActive ? 'bg-emerald-500' : 'bg-gray-300'}`}
                                          >
                                            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${vActive ? 'left-[18px]' : 'left-0.5'}`} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>

                        <div className="p-4 border-t border-gray-100 flex items-center justify-between">
                          <p className="text-[10px] text-gray-400">Pasif varyantlar müşteri tarafında görünmez</p>
                          <button
                            className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-300 transition"
                            onClick={() => setVariantPriceModal(null)}
                          >Kapat</button>
                        </div>
                      </>
                    ) : (
                      /* ── Fiyat Düzenleme Modu ── */
                      <>
                        {/* Toplu işlem + Arama */}
                        <div className="px-5 py-3 border-b border-gray-50 flex flex-col sm:flex-row gap-3 items-center">
                          <input
                            type="text"
                            placeholder="Barkod, ölçü veya isim ile ara..."
                            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                            value={variantPriceSearch}
                            onChange={e => setVariantPriceSearch(e.target.value)}
                          />
                          <div className="flex gap-2 shrink-0">
                            <button
                              className="px-3 py-2 bg-orange-100 text-orange-700 rounded-lg text-xs font-semibold hover:bg-orange-200 transition border border-orange-200"
                              onClick={() => {
                                if (window.confirm('Tüm özel fiyatları kaldırıp SUT fiyatlarına dönmek istiyor musunuz?')) {
                                  setVariantPriceModal(prev => ({ ...prev, editedPrices: Object.fromEntries(prev.variants.map(v => [v.id, ''])) }));
                                }
                              }}
                            >Tümünü Temizle</button>
                            <span className="text-xs text-gray-400 flex items-center">
                              {Object.values(variantPriceModal.editedPrices).filter(v => v !== '' && v != null).length} / {variantPriceModal.variants.length} özel fiyat
                            </span>
                          </div>
                        </div>

                        {/* Tablo */}
                        <div className="flex-1 overflow-auto px-5 py-2">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                              <tr>
                                <th className="text-left px-3 py-2.5 text-gray-600 font-semibold w-[180px]">Barkod</th>
                                <th className="text-left px-3 py-2.5 text-gray-600 font-semibold">Varyant / Ölçü</th>
                                <th className="text-left px-3 py-2.5 text-gray-600 font-semibold w-[90px]">SUT Kodu</th>
                                <th className="text-right px-3 py-2.5 text-gray-600 font-semibold w-[100px]">SUT Fiyatı</th>
                                <th className="text-right px-3 py-2.5 text-gray-600 font-semibold w-[130px]">Özel Fiyat (TL)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {variantPriceModal.variants
                                .filter(v => {
                                  if (!variantPriceSearch) return true;
                                  const s = variantPriceSearch.toLowerCase();
                                  return (v.barcode || '').includes(s) ||
                                    (v.variant_name || '').toLowerCase().includes(s) ||
                                    (v.olcu_clean || '').toLowerCase().includes(s) ||
                                    (v.size_label || '').toLowerCase().includes(s) ||
                                    (v.sut_code_used || '').toLowerCase().includes(s);
                                })
                                .map(v => {
                                  const editVal = variantPriceModal.editedPrices[v.id];
                                  const hasCustom = editVal !== undefined ? (editVal !== '' && editVal != null) : (v.custom_price != null);
                                  const currentVal = editVal !== undefined ? editVal : (v.custom_price != null ? String(v.custom_price) : '');
                                  return (
                                    <tr key={v.id} className={`border-t border-gray-100 transition ${hasCustom ? 'bg-emerald-50/50' : 'hover:bg-gray-50'}`}>
                                      <td className="px-3 py-2 font-mono text-gray-600 text-[11px]">{v.barcode}</td>
                                      <td className="px-3 py-2">
                                        <div className="text-gray-800 font-medium text-[11px] leading-tight">{v.olcu_clean || v.size_label || v.variant_name}</div>
                                        {v.parent_group && <div className="text-[9px] text-gray-400">{v.parent_group}</div>}
                                      </td>
                                      <td className="px-3 py-2 font-mono text-[10px] text-gray-500">{v.sut_code_used || '-'}</td>
                                      <td className="px-3 py-2 text-right">
                                        {v.sut_price ? (
                                          <span className={`font-mono text-[11px] ${hasCustom ? 'text-gray-400 line-through' : 'text-blue-700 font-bold'}`}>
                                            {Number(v.sut_price).toFixed(2)}
                                          </span>
                                        ) : (
                                          <span className="text-gray-300">-</span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2">
                                        <input
                                          type="number" step="0.01" min="0"
                                          className={`w-full px-2 py-1.5 border rounded text-xs font-mono text-right focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400 ${
                                            hasCustom ? 'border-emerald-300 bg-emerald-50 text-emerald-800 font-bold' : 'border-gray-200 bg-white text-gray-600'
                                          }`}
                                          placeholder={v.sut_price ? Number(v.sut_price).toFixed(2) : '0.00'}
                                          value={currentVal}
                                          onChange={e => {
                                            const val = e.target.value;
                                            setVariantPriceModal(prev => ({
                                              ...prev,
                                              editedPrices: { ...prev.editedPrices, [v.id]: val }
                                            }));
                                          }}
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-gray-100 flex items-center justify-between gap-3">
                          <p className="text-[10px] text-gray-400">Boş bırakılan alanlarda SUT fiyatı gösterilir</p>
                          <div className="flex gap-2">
                            <button
                              className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-300 transition"
                              onClick={() => setVariantPriceModal(null)}
                              disabled={variantPriceModal.saving}
                            >İptal</button>
                            <button
                              className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition shadow disabled:opacity-50"
                              onClick={saveVariantPrices}
                              disabled={variantPriceModal.saving}
                            >
                              {variantPriceModal.saving ? (
                                <span className="flex items-center gap-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  Kaydediliyor...
                                </span>
                              ) : 'Tümünü Kaydet'}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
          })()}

          {/* === ÜRÜN LİSTESİ GÖRÜNÜMÜ (varsayılan) === */}
          {mainView === 'products' && (
          <>
          <h1 className="text-2xl md:text-3xl font-bold text-blue-900 mb-4 md:mb-6">{t('Sipariş Oluştur')}</h1>
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>{t('Ürün Sıralaması')}:</strong> {t('Ürünler popülerlik ve önem düzeyine göre sıralanmıştır')}. 
              {t('Kardiyoloji ve damar cerrahisi ürünleri öncelikli olarak gösterilmektedir')}.
            </p>
            {user && (
              <p className="text-xs text-orange-600 mt-1.5 font-medium">
                ⚠ Aşağıdaki ürünlerde belirtilen fiyatlar SUT fiyatlarını temsilen yansıtılmıştır. Gerçek satış fiyatları yansıtmamaktadır.
              </p>
            )}
          </div>
          {/* Arama, filtre ve görünüm toggle */}
          <div className="flex flex-col md:flex-row gap-4 mb-6 md:mb-8 items-center">
            <div className="flex-1 w-full" data-tour="product-search">
            <input
              type="text"
              className="flex-1 px-4 py-3 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 text-blue-900 bg-white shadow-sm w-full"
              placeholder={t('Ürün adı, ölçü, SUT kodu veya barkod ile ara...')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            </div>
            <select
              className="w-full md:w-60 px-4 py-3 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 text-blue-900 bg-white shadow-sm"
              value={category}
              onChange={e => setCategory(e.target.value)}
              data-tour="category-filter"
            >
              <option value="Tüm Kategoriler">Tüm Kategoriler</option>
              {allCategories.map(cat => (
                <option key={cat.name} value={cat.name}>{cat.name}</option>
              ))}
            </select>
            {/* Görünüm toggle butonu */}
            <div className="flex gap-2 ml-auto mt-2 md:mt-0">
              <button
                className={`p-2 rounded-lg border ${viewMode === 'grid' ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-blue-700 border-blue-200'} transition`}
                onClick={() => setViewMode('grid')}
                title={t('Kutu görünüm')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="4" y="4" width="7" height="7" rx="2"/><rect x="13" y="4" width="7" height="7" rx="2"/><rect x="4" y="13" width="7" height="7" rx="2"/><rect x="13" y="13" width="7" height="7" rx="2"/></svg>
              </button>
              <button
                className={`p-2 rounded-lg border ${viewMode === 'list' ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-blue-700 border-blue-200'} transition`}
                onClick={() => setViewMode('list')}
                title={t('Liste görünüm')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="4" y="6" width="16" height="2" rx="1"/><rect x="4" y="11" width="16" height="2" rx="1"/><rect x="4" y="16" width="16" height="2" rx="1"/></svg>
              </button>
            </div>
          </div>
          {/* Ürün listesi: Her zaman göster, sadece sepete ekleme kontrolü yap */}
          <div>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6" data-tour="product-grid">
                {paginated.length === 0 && loading && (
                  <div className="col-span-full flex flex-col items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mb-4"></div>
                    <p className="text-blue-700 text-lg font-medium">Ürünler yükleniyor...</p>
                  </div>
                )}
                {paginated.length === 0 && !loading && (
                  <div className="col-span-full flex flex-col items-center justify-center py-16">
                    <svg className="w-20 h-20 text-blue-200 mb-4" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m6 4.125 2.25 2.25m0 0 2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" /></svg>
                    <p className="text-blue-900 text-xl font-bold mb-2">Ürün Bulunamadı</p>
                    <p className="text-blue-500 text-sm">Sistemde henüz ürün bulunmamaktadır.</p>
                  </div>
                )}
                {paginated.map(product => (
                  <div key={product.id} className="bg-blue-50 rounded-xl shadow p-5 flex flex-col items-start">
                    {/* Ürün Resmi */}
                    <div className="w-full h-36 bg-white border border-blue-100 rounded-lg flex items-center justify-center overflow-hidden mb-3">
                      <img src={product.image_url || "/urunler/300x300.png"} alt={product.name || 'Ürün'} className="object-contain w-full h-full" />
                    </div>

                    {/* Ürün Adı */}
                    <div className="font-bold text-blue-900 text-sm break-words line-clamp-2 leading-tight mb-1" style={{display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}} title={product.name || t('Ürün')}>{product.name || t('Ürün Adı Yok')}</div>
                    
                    {/* Marka & Kategori */}
                    <div className="flex items-center gap-1.5 mb-2">
                      {product.marka && (
                        <span className="bg-indigo-100 text-indigo-700 text-xs px-1.5 py-0.5 rounded font-semibold">{product.marka}</span>
                      )}
                      <span className="text-xs text-blue-500 truncate" title={product.kategori || ''}>{product.kategori || ''}</span>
                    </div>

                    {/* Varyant sayısı */}
                    {product.variant_count > 0 && (
                      <div className="text-xs text-green-700 font-medium mb-1">{product.variant_count} varyant</div>
                    )}

                    {/* Fiyat Aralığı (custom varsa onu göster) */}
                    {user && (product.custom_price_min || product.sut_price_min) && (() => {
                      const pMin = product.custom_price_min || product.sut_price_min;
                      const pMax = product.custom_price_max || product.custom_price_min || product.sut_price_max;
                      return (
                        <div className="text-sm font-bold text-blue-800 mb-2">
                          {Number(pMin) === Number(pMax) ? formatPrice(pMin) : (
                            <>{formatPrice(pMin)} <span className="text-xs font-normal text-blue-500">-</span> {formatPrice(pMax)}</>
                          )}
                        </div>
                      );
                    })()}

                  <button
                    className="mt-auto px-4 py-2 rounded-lg font-bold shadow transition w-full bg-blue-700 text-white hover:bg-blue-800"
                    onClick={() => openModal(product)}
                  >
                    {t('Detay / Sepete Ekle')}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {paginated.length === 0 && loading && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mb-4"></div>
                  <p className="text-blue-700 text-lg font-medium">Ürünler yükleniyor...</p>
                </div>
              )}
              {paginated.length === 0 && !loading && (
                <div className="flex flex-col items-center justify-center py-16">
                  <svg className="w-20 h-20 text-blue-200 mb-4" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m6 4.125 2.25 2.25m0 0 2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" /></svg>
                  <p className="text-blue-900 text-xl font-bold mb-2">Ürün Bulunamadı</p>
                  <p className="text-blue-500 text-sm">Sistemde henüz ürün bulunmamaktadır.</p>
                </div>
              )}
              {paginated.map(product => (
                <div key={product.id} className="bg-blue-50 rounded-xl shadow p-4 flex flex-row items-center gap-4">
                  <div className="w-16 h-16 bg-white border border-blue-100 rounded flex items-center justify-center overflow-hidden aspect-square">
                    <img src={product.image_url || "/urunler/300x300.png"} alt={product.name || 'Ürün'} className="object-contain w-full h-full" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-blue-900 text-sm break-words line-clamp-1 leading-tight" title={product.name || 'Ürün'}>{product.name || 'Ürün Adı Yok'}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {product.marka && (
                        <span className="bg-indigo-100 text-indigo-700 text-xs px-1.5 py-0.5 rounded font-semibold">{product.marka}</span>
                      )}
                      <span className="text-xs text-blue-500 truncate">{product.kategori || ''}</span>
                      {product.variant_count > 0 && (
                        <span className="text-xs text-green-700 font-medium">{product.variant_count} varyant</span>
                      )}
                    </div>
                    {/* Fiyat Aralığı (custom varsa onu göster) */}
                    {user && (product.custom_price_min || product.sut_price_min) && (() => {
                      const pMin = product.custom_price_min || product.sut_price_min;
                      const pMax = product.custom_price_max || product.custom_price_min || product.sut_price_max;
                      return (
                        <div className="text-sm font-bold text-blue-800 mt-1">
                          {Number(pMin) === Number(pMax) ? formatPrice(pMin) : (
                            <>{formatPrice(pMin)} <span className="text-xs font-normal text-blue-500">-</span> {formatPrice(pMax)}</>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <button
                    className="ml-4 px-3 py-1.5 rounded-lg font-bold shadow transition bg-blue-700 text-white hover:bg-blue-800 text-sm"
                    onClick={() => openModal(product)}
                  >
                    {t('Detay')}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Daha Fazla Yükle Butonu */}
          {hasMoreForAllCategories && (
            <div className="flex justify-center mt-8">
              <button
                onClick={handleLoadMore}
                className="px-6 py-3 bg-blue-700 text-white font-semibold rounded-lg hover:bg-blue-800 transition-colors shadow-lg"
              >
                {loading ? 'Yükleniyor...' : 'Daha Fazla Yükle'}
              </button>
            </div>
          )}

        </div>
       
        {/* Toast Notification */}
        {showIletildiUyarisi && (
          <div className="fixed top-4 right-4 z-50 bg-orange-500 text-white px-6 py-3 rounded-lg shadow-lg border border-orange-600 animate-fade-in">
            <div className="flex items-center gap-2">
              <span className="text-lg">⚠️</span>
              <div>
                <div className="font-semibold">Giriş Yapmanız Gerekli</div>
                <div className="text-sm opacity-90">Sepete eklemek için lütfen giriş yapın veya kayıt olun</div>
              </div>
            </div>
          </div>
        )}

        {/* Sepete Ekle Modalı - Hiyerarşik: SUT → Parent Group → Ölçü/Barkod */}
        {modalProduct && (() => {
          // Seçili SUT koduna göre varyantları filtrele
          const filteredBySut = modalSelectedSut
            ? modalVariants.filter(v => {
                const codes = v.sut_codes ? v.sut_codes.split(',').map(s => s.trim()) : [];
                return codes.includes(modalSelectedSut);
              })
            : modalVariants;
          
          // Seçili SUT'taki benzersiz parent grupları
          const sutParentGroups = [...new Set(filteredBySut.filter(v => v.parent_group).map(v => v.parent_group))].sort();
          const hasParentGroups = sutParentGroups.length > 1;
          
          // Parent grup seçiliyse ona göre filtrele
          const filteredByParent = hasParentGroups && modalSelectedParentGroup
            ? filteredBySut.filter(v => v.parent_group === modalSelectedParentGroup)
            : filteredBySut;
          
          // Arama filtresi
          const searchLower = modalVariantSearch.toLowerCase();
          const displayVariants = searchLower
            ? filteredByParent.filter(v => 
                (v.size_label || '').toLowerCase().includes(searchLower) ||
                (v.olcu_clean || '').toLowerCase().includes(searchLower) ||
                (v.barcode || '').includes(searchLower) ||
                (v.variant_name || '').toLowerCase().includes(searchLower)
              )
            : filteredByParent;

          // Seçili SUT fiyatı
          const selectedSutInfo = modalSelectedSut && modalSutPrices[modalSelectedSut] ? modalSutPrices[modalSelectedSut] : null;

          return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col border border-blue-100 relative max-h-[92vh]">
              {/* Header */}
              <div className="p-5 pb-3 border-b border-gray-100">
                <button className="absolute top-3 right-4 text-gray-400 text-2xl font-bold hover:text-red-500 transition" onClick={() => { trackProductDetailClose(modalProduct); setModalProduct(null); }}>&times;</button>
                <div className="font-bold text-blue-900 text-xl mb-1 pr-8">{modalProduct.name || 'Ürün'}</div>
                <div className="flex flex-wrap items-center gap-2">
                  {modalProduct.marka && <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded font-semibold">{modalProduct.marka}</span>}
                  <span className="text-xs text-blue-500">{modalProduct.kategori || ''}</span>
                  <span className="text-xs text-gray-400">{modalProduct.variant_count} varyant</span>
                </div>
                {/* Seçili varyant barkod gösterimi */}
                {modalSelectedVariant && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="bg-emerald-50 border border-emerald-300 text-emerald-800 text-xs font-mono px-2 py-1 rounded font-bold">
                      Barkod: {modalSelectedVariant.barcode}
                    </span>
                    <span className="text-xs text-gray-600">{modalSelectedVariant.size_label}</span>
                  </div>
                )}
              </div>

              {/* Loading */}
              {modalLoading && (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
                  <span className="ml-3 text-blue-700">Varyantlar yükleniyor...</span>
                </div>
              )}

              {!modalLoading && (
              <div className="flex-1 overflow-y-auto p-5 pt-3 flex flex-col gap-3">
                {/* Giriş yapılmamışsa uyarı */}
                {!user && (
                  <div className="p-2.5 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800">
                    <span className="font-semibold">Uyarı:</span> Sepete eklemek için giriş yapmanız gerekmektedir.
                  </div>
                )}

                {/* Seçili Varyant Detayı + Adet + Açıklama (ÜSTTE) */}
                {modalSelectedVariant && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm font-bold text-emerald-900">{modalSelectedVariant.size_label || modalSelectedVariant.variant_name}</div>
                        <div className="text-xs text-gray-500 font-mono mt-0.5">Barkod: {modalSelectedVariant.barcode}</div>
                      </div>
                      {user && (() => {
                        const hasCustom = modalSelectedVariant.custom_price != null;
                        const displayPrice = hasCustom ? Number(modalSelectedVariant.custom_price) : (selectedSutInfo?.price || 0);
                        return displayPrice > 0 ? (
                          <div className="text-right">
                            <div className="text-lg font-bold text-blue-900">{formatPrice(displayPrice)}</div>
                            {hasCustom && selectedSutInfo?.price > 0 && (
                              <div className="text-[10px] text-gray-400 line-through">{formatPrice(selectedSutInfo.price)}</div>
                            )}
                            {modalQty > 1 && (
                              <div className="text-xs text-blue-500">Toplam: {formatPrice(displayPrice * modalQty)}</div>
                            )}
                          </div>
                        ) : null;
                      })()}
                    </div>
                    <div className="flex gap-3">
                      <div className="w-20">
                        <label className="block text-xs font-semibold text-emerald-800 mb-1">Adet</label>
                        <input type="number" min="1"
                          className="w-full px-2 py-2 border-2 border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-400 text-emerald-900 bg-white text-sm text-center"
                          value={modalQty} onChange={e => setModalQty(Number(e.target.value))} />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-semibold text-emerald-800 mb-1">Açıklama</label>
                        <input type="text"
                          className="w-full px-3 py-2 border-2 border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-400 text-emerald-900 bg-white text-sm"
                          placeholder="Ek not..." value={modalDesc} onChange={e => setModalDesc(e.target.value)} />
                      </div>
                    </div>
                  </div>
                )}

                {/* ADIM 1: SUT Kodu Seçimi */}
                {modalSutCodes.length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-blue-900 mb-2">
                      1. SUT Kodu
                      <span className="text-xs text-gray-400 font-normal ml-2">({modalSutCodes.length} kod)</span>
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {modalSutCodes.map(sut => {
                        const info = modalSutPrices[sut];
                        return (
                          <button key={sut}
                            className={`px-3 py-1.5 rounded-lg text-xs font-mono border-2 transition-all ${
                              modalSelectedSut === sut
                                ? 'bg-emerald-600 text-white border-emerald-600 shadow'
                                : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:border-emerald-400'
                            }`}
                            onClick={() => {
                              setModalSelectedSut(sut);
                              setModalSelectedParentGroup('');
                              setModalSelectedVariant(null);
                            }}
                          >
                            <span className="font-bold">{sut}</span>
                            {user && info && info.price > 0 && (
                              <span className={`ml-1.5 font-bold ${modalSelectedSut === sut ? 'text-emerald-100' : 'text-blue-700'}`}>
                                {formatPrice(info.price)}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {/* SUT Açıklaması */}
                    {selectedSutInfo && selectedSutInfo.description && (
                      <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-2 text-xs text-gray-700">
                        {selectedSutInfo.description}
                      </div>
                    )}
                  </div>
                )}

                {/* ADIM 2: Parent Grup Seçimi (varsa) */}
                {modalSelectedSut && hasParentGroups && (
                  <div>
                    <label className="block text-sm font-semibold text-blue-900 mb-2">
                      2. Alt Grup
                      <span className="text-xs text-gray-400 font-normal ml-2">({sutParentGroups.length} grup)</span>
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {sutParentGroups.map(pg => {
                        const count = filteredBySut.filter(v => v.parent_group === pg).length;
                        return (
                          <button key={pg}
                            className={`px-3 py-1.5 rounded-lg text-xs border-2 transition-all ${
                              modalSelectedParentGroup === pg
                                ? 'bg-blue-600 text-white border-blue-600 shadow'
                                : 'bg-blue-50 text-blue-700 border-blue-200 hover:border-blue-400'
                            }`}
                            onClick={() => {
                              setModalSelectedParentGroup(pg);
                              setModalSelectedVariant(null);
                            }}
                          >
                            <span className="font-semibold">{pg}</span>
                            <span className={`ml-1 ${modalSelectedParentGroup === pg ? 'text-blue-200' : 'text-gray-400'}`}>({count})</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ADIM 3: Varyant Seçimi (Ölçü + Barkod tablosu) */}
                {modalSelectedSut && (!hasParentGroups || modalSelectedParentGroup) && (
                  <div>
                    <label className="block text-sm font-semibold text-blue-900 mb-2">
                      {hasParentGroups ? '3.' : '2.'} Varyant Seçin
                      <span className="text-xs text-gray-400 font-normal ml-2">({displayVariants.length} seçenek)</span>
                    </label>
                    
                    {/* Arama */}
                    {filteredByParent.length > 6 && (
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                        placeholder="Ara... (ölçü, barkod)"
                        value={modalVariantSearch}
                        onChange={e => setModalVariantSearch(e.target.value)}
                      />
                    )}

                    {/* Varyant Listesi */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[35vh] overflow-y-auto">
                      {displayVariants.length === 0 ? (
                        <div className="p-4 text-center text-gray-400 text-sm">Varyant bulunamadı</div>
                      ) : (
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="text-left px-3 py-2 text-gray-600 font-semibold">Ölçü / Varyant</th>
                              <th className="text-left px-3 py-2 text-gray-600 font-semibold">Barkod</th>
                              <th className="text-left px-3 py-2 text-gray-600 font-semibold">Ref</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayVariants.map(v => {
                              const isSelected = modalSelectedVariant?.id === v.id;
                              return (
                                <tr key={v.id}
                                  className={`cursor-pointer border-t border-gray-100 transition-all ${
                                    isSelected
                                      ? 'bg-emerald-50 ring-1 ring-emerald-400'
                                      : 'hover:bg-blue-50'
                                  }`}
                                  onClick={() => setModalSelectedVariant(v)}
                                >
                                  <td className="px-3 py-2">
                                    <div className={`font-medium ${isSelected ? 'text-emerald-800' : 'text-gray-800'}`}>
                                      {v.olcu_clean || v.size_label || v.variant_name}
                                    </div>
                                    {v.parent_group && !hasParentGroups && (
                                      <div className="text-[10px] text-gray-400">{v.parent_group}</div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 font-mono text-gray-500">{v.barcode}</td>
                                  <td className="px-3 py-2 text-gray-400">{v.ref_code || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                )}

              </div>
              )}

              {/* Footer Butonlar */}
              <div className="p-4 pt-3 border-t border-gray-100 flex gap-2">
                <button
                  className="flex-1 px-3 py-3 rounded-xl font-bold shadow transition bg-emerald-600 text-white hover:bg-emerald-700 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() => handleAddToCart(true)}
                  disabled={!modalSelectedVariant}
                >
                  Ekle ve Devam Et
                </button>
                <button
                  className="flex-1 px-3 py-3 rounded-xl font-bold shadow transition bg-blue-700 text-white hover:bg-blue-800 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() => handleAddToCart(false)}
                  disabled={!modalSelectedVariant}
                >
                  Ekle ve Kapat
                </button>
              </div>
            </div>
          </div>
          );
        })()}

        {/* Tur butonu artık FloatingHelpButton içinde (App.js'de global) */}

        {showPasswordChange && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" style={{backdropFilter:'blur(4px)'}}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-blue-100 overflow-hidden">
              <div className="bg-gradient-to-r from-[#0d2b52] to-[#1a4a8a] p-6 text-center">
                <div className="w-14 h-14 mx-auto bg-white/10 rounded-full flex items-center justify-center mb-3">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" /></svg>
                </div>
                <h2 className="text-xl font-bold text-white">Şifre Değiştirme</h2>
                <p className="text-blue-200 text-sm mt-1">Güvenliğiniz için geçici şifrenizi değiştirmeniz gerekmektedir.</p>
              </div>
              <form className="p-6 space-y-4" onSubmit={async e => {
                e.preventDefault();
                setPwChangeError('');
                if (!pwChangeNew || !pwChangeConfirm) { setPwChangeError('Tüm alanları doldurun.'); return; }
                if (pwChangeNew.length < 6) { setPwChangeError('Yeni şifre en az 6 karakter olmalıdır.'); return; }
                if (pwChangeNew !== pwChangeConfirm) { setPwChangeError('Şifreler eşleşmiyor.'); return; }
                if (pwChangeCurrent === pwChangeNew) { setPwChangeError('Yeni şifre mevcut şifreden farklı olmalıdır.'); return; }
                setPwChangeLoading(true);
                try {
                  const res = await authFetch('/api/change-password', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: pwChangeEmail, current_password: pwChangeCurrent, new_password: pwChangeNew })
                  });
                  const data = await res.json();
                  if (data.success) {
                    // Şifre değişti, kullanıcıyı oturum aç
                    if (pwChangePendingUser) {
                      const u = { ...pwChangePendingUser };
                      delete u.must_change_password;
                      localStorage.setItem('user', JSON.stringify(u));
                      login({ ...u, role: u.rol });
                      setFirma(u.firma || ''); setEmail(u.email || ''); setTelefon(u.telefon || ''); setYetkili(u.yetkili || '');
                      setBilgilerIletildi(true); setShowIletildiUyarisi(false); setRightPanelView('cart');
                    }
                    setShowPasswordChange(false);
                    setPwChangeNew(''); setPwChangeConfirm(''); setPwChangeError('');
                    setPwChangePendingUser(null);
                  } else {
                    setPwChangeError(data.error || 'Şifre değiştirilemedi.');
                  }
                } catch { setPwChangeError('Sunucuya bağlanılamadı.'); }
                setPwChangeLoading(false);
              }}>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong>Geçici şifre ile giriş yaptınız.</strong> Devam etmek için yeni bir şifre belirlemeniz zorunludur. 3 gün içinde şifre değiştirilmezse hesabınız pasif moda alınacaktır.
                  </p>
                </div>

                {pwChangeError && (
                  <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs text-center">{pwChangeError}</div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">E-posta</label>
                  <input type="text" value={pwChangeEmail} readOnly className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500" />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Yeni Şifre</label>
                  <input type="password" value={pwChangeNew} onChange={e => setPwChangeNew(e.target.value)} placeholder="En az 6 karakter"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400" required />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Yeni Şifre (Tekrar)</label>
                  <input type="password" value={pwChangeConfirm} onChange={e => setPwChangeConfirm(e.target.value)} placeholder="Şifreyi tekrar girin"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400" required />
                </div>

                <button type="submit" disabled={pwChangeLoading}
                  className="w-full py-3 bg-gradient-to-r from-[#0d2b52] to-[#1a4a8a] text-white rounded-xl font-bold text-sm hover:from-[#0a2244] hover:to-[#153d73] transition shadow-md disabled:opacity-50">
                  {pwChangeLoading ? 'Değiştiriliyor...' : 'Şifremi Değiştir ve Giriş Yap'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Mobil Sepet Floating Icon */}
        <div className="lg:hidden fixed bottom-6 right-6 z-40">
          <button
            onClick={() => setShowMobileCart(true)}
            className="bg-blue-700 text-white p-4 rounded-full shadow-lg hover:bg-blue-800 transition-all duration-200 relative"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
            </svg>
            {cart.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
                {cart.length}
              </span>
            )}
          </button>
        </div>

        {/* Mobil Sepet Modal */}
        {showMobileCart && (
          <div className="lg:hidden fixed inset-0 z-50 flex items-end justify-center bg-black/40" onClick={(e) => { if (e.target === e.currentTarget) setShowMobileCart(false); }}>
            <div className="bg-white rounded-t-2xl shadow-2xl w-full max-h-[85vh] overflow-hidden animate-slide-up" style={{animation: 'slideUp 0.3s ease-out'}}>
              <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-bold text-blue-900">{t('Sepetiniz')} {cart.length > 0 && <span className="text-sm font-normal text-gray-500">({cart.length})</span>}</h3>
                  {cart.length > 0 && (
                    <button
                      onClick={() => {
                        if (!window.confirm('Sepeti tamamen temizlemek istediğinize emin misiniz?')) return;
                        setCart([]);
                        localStorage.removeItem('teklifCart');
                      }}
                      className="text-xs text-red-600 hover:text-red-800 font-medium px-2 py-1 rounded border border-red-200 hover:bg-red-50 transition"
                      title="Sepeti Temizle"
                    >
                      Temizle
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowMobileCart(false)}
                  className="text-gray-400 hover:text-gray-700 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition text-2xl font-bold"
                >
                  &times;
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {cart.length === 0 ? (
                  <div className="text-center text-blue-700 py-8">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
                    </svg>
                    <p className="text-lg font-semibold">{t('Sepetiniz boş')}</p>
                    <p className="text-sm text-gray-500">{t('Ürün eklemek için ürün listesinden seçim yapın')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cart.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-blue-50 rounded-lg p-3">
                        <div className="w-12 h-12 bg-white border border-blue-100 rounded flex items-center justify-center overflow-hidden aspect-square">
                          <img src={item.image_url || "/urunler/300x300.png"} alt={item.name} className="object-contain w-full h-full" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-blue-900 text-sm break-words line-clamp-2 min-h-[32px] leading-tight" style={{display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}} title={item.name}>{item.name}</div>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            {item.sutCode && item.sutCode !== '' && item.sutCode !== '-' && (
                              <span className="inline-block bg-emerald-50 text-emerald-700 text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded border border-emerald-200">{item.sutCode}</span>
                            )}
                            {formatMeasureDisplay(item.measure) && (
                              <span className="inline-block bg-blue-100 text-blue-800 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-blue-200">{formatMeasureDisplay(item.measure)}</span>
                            )}
                            <div className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-0.5">
                              <input
                                type="number"
                                min={1}
                                value={item.qty}
                                onChange={e => {
                                  const val = Math.max(1, Number(e.target.value));
                                  setCart(prev => prev.map((c, i) => i === idx ? { ...c, qty: val } : c));
                                }}
                                className="w-10 text-xs text-blue-900 bg-transparent outline-none border-none text-center"
                                style={{minWidth:'32px'}}
                              />
                              <span className="text-[10px] text-blue-700 font-medium">{t('adet')}</span>
                            </div>
                          </div>
                          {item.barcode && item.barcode !== '' && (
                            <div className="text-[10px] text-gray-400 font-mono mt-0.5">Barkod: {item.barcode}</div>
                          )}
                          {item.price > 0 && <div className="text-xs font-bold text-blue-800 mt-1">{formatPrice(item.price)} <span className="font-normal text-blue-500">x {item.qty} = {formatPrice(item.price * item.qty)}</span></div>}
                          {item.desc && <div className="text-xs text-blue-500 truncate mt-1">{item.desc}</div>}
                        </div>
                        <div className="flex flex-col gap-1 justify-center items-end h-full">
                          <button className="text-blue-600 hover:text-blue-900 text-lg font-bold px-2" onClick={() => { openModal({ ...item }, idx); setShowMobileCart(false); }} title={t('Düzenle')}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.1 2.1 0 1 1 2.97 2.97L7.5 18.79l-4 1 1-4 12.362-12.303Z" /></svg>
                          </button>
                          <button className="text-red-600 hover:text-red-900 text-lg font-bold px-2" onClick={() => handleRemoveFromCart(idx)} title={t('Kaldır')}>&times;</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {cart.length > 0 && (
                <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-3">
                  {/* Ödeme Yöntemi ve Not */}
                  <div className="space-y-2">
                    <select className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white" value={cartPaymentMethod} onChange={e => setCartPaymentMethod(e.target.value)}>
                      <option value="">Ödeme Yöntemi (opsiyonel)</option>
                      <option value="havale">Havale / EFT</option>
                      <option value="kredi_karti">Kredi Kartı</option>
                      <option value="diger">Diğer</option>
                    </select>
                    {cartPaymentMethod === 'diger' && (
                      <textarea className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" rows={2} placeholder="Ödeme detayını yazın..." value={cartPaymentNotes} onChange={e => setCartPaymentNotes(e.target.value)} />
                    )}
                  </div>
                  <button
                    className="w-full px-4 py-3 rounded-lg bg-blue-700 text-white font-bold shadow hover:bg-blue-800 transition"
                    onClick={async () => {
                      setShowMobileCart(false);
                      // Giriş yapmış kullanıcı veya bilgileri iletilmiş misafir kontrolü
                      if (!user && !bilgilerIletildi) {
                        alert(t('Lütfen önce giriş yapın veya firma bilgilerinizi eksiksiz iletin'));
                        return;
                      }
                      try {
                        let activeFirmaId, activeFirma, activeSehir, activeTelefon, activeEmail;

                        if (user && selectedFirma) {
                          // Giriş yapmış kullanıcı + seçili firma
                          activeFirmaId = selectedFirma.id;
                          activeFirma = selectedFirma.firma;
                          activeSehir = selectedFirma.sehir || user.sehir || sehir;
                          activeTelefon = selectedFirma.telefon || user.telefon || telefon;
                          activeEmail = selectedFirma.email || user.email || email;
                        } else {
                          // Giriş yapmış kullanıcı (firma seçimsiz) veya misafir
                          const useFirma = user?.firma || firma;
                          const useSehir = user?.sehir || sehir;
                          const useTelefon = user?.telefon || telefon;
                          const useEmail = user?.email || email;
                          const firmaResp = await fetch('/api/guest-firmalar', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ firma: useFirma, sehir: useSehir, telefon: useTelefon, email: useEmail })
                          });
                          if (!firmaResp.ok) {
                            let errData = {};
                            try { errData = await firmaResp.json(); } catch {}
                            throw new Error(errData.error || 'Firma bilgileri kaydedilemedi');
                          }
                          const firmaResult = await firmaResp.json();
                          activeFirmaId = firmaResult.firmaId;
                          activeFirma = useFirma;
                          activeSehir = useSehir;
                          activeTelefon = useTelefon;
                          activeEmail = useEmail;
                        }

                        const resp = await fetch('/api/guest-offers', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            firma_id: activeFirmaId,
                            firma: activeFirma, sehir: activeSehir, telefon: activeTelefon, email: activeEmail,
                            payment_method: cartPaymentMethod || undefined,
                            payment_notes: cartPaymentNotes || undefined,
                            items: cart.map(item => ({
                              name: item.name, measure: item.measure, qty: item.qty, desc: item.desc || '',
                              sutCode: item.sutCode || '', price: item.price || 0, marka: item.marka || '',
                              kategori: item.kategori || '', barcode: item.barcode || '', code: item.code || ''
                            }))
                          })
                        });
                        if (!resp.ok) {
                          let errData = {};
                          try { errData = await resp.json(); } catch {}
                          throw new Error(errData.error || 'Sipariş kaydedilemedi');
                        }
                        trackOrderCreate({ item_count: cart.length, total_qty: cart.reduce((s, i) => s + i.qty, 0), source: 'mobile_cart' });
                        alert('Sipariş başarıyla kaydedildi!');
                        setCart([]); setCartPaymentMethod(''); setCartPaymentNotes('');
                        localStorage.removeItem('teklifCart');
                      } catch (err) {
                        alert('Sipariş kaydedilemedi: ' + (err.message || err));
                      }
                    }}
                  >
                    {t('Siparişi Tamamla')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bayi Başvuru Modalı */}
        {showBayiModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-0 w-full max-w-xl max-h-[90vh] flex flex-col border border-[#e3e6ef] relative overflow-hidden">
              <button className="absolute top-4 right-4 text-[#143b6b] text-2xl font-bold z-10" onClick={() => setShowBayiModal(false)}>&times;</button>
              {bayiStep === 1 && (
                <form onSubmit={handleBayiFormSubmit} className="flex flex-col h-full overflow-y-auto">
                  <div className="flex-shrink-0 px-8 pt-8 pb-2">
                    <div className="text-2xl font-bold text-[#143b6b] mb-2">{t('bayiBasvuru')}</div>
                    <div className="flex gap-2 mb-4">
                      {BAYI_TABS.map((tab, idx) => (
                        <button type="button" key={tab.name} className={`flex-1 py-2 rounded-lg font-semibold text-base transition border ${bayiTab === idx ? 'bg-[#143b6b] text-white border-[#143b6b]' : 'bg-[#e3e6ef] text-[#143b6b] border-[#e3e6ef]'}`} onClick={() => setBayiTab(idx)}>{tab.name}</button>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto px-8 pb-8 pt-0">
                    {BAYI_FIELDS.filter(f => BAYI_TABS[bayiTab].fields.includes(f.name)).map(field => (
                      <div key={field.name} className="mb-4">
                        <label className="block text-sm font-semibold text-[#143b6b] mb-1">
                          {t('applyFields')[field.name] || field.label || field.name}
                        </label>
                        <input
                          type={field.type}
                          className="w-full px-4 py-2 rounded-lg border border-[#e3e6ef] focus:ring-2 focus:ring-[#143b6b] text-[#1e2a44] transition placeholder:text-[#bfc9e0]"
                          value={bayiForm[field.name] || ''}
                          onChange={e => setBayiForm(f => ({ ...f, [field.name]: e.target.value }))}
                          required={field.required}
                          placeholder={t('applyFields')[field.name] || field.label || field.name}
                        />
                      </div>
                    ))}
                    {bayiTab === 1 && (
                      <div className="mt-4">
                        <label className="flex items-start gap-2">
                          <input 
                            type="checkbox" 
                            checked={bayiSozlesmeOnay} 
                            onChange={e => setBayiSozlesmeOnay(e.target.checked)} 
                            required 
                            className="mt-1"
                          />
                          <div className="text-xs text-[#4b587c]">
                            <span>{t('partnerlikSozlesmeOnay')}</span>
                            <button 
                              type="button" 
                              className="text-[#143b6b] font-semibold hover:underline ml-1"
                              onClick={() => setShowSozlesmeModal(true)}
                            >
                              {t('sozlesmeyiGoruntule')}
                            </button>
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 flex gap-2 px-8 pb-8 pt-2 bg-white border-t border-[#e3e6ef]">
                    {bayiTab > 0 && (
                      <button type="button" className="flex-1 px-4 py-3 rounded-lg bg-[#e3e6ef] text-[#143b6b] font-bold text-base border border-[#e3e6ef] hover:bg-[#e3e6ef] transition" onClick={() => setBayiTab(bayiTab - 1)}>{t('geri')}</button>
                    )}
                    <button type="submit" className="flex-1 px-4 py-3 rounded-lg bg-[#143b6b] text-white font-bold text-base shadow-lg hover:bg-[#1e2a44] hover:text-white border border-[#143b6b] transition disabled:opacity-60 disabled:cursor-not-allowed" disabled={bayiTab === 1 && !bayiSozlesmeOnay}>{bayiTab === BAYI_TABS.length - 1 ? t('basvuruGonder') : t('ileri')}</button>
                  </div>
                </form>
              )}
              {bayiStep === 2 && (
                <div className="flex flex-col items-center gap-4">
                  <div className="text-xl font-bold text-[#143b6b]">{t('sozlesmeGonderildi')}</div>
                  <div className="text-[#4b587c] text-center">{t('sozlesmeDesc')}</div>
                  <input type="file" multiple className="mt-2" onChange={handleBayiFileChange} />
                  <button className="mt-4 w-full px-4 py-3 rounded-lg bg-[#143b6b] text-white font-bold text-lg shadow-lg hover:bg-[#1e2a44] hover:text-white border border-[#143b6b] transition text-base" onClick={handleBayiComplete} disabled={bayiFiles.length === 0}>{t('tamamla')}</button>
                </div>
              )}
              {bayiStep === 3 && (
                <div className="flex flex-col items-center gap-4">
                  <div className="text-2xl font-bold text-[#143b6b]">{t('basvuruAlindi')}</div>
                  <div className="text-[#4b587c] text-center">{t('basvuruAlindiDesc')}</div>
                  <button className="mt-4 w-full px-4 py-3 rounded-lg bg-[#143b6b] text-white font-bold text-lg shadow-lg hover:bg-[#1e2a44] hover:text-white border border-[#143b6b] transition text-base" onClick={() => setShowBayiModal(false)}>{t('kapat')}</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sözleşme Modalı */}
        {showSozlesmeModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-4xl max-h-[90vh] flex flex-col border border-[#e3e6ef] relative overflow-hidden">
              <button className="absolute top-4 right-4 text-[#143b6b] text-2xl font-bold z-10" onClick={() => setShowSozlesmeModal(false)}>&times;</button>
              <div className="flex-shrink-0 mb-4">
                <div className="text-2xl font-bold text-[#143b6b]">{t('partnerlikSozlesmeTitle')}</div>
              </div>
              <div className="flex-1 overflow-y-auto">
                <div className="text-[#4b587c] text-sm leading-relaxed whitespace-pre-line">
                  {t('partnerlikSozlesmeText')}
                </div>
              </div>
              <div className="flex-shrink-0 mt-4 pt-4 border-t border-[#e3e6ef]">
                <button className="w-full px-4 py-3 rounded-lg bg-[#143b6b] text-white font-bold text-base shadow-lg hover:bg-[#1e2a44] hover:text-white border border-[#143b6b] transition" onClick={() => setShowSozlesmeModal(false)}>{t('sozlesmeyiKapat')}</button>
              </div>
            </div>
          </div>
        )}

          </>
          )}
        
      </div>
       {/* Desktop Firma Bilgileri ve Sepet Alanı - Sadece desktop'ta ve ürün görünümünde görünür */}
       <div className={`${mainView === 'products' ? 'hidden lg:block' : 'hidden'} w-[340px] max-w-full lg:sticky lg:top-24 lg:self-start right-0 mt-0 bg-white rounded-2xl shadow-xl p-6 h-fit border-l border-blue-100`} data-tour="cart-sidebar">
          {/* Giriş yapılmamışsa: Giriş/Kayıt paneli */}
          {!user ? (
            <div id="login-panel">
              {showIletildiUyarisi && !user && (
                <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-center text-sm font-medium">
                  Sipariş vermek için lütfen giriş yapın veya kayıt olun.
                </div>
              )}

              {/* Sadece Giriş Yap Formu */}
              <form className="flex flex-col gap-3" onSubmit={async e => {
                e.preventDefault();
                setLoginError('');
                setLoginLoading(true);
                try {
                  const res = await fetch('/api/login', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: loginEmail, password: loginPassword })
                  });
                  if (res.ok) {
                    const userData = await res.json();
                    trackLogin({ email: loginEmail, firma: userData.firma, role: userData.rol, source: 'product_page_sidebar' });
                    if (userData.must_change_password) {
                      setPwChangeEmail(loginEmail);
                      setPwChangeCurrent(loginPassword);
                      setPwChangePendingUser(userData);
                      setShowPasswordChange(true);
                    } else {
                      localStorage.setItem('user', JSON.stringify(userData));
                      login({ ...userData, role: userData.rol });
                      setFirma(userData.firma || '');
                      setEmail(userData.email || '');
                      setTelefon(userData.telefon || '');
                      setYetkili(userData.yetkili || '');
                      setBilgilerIletildi(true);
                      setShowIletildiUyarisi(false);
                      setRightPanelView('cart');
                    }
                  } else {
                    const data = await res.json();
                    setLoginError(data.error || 'Giriş başarısız.');
                  }
                } catch {
                  setLoginError('Sunucuya bağlanılamadı.');
                }
                setLoginLoading(false);
              }}>
                <div className="text-center mb-2">
                  <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-3">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" /></svg>
                  </div>
                  <div className="text-lg font-bold text-blue-900">{t('Üye Girişi')}</div>
                  <div className="text-xs text-blue-600 mt-1">Sipariş vermek için giriş yapın</div>
                </div>
                {loginError && <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs text-center">{loginError}</div>}
                <input
                  type="email"
                  className="px-3 py-2.5 border border-blue-200 rounded-lg text-blue-900 bg-white shadow-sm text-sm"
                  placeholder="E-Posta *"
                  value={loginEmail}
                  onChange={e => setLoginEmail(e.target.value)}
                  required
                />
                <input
                  type="password"
                  className="px-3 py-2.5 border border-blue-200 rounded-lg text-blue-900 bg-white shadow-sm text-sm"
                  placeholder="Şifre *"
                  value={loginPassword}
                  onChange={e => setLoginPassword(e.target.value)}
                  required
                />
                <button
                  type="submit"
                  disabled={loginLoading}
                  className="bg-blue-700 text-white px-4 py-2.5 rounded-lg hover:bg-blue-800 transition font-semibold text-sm disabled:opacity-60"
                >
                  {loginLoading ? 'Giriş yapılıyor...' : t('Giriş Yap')}
                </button>
              </form>

              {/* Kayıt Ol Linki - Partner Ol dialog'unu açar */}
              <div className="mt-4 pt-4 border-t border-blue-100 text-center">
                <div className="text-xs text-gray-500 mb-2">Henüz üye değil misiniz?</div>
                <button
                  className="w-full px-4 py-2 border-2 border-[#143b6b] text-[#143b6b] rounded-lg font-semibold hover:bg-[#143b6b] hover:text-white transition text-sm"
                  onClick={() => setShowBayiModal(true)}
                >
                  Kayıt Ol
                </button>
              </div>

              {/* Yardım Merkezi Linki */}
              <div className="mt-4 pt-4 border-t border-blue-100">
                <button
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 rounded-lg font-semibold hover:from-blue-100 hover:to-indigo-100 transition text-sm border border-blue-200"
                  onClick={() => navigate('/yardim')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                  </svg>
                  Yardım Merkezi
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Giriş yapılmışsa: Panel görünüm seçici */}
              {/* === SEPET GÖRÜNÜMÜ === */}
              {rightPanelView === 'cart' && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-xl font-bold text-blue-900">{t('Sepetiniz')}</div>
                    {cart.length > 0 && (
                      <button
                        onClick={() => { setCart([]); localStorage.removeItem('teklifCart'); }}
                        className="text-sm text-red-600 hover:text-red-800 font-medium px-2 py-1 rounded border border-red-200 hover:bg-red-50 transition"
                      >
                        Sepeti Temizle
                      </button>
                    )}
                  </div>
                  {cart.length === 0 ? (
                    <div className="text-center py-8">
                      <svg className="w-16 h-16 mx-auto text-blue-200 mb-3" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" /></svg>
                      <div className="text-blue-700 text-sm">{t('Sepetiniz boş')}.</div>
                      <div className="text-blue-400 text-xs mt-1">Ürünlere tıklayarak sepetinize ekleyin</div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-4">
                      {cart.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 bg-blue-50 rounded-lg p-3">
                          <div className="w-12 h-12 bg-white border border-blue-100 rounded flex items-center justify-center overflow-hidden aspect-square">
                            <img src={item.image_url || "/urunler/300x300.png"} alt={item.name} className="object-contain w-full h-full" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-blue-900 text-sm break-words line-clamp-2 min-h-[32px] leading-tight" style={{display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}} title={item.name}>{item.name}</div>
                            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                              {item.sutCode && item.sutCode !== '' && item.sutCode !== '-' && (
                                <span className="inline-block bg-emerald-50 text-emerald-700 text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded border border-emerald-200">{item.sutCode}</span>
                              )}
                              {formatMeasureDisplay(item.measure) && (
                                <span className="inline-block bg-blue-100 text-blue-800 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-blue-200">{formatMeasureDisplay(item.measure)}</span>
                              )}
                              <div className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-0.5">
                                <input type="number" min={1} value={item.qty} onChange={e => { const val = Math.max(1, Number(e.target.value)); setCart(prev => prev.map((c, i) => i === idx ? { ...c, qty: val } : c)); }} className="w-10 text-xs text-blue-900 bg-transparent outline-none border-none text-center" style={{minWidth:'32px'}} />
                                <span className="text-[10px] text-blue-700 font-medium">{t('adet')}</span>
                              </div>
                            </div>
                            {item.barcode && item.barcode !== '' && (
                              <div className="text-[10px] text-gray-400 font-mono mt-0.5">Barkod: {item.barcode}</div>
                            )}
                            {item.price > 0 && <div className="text-xs font-bold text-blue-800 mt-1">{formatPrice(item.price)} <span className="font-normal text-blue-500">x {item.qty} = {formatPrice(item.price * item.qty)}</span></div>}
                            {item.desc && <div className="text-xs text-blue-500 truncate mt-1">{item.desc}</div>}
                          </div>
                          <div className="flex flex-col gap-1 justify-center items-end h-full">
                            <button className="text-blue-600 hover:text-blue-900 text-lg font-bold px-2" onClick={() => openModal({ ...item }, idx)} title={t('Düzenle')}>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.1 2.1 0 1 1 2.97 2.97L7.5 18.79l-4 1 1-4 12.362-12.303Z" /></svg>
                            </button>
                            <button className="text-red-600 hover:text-red-900 text-lg font-bold px-2" onClick={() => handleRemoveFromCart(idx)} title={t('Kaldır')}>&times;</button>
                          </div>
                        </div>
                      ))}
                      {/* Sepet Toplam */}
                      {(() => {
                        const total = cart.reduce((sum, item) => sum + ((item.price || 0) * (item.qty || 1)), 0);
                        if (total <= 0) return null;
                        return (
                          <div className="mt-3 p-3 bg-blue-100 border border-blue-200 rounded-xl flex items-center justify-between">
                            <span className="text-sm font-semibold text-blue-800">Toplam</span>
                            <span className="text-lg font-bold text-blue-900">{formatPrice(total)}</span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {cart.length > 0 && (
                    <>
                      {/* Ödeme Yöntemi ve Not */}
                      <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                        <div className="text-xs font-semibold text-gray-600">Ödeme Bilgileri</div>
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-1">Ödeme Yöntemi</label>
                          <select className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-400" value={cartPaymentMethod} onChange={e => setCartPaymentMethod(e.target.value)}>
                            <option value="">Seçiniz (opsiyonel)</option>
                            <option value="havale">Havale / EFT</option>
                            <option value="kredi_karti">Kredi Kartı</option>
                            <option value="diger">Diğer</option>
                          </select>
                        </div>
                        {cartPaymentMethod === 'diger' && (
                          <div>
                            <label className="block text-[10px] text-gray-500 mb-1">Ödeme Notu</label>
                            <textarea className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400" rows={2} placeholder="Ödeme detayını yazın..." value={cartPaymentNotes} onChange={e => setCartPaymentNotes(e.target.value)} />
                          </div>
                        )}
                      </div>
                      {/* Seçili firma bilgisi göster */}
                      {selectedFirma && (
                        <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 0h.008v.008h-.008V7.5Z" />
                            </svg>
                            <div className="text-xs">
                              <span className="text-amber-700 font-medium">Sipariş firması: </span>
                              <span className="text-amber-900 font-bold">{selectedFirma.firma}</span>
                              {selectedFirma.sehir && <span className="text-amber-600 ml-1">({selectedFirma.sehir})</span>}
                            </div>
                          </div>
                        </div>
                      )}
                      <button
                        className="mt-4 w-full px-4 py-3 rounded-lg bg-blue-700 text-white font-bold shadow hover:bg-blue-800 transition"
                        onClick={async () => {
                          if (!user) { alert('Lütfen giriş yapın.'); return; }
                          try {
                            // selectedFirma varsa onu kullan, yoksa eski akış (yeni firma oluştur)
                            let activeFirmaId;
                            let activeFirma, activeSehir, activeTelefon, activeEmail;

                            if (selectedFirma) {
                              activeFirmaId = selectedFirma.id;
                              activeFirma = selectedFirma.firma;
                              activeSehir = selectedFirma.sehir || user.sehir || sehir;
                              activeTelefon = selectedFirma.telefon || user.telefon || telefon;
                              activeEmail = selectedFirma.email || user.email || email;
                            } else {
                              // Eski akış: yeni firma oluştur
                              const firmaResp = await fetch('/api/guest-firmalar', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ firma: user.firma || firma, sehir: user.sehir || sehir, telefon: user.telefon || telefon, email: user.email || email })
                              });
                              if (!firmaResp.ok) { const errData = await firmaResp.json().catch(() => ({})); throw new Error(errData.error || 'Firma bilgileri kaydedilemedi'); }
                              const firmaResult = await firmaResp.json();
                              activeFirmaId = firmaResult.firmaId;
                              activeFirma = user.firma || firma;
                              activeSehir = user.sehir || sehir;
                              activeTelefon = user.telefon || telefon;
                              activeEmail = user.email || email;
                            }

                            const resp = await fetch('/api/guest-offers', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                firma_id: activeFirmaId,
                                firma: activeFirma, sehir: activeSehir, telefon: activeTelefon, email: activeEmail,
                                payment_method: cartPaymentMethod || undefined,
                                payment_notes: cartPaymentNotes || undefined,
                                items: cart.map(item => ({ name: item.name, measure: item.measure, qty: item.qty, desc: item.desc || '', sutCode: item.sutCode || '', price: item.price || 0, marka: item.marka || '', kategori: item.kategori || '', barcode: item.barcode || '', code: item.code || '' }))
                              })
                            });
                            if (!resp.ok) { const errData = await resp.json().catch(() => ({})); throw new Error(errData.error || 'Sipariş kaydedilemedi'); }
                            trackOrderCreate({ item_count: cart.length, total_qty: cart.reduce((s, i) => s + i.qty, 0), source: 'desktop_cart' });
                            alert('Sipariş başarıyla kaydedildi!');
                            setCart([]); setCartPaymentMethod(''); setCartPaymentNotes('');
                            localStorage.removeItem('teklifCart');
                          } catch (err) {
                            alert('Sipariş kaydedilemedi: ' + (err.message || err));
                          }
                        }}
                      >
                        Siparişi Tamamla
                      </button>
                    </>
                  )}
                </>
              )}

            </>
          )}
        </div>
    </div>
    <footer className="w-full bg-white py-6 flex items-center justify-center text-center mt-5">
          <span className="text-xs text-[#bfc9e0]">© {new Date().getFullYear()} INVAMED Portal. All rights reserved.</span>
        </footer>
    {/* İlave Ürün Ekle Modalı (Admin Sipariş Yönetimi) */}
    {addItemModal && (() => {
      const addItemVariants = addItemSelected?._variants || [];
      const addItemSutPrices = addItemSelected?._sutPrices || {};
      const addItemSutCodes = addItemSelected?._sutCodes || [];
      const addItemSelectedSut = addItemSut;
      const addItemSelectedVariant = addItemSelected?._selectedVariant || null;
      const addItemSelectedParentGroup = addItemSelected?._selectedParentGroup || '';

      const aiFiltBySut = addItemSelectedSut
        ? addItemVariants.filter(v => { const codes = v.sut_codes ? v.sut_codes.split(',').map(s => s.trim()) : []; return codes.includes(addItemSelectedSut); })
        : addItemVariants;
      const aiParentGroups = [...new Set(aiFiltBySut.filter(v => v.parent_group).map(v => v.parent_group))].sort();
      const aiHasParentGroups = aiParentGroups.length > 1;
      const aiFiltByParent = aiHasParentGroups && addItemSelectedParentGroup
        ? aiFiltBySut.filter(v => v.parent_group === addItemSelectedParentGroup)
        : aiFiltBySut;
      const aiSearchLower = (addItemMeasure || '').toLowerCase();
      const aiDisplayVariants = aiSearchLower
        ? aiFiltByParent.filter(v => (v.size_label || '').toLowerCase().includes(aiSearchLower) || (v.olcu_clean || '').toLowerCase().includes(aiSearchLower) || (v.barcode || '').includes(aiSearchLower) || (v.variant_name || '').toLowerCase().includes(aiSearchLower))
        : aiFiltByParent;
      const aiSelectedSutInfo = addItemSelectedSut && addItemSutPrices[addItemSelectedSut] ? addItemSutPrices[addItemSelectedSut] : null;

      // Ürün seçildi ama varyant yoksa ve SUT yoksa → direkt eklenebilir
      const canAddDirect = addItemSelected && !addItemSelected._loading && addItemSutCodes.length === 0 && addItemVariants.length === 0;
      const canAddWithVariant = addItemSelected && addItemSelectedVariant;
      const canAdd = canAddDirect || canAddWithVariant;

      // Fiyat hesapla
      let addItemPrice = 0;
      if (canAddWithVariant) {
        const hasCustom = addItemSelectedVariant.custom_price != null;
        addItemPrice = hasCustom ? Number(addItemSelectedVariant.custom_price) : (aiSelectedSutInfo?.price || addItemSelectedVariant.price || addItemSelected.price || 0);
      } else if (canAddDirect) {
        addItemPrice = addItemSelected.price || 0;
      }

      return (
      <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50" onClick={() => { setAddItemModal(false); setAddItemSelected(null); }}>
        <div className="bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl w-full sm:max-w-lg sm:mx-4 flex flex-col max-h-[92vh]" onClick={e => e.stopPropagation()} style={{animation: 'slideUp 0.25s ease-out'}}>

          {/* Başlık */}
          <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
            <div>
              <h3 className="font-bold text-gray-800 text-lg">İlave Ürün Ekle</h3>
              <p className="text-xs text-gray-400 mt-0.5">Siparişe yeni ürün kalemi ekleyin</p>
            </div>
            <button className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-xl font-bold" onClick={() => { setAddItemModal(false); setAddItemSelected(null); }}>&times;</button>
          </div>

          {/* İçerik */}
          <div className="flex-1 overflow-y-auto p-5">
            {!addItemSelected ? (
              /* ADIM 1: Ürün Arama */
              <div>
                <input type="text" autoFocus placeholder="Ürün adı, marka veya barkod ile arayın..." value={addItemSearch} onChange={e => setAddItemSearch(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base bg-gray-50 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 focus:bg-white transition mb-3" />
                <div className="space-y-2 max-h-[55vh] overflow-y-auto">
                  {addItemSearch.length < 2 && <div className="text-center py-10 text-gray-400 text-sm">En az 2 karakter yazın...</div>}
                  {addItemSearch.length >= 2 && products.filter(p => {
                    const s = addItemSearch.toLowerCase();
                    return (p.name || '').toLowerCase().includes(s) || (p.marka || '').toLowerCase().includes(s) || (p.barcode || '').includes(s) || (p.sut_codes || '').toLowerCase().includes(s) || (p.code || '').toLowerCase().includes(s);
                  }).slice(0, 30).map(p => (
                    <button key={p.id} onClick={async () => {
                      setAddItemSelected({ ...p, _loading: true, _variants: [], _sutPrices: {}, _sutCodes: [], _selectedVariant: null, _selectedParentGroup: '' });
                      setAddItemSut(''); setAddItemMeasure(''); setAddItemQty(1);
                      try {
                        const res = await fetch(`/api/products/${p.id}/variants`);
                        const data = await res.json();
                        const variants = data.variants || [];
                        const sutPrices = data.sut_prices || {};
                        const sutCodes = Object.keys(sutPrices);
                        setAddItemSelected(prev => prev ? { ...prev, _loading: false, _variants: variants, _sutPrices: sutPrices, _sutCodes: sutCodes } : null);
                        if (sutCodes.length === 1) setAddItemSut(sutCodes[0]);
                      } catch (err) {
                        console.error(err);
                        setAddItemSelected(prev => prev ? { ...prev, _loading: false } : null);
                      }
                    }} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-blue-50 active:bg-blue-100 transition text-left border border-gray-200 hover:border-blue-300">
                      <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden shrink-0 border border-gray-100">
                        <img src={p.image_url || '/urunler/300x300.png'} alt="" className="w-full h-full object-contain" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-800 leading-tight line-clamp-2">{p.name}</div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          {p.marka && <span className="bg-indigo-50 text-indigo-600 text-xs px-1.5 py-0.5 rounded font-medium">{p.marka}</span>}
                          <span className="text-xs text-gray-400">{p.variant_count || 0} varyant</span>
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-gray-300 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" /></svg>
                    </button>
                  ))}
                  {addItemSearch.length >= 2 && products.filter(p => {
                    const s = addItemSearch.toLowerCase();
                    return (p.name || '').toLowerCase().includes(s) || (p.marka || '').toLowerCase().includes(s) || (p.barcode || '').includes(s) || (p.sut_codes || '').toLowerCase().includes(s);
                  }).length === 0 && (
                    <div className="text-center py-10 text-gray-400 text-sm">Ürün bulunamadı</div>
                  )}
                </div>
              </div>
            ) : (
              /* ADIM 2: Ürün Detay + Varyant Seçimi */
              <div className="space-y-4">
                {/* Seçili ürün kartı */}
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                    <img src={addItemSelected.image_url || '/urunler/300x300.png'} alt="" className="w-full h-full object-contain" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-blue-900 text-sm leading-tight">{addItemSelected.name}</div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                      {addItemSelected.marka && <span className="bg-indigo-100 text-indigo-700 text-xs px-1.5 py-0.5 rounded font-medium">{addItemSelected.marka}</span>}
                      {addItemSelectedVariant && <span className="bg-emerald-100 text-emerald-700 text-xs font-mono px-1.5 py-0.5 rounded font-bold">{addItemSelectedVariant.barcode}</span>}
                    </div>
                  </div>
                  <button onClick={() => { setAddItemSelected(null); setAddItemSut(''); setAddItemMeasure(''); }} className="text-xs text-blue-600 hover:underline font-semibold shrink-0 px-2 py-1">Değiştir</button>
                </div>

                {/* Loading */}
                {addItemSelected._loading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-700"></div>
                    <span className="ml-2 text-blue-700 text-sm">Varyantlar yükleniyor...</span>
                  </div>
                )}

                {!addItemSelected._loading && (
                  <>
                    {/* SUT Kodu Seçimi */}
                    {addItemSutCodes.length > 0 && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">SUT Kodu</label>
                        <div className="flex flex-wrap gap-2">
                          {addItemSutCodes.map(sut => {
                            const info = addItemSutPrices[sut];
                            return (
                              <button key={sut}
                                className={`px-3 py-2 rounded-xl text-sm font-mono border-2 transition-all ${addItemSelectedSut === sut ? 'bg-emerald-600 text-white border-emerald-600 shadow-md' : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-300'}`}
                                onClick={() => { setAddItemSut(sut); setAddItemSelected(prev => prev ? { ...prev, _selectedParentGroup: '', _selectedVariant: null } : null); }}>
                                <span className="font-bold">{sut}</span>
                                {info && info.price > 0 && <span className={`ml-2 ${addItemSelectedSut === sut ? 'text-emerald-100' : 'text-blue-600 font-bold'}`}>{formatPrice(info.price)}</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Alt Grup Seçimi */}
                    {(addItemSelectedSut || addItemSutCodes.length === 0) && aiHasParentGroups && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Alt Grup</label>
                        <div className="flex flex-wrap gap-2">
                          {aiParentGroups.map(pg => (
                            <button key={pg}
                              className={`px-3 py-2 rounded-xl text-sm border-2 transition-all ${addItemSelectedParentGroup === pg ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'}`}
                              onClick={() => setAddItemSelected(prev => prev ? { ...prev, _selectedParentGroup: pg, _selectedVariant: null } : null)}>
                              {pg} <span className={`ml-1 text-xs ${addItemSelectedParentGroup === pg ? 'text-blue-200' : 'text-gray-400'}`}>({aiFiltBySut.filter(v => v.parent_group === pg).length})</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Varyant Seçimi */}
                    {(addItemSelectedSut || addItemSutCodes.length === 0) && (!aiHasParentGroups || addItemSelectedParentGroup) && aiDisplayVariants.length > 0 && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Varyant Seçin <span className="text-xs text-gray-400 font-normal">({aiDisplayVariants.length})</span></label>
                        {aiFiltByParent.length > 6 && (
                          <input type="text" className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm mb-2 bg-gray-50 focus:ring-2 focus:ring-blue-300 focus:bg-white"
                            placeholder="Ölçü veya barkod ile filtrele..." value={addItemMeasure} onChange={e => setAddItemMeasure(e.target.value)} />
                        )}
                        <div className="border border-gray-200 rounded-xl overflow-hidden max-h-[28vh] overflow-y-auto">
                          {aiDisplayVariants.map(v => {
                            const isSelected = addItemSelectedVariant?.id === v.id;
                            return (
                              <div key={v.id}
                                className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-gray-50 transition-all ${isSelected ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : 'hover:bg-blue-50 border-l-4 border-l-transparent'}`}
                                onClick={() => setAddItemSelected(prev => prev ? { ...prev, _selectedVariant: v } : null)}>
                                <div className="flex-1 min-w-0">
                                  <div className={`text-sm font-medium ${isSelected ? 'text-emerald-800' : 'text-gray-800'}`}>{v.olcu_clean || v.size_label || v.variant_name}</div>
                                  <div className="text-xs text-gray-400 font-mono mt-0.5">{v.barcode}</div>
                                </div>
                                {isSelected && <svg className="w-5 h-5 text-emerald-600 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" /></svg>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Adet alanı - her zaman göster ürün seçildikten sonra */}
                    {(canAdd || addItemSelectedSut || (addItemSutCodes.length === 0 && addItemVariants.length > 0)) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">Adet</label>
                        <input type="number" min={1} value={addItemQty} onChange={e => setAddItemQty(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-full px-4 py-3 text-base font-bold text-blue-900 text-center border border-gray-200 rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-300 focus:bg-white transition" />
                        <p className="text-xs text-amber-600 mt-2 flex items-center gap-1.5">
                          <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" /></svg>
                          Birim fiyat yönetim panelinden belirlenecektir.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Alt butonlar */}
          <div className="p-5 border-t border-gray-100 shrink-0 space-y-2">
            {canAdd && (
              <button disabled={addItemSaving} onClick={async () => {
                setAddItemSaving(true);
                try {
                  const v = addItemSelectedVariant;
                  let price = 0, barcode = addItemSelected.barcode || '', measure = '', sutCode = addItemSut;
                  if (v) {
                    const hasCustom = v.custom_price != null;
                    price = hasCustom ? Number(v.custom_price) : (aiSelectedSutInfo?.price || 0);
                    barcode = v.barcode || barcode;
                    measure = v.olcu_clean || v.size_label || v.variant_name || '';
                  } else {
                    price = addItemSelected.price || 0;
                  }
                  const resp = await authFetch('/api/guest-offers/' + addItemOrderId + '/item-add', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: addItemSelected.name, measure, qty: addItemQty, price, sutCode, marka: addItemSelected.marka || '', kategori: addItemSelected.kategori || '', barcode, code: addItemSelected.code || '' })
                  });
                  if (resp.ok) {
                    const data = await resp.json();
                    updateAdminOrderLocal(addItemOrderId, { items: JSON.stringify(data.items) });
                    setAddItemModal(false); setAddItemSelected(null);
                  }
                } catch (e) { console.error(e); }
                setAddItemSaving(false);
              }} className="w-full py-3 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-700 active:bg-emerald-800 transition shadow-lg shadow-emerald-200 disabled:opacity-50">
                {addItemSaving ? 'Ekleniyor...' : `Siparişe Ekle (${addItemQty} adet)`}
              </button>
            )}
            <button onClick={() => { setAddItemModal(false); setAddItemSelected(null); }} className="w-full py-2.5 text-sm font-semibold text-gray-500 bg-gray-100 rounded-xl hover:bg-gray-200 transition">
              İptal
            </button>
          </div>
        </div>
      </div>
      );
    })()}

    {/* Firma Seçim Modalı */}
    <FirmaSecimModal />
    </div>
  );
} 